import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import UserSidebar from './UserSidebar';
import TopBar from '../TopBar';
import UserSectionSubscription from './UserSectionSubscription';
import UserSectionProfile from './UserSectionProfile';
import UserSectionSupport from './UserSectionSupport';
import UserSectionPathBadges from './UserSectionPathBadges';
import ProfileSettings from '../tabs/ProfileSettings';
import AccountSettings from '../tabs/AccountSettings';
import { cleanHtmlForPreview } from '../../utils/contentCleaner';
import { formatArticleContent } from '../../utils/formatArticleContent';
import {
  QUIZ_FETCH_DEFER_MS,
  QUIZ_OPEN_GUARD_MS,
  createQuizSubmitGuard,
  resolveReaderDismiss,
  retentionQuizMessageKey
} from '../../utils/articleQuizUx';
import { ArticleQuizReader } from './ArticleVocabularyQuizButton';
import {
  beginExamLoad,
  catalogFromResponse,
  examCatalogView,
  fetchExamCatalog,
  shouldApplyExamLoad,
  yearNameForExamTab
} from '../../utils/examVocabularyLoad';
import { getSafeAvatarUrl } from '../../utils/avatarPlaceholder';
import { getImageUrl } from '../../utils/imageUrl';
import ArticleDetailsModal from './ArticleDetailsModal';
import DictationOverlay from './DictationOverlay';
import DictationDetailsOverlay from './DictationDetailsOverlay';
import { faculties, specialtiesData, academicLevels as centralizedAcademicLevels } from '../../data/espData';
import AzureSpeechService from '../../services/azureSpeechService';
import { azureConfig } from '../../config/azureConfig';
import ComingSoonOverlay from '../common/ComingSoonOverlay';
import TawkTo from '../common/TawkTo';

/* global SpeechSDK */


import { getApiBaseUrl, getAuthToken } from '../../services/api';
import { markHandled, reportLoaderFailure } from '../../monitoring/sentry';
import ErrorBoundary from '../common/ErrorBoundary';
import './UserDashboard.css';

// Use getApiBaseUrl() so on HTTPS we use same-origin (avoids mixed content); subscription and all API calls work.
const API_BASE_URL = getApiBaseUrl();

/** Slug for i18n keys under user.examVocabulary.variants.* (must match locale JSON keys). */
function examVariantI18nKey(variant) {
  const s = String(variant ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return s || 'unknown';
}

const USER_DASHBOARD_SECTION_KEY = 'syllera-user-dashboard-section';

/** Renders the active section inside the section error boundary, so a crash leaves the sidebar usable. */
function SectionContent({ render }) {
  return render();
}

const readStoredUserSection = (fallback) => {
  try {
    return sessionStorage.getItem(USER_DASHBOARD_SECTION_KEY) || fallback;
  } catch {
    return fallback;
  }
};

const UserDashboard = ({ initialSection = null, hideSidebar = false, unlockExploreCourses = false }) => {
  const { t } = useTranslation('user');
  const [activeSection, setActiveSection] = useState(
    () => initialSection || (hideSidebar ? 'dashboard' : readStoredUserSection('dashboard'))
  );
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar toggle state

  // Debug: Log sidebar state changes (remove in production)
  useEffect(() => {
    if (window.innerWidth <= 768) {
      console.log('[Mobile Sidebar] State changed:', sidebarOpen);
    }
  }, [sidebarOpen]);
  
  // Update activeSection if initialSection prop changes (for parent dashboard integration)
  useEffect(() => {
    if (initialSection) {
      setActiveSection(initialSection);
    }
  }, [initialSection]);

  useEffect(() => {
    if (hideSidebar || !activeSection) return;
    try {
      sessionStorage.setItem(USER_DASHBOARD_SECTION_KEY, activeSection);
    } catch {
      // Ignore storage errors
    }
  }, [activeSection, hideSidebar]);

  // Close sidebar when section changes on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false); // Close sidebar on desktop
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && window.innerWidth <= 768) {
        const sidebar = document.querySelector('.user-sidebar');
        const hamburger = document.querySelector('.mobile-menu-toggle');
        if (sidebar && !sidebar.contains(event.target) && hamburger && !hamburger.contains(event.target)) {
          setSidebarOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);
  const [sectionKey, setSectionKey] = useState(0);
  const [activeShopTab, setActiveShopTab] = useState('courses');
  const [activeLiveTab, setActiveLiveTab] = useState('book-lesson');
  const [activeBookingTab, setActiveBookingTab] = useState('upcoming');
  const [activeCourseLibraryTab, setActiveCourseLibraryTab] = useState('courses');
  const [activeExploreTab, setActiveExploreTab] = useState('educational-levels-vocabulary');
  const [selectedExploreLanguage, setSelectedExploreLanguage] = useState(null); // null = show only language cards; code = show modules (same page)
  const LANGUAGE_BY_CODE = useMemo(() => ({
    en: 'English',
    fr: 'French',
    es: 'Spanish',
    de: 'German',
    it: 'Italian',
  }), []);
  const [selectedLearningLanguage, setSelectedLearningLanguage] = useState(() => {
    try {
      return localStorage.getItem('selected-learning-language') || 'English';
    } catch {
      return 'English';
    }
  });

  useEffect(() => {
    if (!selectedExploreLanguage) return;
    const label = LANGUAGE_BY_CODE[selectedExploreLanguage] || 'English';
    setSelectedLearningLanguage(label);
    try {
      localStorage.setItem('selected-learning-language', label);
    } catch {
      // ignore
    }
  }, [selectedExploreLanguage, LANGUAGE_BY_CODE]);

  // Shop functionality state
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentFilters, setCurrentFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [loading, setLoading] = useState(false);
  // My Courses (paid content from subscription)
  const [myCourses, setMyCourses] = useState([]);
  const [myCoursesLoading, setMyCoursesLoading] = useState(false);
  const [learningPathsProgress, setLearningPathsProgress] = useState([]);
  const [learningPathsProgressLoading, setLearningPathsProgressLoading] = useState(false);
  const [pathBadgesSummary, setPathBadgesSummary] = useState(null);
  const [pathBadgesLoading, setPathBadgesLoading] = useState(true);
  const [pathBadgesError, setPathBadgesError] = useState(null);
  const [pathBadgesRetryTick, setPathBadgesRetryTick] = useState(0);
  // Premium badge (header): active subscription = premium, else free
  const [isPremium, setIsPremium] = useState(false);
  const [subscriptionEndsAt, setSubscriptionEndsAt] = useState(null);
  // Dashboard learning progress (courses, streak, vocabulary, badges) from GET /api/learning-progress
  const [dashboardProgress, setDashboardProgress] = useState(null);
  const [dashboardProgressLoading, setDashboardProgressLoading] = useState(false);
  // Article retention (spaced repetition) for dashboard
  const [pathRetentionFilter, setPathRetentionFilter] = useState(null);
  const [pathRetentionList, setPathRetentionList] = useState([]);
  const [pathRetentionLoading, setPathRetentionLoading] = useState(false);
  const [retentionRefreshTick, setRetentionRefreshTick] = useState(0);
  // General Vocabulary (User) state
  const [gvArticles, setGvArticles] = useState([]);
  const [gvLoading, setGvLoading] = useState(false);
  const [gvError, setGvError] = useState('');
  const [gvSearch, setGvSearch] = useState('');
  const [gvFilterLevel, setGvFilterLevel] = useState('all');
  const [gvFilterGenre, setGvFilterGenre] = useState('all');
  const [gvFilterReadTime, setGvFilterReadTime] = useState('all');
  const [gvFilterDifficulty, setGvFilterDifficulty] = useState('all');
  const [gvSortBy, setGvSortBy] = useState('recommended');
  const [gvSaved, setGvSaved] = useState([]);
  const [gvProgress, setGvProgress] = useState({ summary: {}, progress: [] });

  // Dictation Module State
  const [dictationPathsOpen, setDictationPathsOpen] = useState(false);
  const [activeDictationPath, setActiveDictationPath] = useState(null);
  const [dictationContent, setDictationContent] = useState(null);
  const [dictationArticles, setDictationArticles] = useState([]);
  const [dictationLoading, setDictationLoading] = useState(false);
  const [dictationError, setDictationError] = useState('');
  const [activeDictationSchoolLevel, setActiveDictationSchoolLevel] = useState('primary-school');
  const [activeDictationYear, setActiveDictationYear] = useState('primary-school-1');
  const [dictationOverlayOpen, setDictationOverlayOpen] = useState(false);
  const [currentDictationArticle, setCurrentDictationArticle] = useState(null);
  const [dictationDetailsOverlayOpen, setDictationDetailsOverlayOpen] = useState(false);
  const [currentDictationDetailsArticle, setCurrentDictationDetailsArticle] = useState(null);

  // Reading Module State (paths like Dictation)
  const [readingPathsOpen, setReadingPathsOpen] = useState(false);
  const [activeReadingPath, setActiveReadingPath] = useState(null);
  const [readingArticles, setReadingArticles] = useState([]);
  const [readingLoading, setReadingLoading] = useState(false);
  const [readingError, setReadingError] = useState('');
  const [readingExamLockNotice, setReadingExamLockNotice] = useState('');
  const [exploreExamSequentialNotice, setExploreExamSequentialNotice] = useState('');
  const [examMasteryLockModalOpen, setExamMasteryLockModalOpen] = useState(false);
  const [readingSearch, setReadingSearch] = useState('');
  const [readingLevel, setReadingLevel] = useState('all');
  const [readingDifficulty, setReadingDifficulty] = useState('all');
  const [readingMinTime, setReadingMinTime] = useState('');
  const [readingMaxTime, setReadingMaxTime] = useState('');
  // Path-specific filters
  const [readingSchool, setReadingSchool] = useState('');
  const [readingYear, setReadingYear] = useState('');
  const [readingExamType, setReadingExamType] = useState('');
  const [readingExamSection, setReadingExamSection] = useState('');
  const [readingExamDifficulty, setReadingExamDifficulty] = useState('');
  const [readingFaculty, setReadingFaculty] = useState('');
  const [readingSpecialty, setReadingSpecialty] = useState('');
  const [readingAcademicYear, setReadingAcademicYear] = useState('');
  const [readingGenre, setReadingGenre] = useState('');
  const [readingReaderOpen, setReadingReaderOpen] = useState(false);
  const [readingCurrentArticle, setReadingCurrentArticle] = useState(null);
  const readingContentRef = useRef(null);

  // Reading path-local selections (to mirror vocabulary UIs)
  const [readingActiveSchoolLevel, setReadingActiveSchoolLevel] = useState('high-school');
  const [readingActiveYearLevel, setReadingActiveYearLevel] = useState('high-school-1');
  const [readingActiveExam, setReadingActiveExam] = useState('ielts');
  const [readingActiveExamVariant, setReadingActiveExamVariant] = useState('Academic');

  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Placement test navigation state
  const [activePlacementPath, setActivePlacementPath] = useState(null); // 'educational-levels' | 'general' | 'international-exam' | 'esp'
  const [activePlacementMode, setActivePlacementMode] = useState(null); // 'check-year' | 'find-level'

  // Live Learning functionality state
  const [teacherSearch, setTeacherSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [bookingModal, setBookingModal] = useState({ show: false, teacher: null });
  const [ratingModal, setRatingModal] = useState({ show: false, teacher: null });
  
  // Live Learning data state
  const [teachers, setTeachers] = useState([]);
  const [bookings, setBookings] = useState({
    upcoming: [],
    completed: [],
    cancelled: []
  });
  const [myTeachers, setMyTeachers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [currentTeacherId, setCurrentTeacherId] = useState(null);
  const [currentTeacherRate, setCurrentTeacherRate] = useState(0);
  const [filteredTeachers, setFilteredTeachers] = useState([]);

  // Course Library functionality state
  const [courseSearch, setCourseSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [priceFilterCourse, setPriceFilterCourse] = useState('');
  const [ratingFilterCourse, setRatingFilterCourse] = useState('');
  const [sortFilter, setSortFilter] = useState('popular');
  const [viewMode, setViewMode] = useState('grid');
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [myLearning, setMyLearning] = useState([]);
  const [courseWishlist, setCourseWishlist] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [courseDetailModal, setCourseDetailModal] = useState({ show: false, course: null, videos: [], isEnrolled: false });

  // Support Classes functionality state
  const [supportClassModal, setSupportClassModal] = useState({ show: false, type: null });
  const [selectedSchoolLevel, setSelectedSchoolLevel] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [showRecordedCoursesView, setShowRecordedCoursesView] = useState(false);
  const [recordedCourses, setRecordedCourses] = useState([]);
  const [loadingRecordedCourses, setLoadingRecordedCourses] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [showWorkshopView, setShowWorkshopView] = useState(false);
  const [workshopChannelPosts, setWorkshopChannelPosts] = useState([]);
  const [workshopSessions, setWorkshopSessions] = useState([]);
  const [activeWorkshopTab, setActiveWorkshopTab] = useState('overview');
  const [isEnrolledInWorkshop, setIsEnrolledInWorkshop] = useState(false);
  const [loadingWorkshop, setLoadingWorkshop] = useState(false);
  const [showNormalClassesView, setShowNormalClassesView] = useState(false);
  const [normalClasses, setNormalClasses] = useState([]);
  const [loadingNormalClasses, setLoadingNormalClasses] = useState(false);
  const [selectedNormalClass, setSelectedNormalClass] = useState(null);
  const [courseTypeImages, setCourseTypeImages] = useState({
    recorded: '/assets/images/recorded_courses.jpg',
    workshops: '/assets/images/workshops.jpg',
    normal: '/assets/images/normal_classes.jpg'
  });

  // Talk functionality state
  const [activeTalkTab, setActiveTalkTab] = useState('communication-hub');
  const [roomSearch, setRoomSearch] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [levelFilterTalk, setLevelFilterTalk] = useState('');
  const [categoryFilterTalk, setCategoryFilterTalk] = useState('');
  const [rooms, setRooms] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [myRooms, setMyRooms] = useState([]);
  const [recentRooms, setRecentRooms] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [reportedUsers, setReportedUsers] = useState([]);
  const [roomModal, setRoomModal] = useState({ show: false, room: null });
  const [reportModal, setReportModal] = useState({ show: false, target: null });
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeActivityTab, setActiveActivityTab] = useState('my-rooms');
  const [createRoomForm, setCreateRoomForm] = useState({
    title: '',
    language: '',
    type: '',
    category: '',
    level: 'any',
    maxUsers: 10,
    tags: '',
    description: '',
    isPrivate: false
  });
  const [profileSettings, setProfileSettings] = useState({
    username: 'John Doe',
    country: '🇩🇿 Algeria',
    nativeLanguage: 'Arabic',
    learningLanguage: 'English',
    languageLevel: 'intermediate'
  });
  const [reportForm, setReportForm] = useState({
    reason: '',
    description: ''
  });

  const readStoredUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  };

  const [storedUserSnapshot, setStoredUserSnapshot] = useState(() =>
    typeof window !== 'undefined' ? readStoredUser() : {}
  );

  useEffect(() => {
    setStoredUserSnapshot(readStoredUser());
  }, [activeSection]);

  const resolvedNumericUserId = (() => {
    const raw = storedUserSnapshot?.id;
    if (raw == null || raw === '') return null;
    const s = String(raw).trim();
    return /^\d+$/.test(s) ? s : null;
  })();

  const displayNameFromStorage = [storedUserSnapshot.first_name, storedUserSnapshot.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Current user data (real DB id from login when available — avoids /retention/user123)
  const currentUser = {
    id: resolvedNumericUserId,
    name: displayNameFromStorage || storedUserSnapshot.name || 'John Doe',
    country: '🇩🇿 Algeria',
    level: 'intermediate',
    avatar: (() => {
      const a = storedUserSnapshot.first_name ? String(storedUserSnapshot.first_name).charAt(0).toUpperCase() : '';
      const b = storedUserSnapshot.last_name ? String(storedUserSnapshot.last_name).charAt(0).toUpperCase() : '';
      return (a + b) || 'JD';
    })(),
  };

  // Educational Levels Vocabulary State
  const [showEducationalLevels, setShowEducationalLevels] = useState(false);
  const [activeSchoolLevel, setActiveSchoolLevel] = useState('high-school');
  const [activeYearLevel, setActiveYearLevel] = useState('high-1');
  const [educationalContent, setEducationalContent] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const selectedArticleContent = selectedArticle?.content;
  const selectedArticleHtml = useMemo(() => formatArticleContent(selectedArticleContent), [selectedArticleContent]);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [floatingBox, setFloatingBox] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsArticle, setDetailsArticle] = useState(null);
  const [detailsArticleIsLocked, setDetailsArticleIsLocked] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // Scope from My Courses: when set, only these year_level_ids are allowed (filter tabs + backend enforces)
  const [allowedEducationalYearLevelIds, setAllowedEducationalYearLevelIds] = useState(null);
  const [yearLevelsFromApi, setYearLevelsFromApi] = useState([]);
  const [educationalContentScoped, setEducationalContentScoped] = useState([]);
  const [selectedScopedYearLevelId, setSelectedScopedYearLevelId] = useState(null);
  const [educationalScopedLoading, setEducationalScopedLoading] = useState(false);
  const [educationalContentFromSubscription, setEducationalContentFromSubscription] = useState(false);
  // Units (curriculum): when non-empty, show Unit Cards first; selecting one shows articles for that unit
  const [units, setUnits] = useState([]);
  const [unitCardBanners, setUnitCardBanners] = useState([]); // [{ unit_id, image_url }, ...] for unit card placeholders
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [unitsLoading, setUnitsLoading] = useState(false);

  // Reading System State - ONLY for Reading Section
  const [isReadingActive, setIsReadingActive] = useState(false);
  const [isModelReading, setIsModelReading] = useState(false);
  const [showRemark, setShowRemark] = useState(false);

  // Retention quiz. IELTS/exam articles open it from the reader button; educational articles still open it after close.
  const [showRetentionQuizModal, setShowRetentionQuizModal] = useState(false);
  const [retentionQuizArticle, setRetentionQuizArticle] = useState(null);
  const [retentionQuizData, setRetentionQuizData] = useState(null);
  const [retentionQuizLoading, setRetentionQuizLoading] = useState(false);
  const [retentionQuizSelected, setRetentionQuizSelected] = useState([]);
  const [retentionQuizSubmitting, setRetentionQuizSubmitting] = useState(false);
  const [retentionQuizResult, setRetentionQuizResult] = useState(null);
  const [articleQuizStarting, setArticleQuizStarting] = useState(false);
  
  // Azure Speech Service instance
  const azureSpeechService = useRef(new AzureSpeechService());
  // Reading completion: ref to article modal body (scroll), session start time, and active (visible) time for end-session
  const articleModalBodyRef = useRef(null);
  const articleGestureRef = useRef({ x: 0, y: 0, moved: false, downOnOverlay: false });
  const articleClosingRef = useRef(false);
  const quizOverlayReadyAtRef = useRef(0);
  const quizSubmitGuardRef = useRef(null);
  const readingSessionStartedAtRef = useRef(null);
  const readingActiveSecondsRef = useRef(0);
  const readingVisibilityStartRef = useRef(null);
  const readingVisibilityHandlerRef = useRef(null);
  // Ignore stale article loads when user switches school/year before request completes
  const educationalLoadKeyRef = useRef('');
  const examLoadKeyRef = useRef(0);
  const educationalReloadParamsRef = useRef({ schoolLevel: null, yearName: null, unitId: null, unitDisplayTitle: null });
  const scopedSchoolSlugRef = useRef(null);
  const scopedYearNameRef = useRef(null);
  // Throttle word-interaction API: queue and send at most one request every 400ms to avoid 429
  const wordInteractionQueueRef = useRef([]);
  const wordInteractionDrainRef = useRef(null);

  // Check SpeechSDK availability on component mount
  useEffect(() => {
    if (typeof SpeechSDK !== 'undefined') {
      console.log('✅ Microsoft Speech SDK loaded successfully');
    } else {
      console.log('⚠️ Microsoft Speech SDK not yet loaded');
    }
  }, []);

  // Clear word-interaction drain timeout on unmount
  useEffect(() => {
    return () => {
      const tid = wordInteractionDrainRef.current;
      if (typeof tid === 'number') clearTimeout(tid);
      wordInteractionDrainRef.current = null;
    };
  }, []);




  // International Exam Levels State
  const [showExamLevels, setShowExamLevels] = useState(false);
  const [activeExamLevel, setActiveExamLevel] = useState('ielts');
  const [activeExamYearLevel, setActiveExamYearLevel] = useState('ielts-1');
  const [examArticles, setExamArticles] = useState([]);
  const [examContentEntitled, setExamContentEntitled] = useState(false);
  const [examCatalogStatus, setExamCatalogStatus] = useState(() => {
    const section = initialSection || (hideSidebar ? 'dashboard' : readStoredUserSection('dashboard'));
    return section === 'exam-levels' ? 'loading' : 'idle';
  });

  // Educational Levels Data (by language)
  const schoolLevels = (() => {
    const lang = selectedLearningLanguage ? String(selectedLearningLanguage).trim().toLowerCase() : '';

    // Algerian context for foreign languages: Spanish / German / Italian
    // → only High School, 2nd & 3rd year
    if (lang === 'spanish' || lang === 'german' || lang === 'italian') {
      return [
        { id: 'high-school', name: 'High School', years: ['Second Year', 'Third Year'] }
      ];
    }

    // Default: full structure for languages that have primary/middle content
    return [
      { id: 'high-school', name: 'High School', years: ['First Year', 'Second Year', 'Third Year'] },
      { id: 'middle-school', name: 'Middle School', years: ['First Year', 'Second Year', 'Third Year', 'Fourth Year'] },
      { id: 'primary-school', name: 'Primary School', years: ['First Year', 'Second Year', 'Third Year'] }
    ];
  })();

  // Exam Levels by language: English (IELTS/TOEFL/Cambridge) vs French (DELF/TCF/TEF/DCL) vs Spanish (DELE/SIELE/CELU)
  const examLevelsEnglish = [
    { id: 'ielts', name: 'IELTS', years: ['Academic', 'General Training', 'UKVI'] },
    { id: 'toefl', name: 'TOEFL', years: ['iBT', 'PBT', 'CBT'] },
    { id: 'cambridge', name: 'Cambridge', years: ['FCE', 'CAE', 'CPE', 'PET', 'KET'] },
    { id: 'other-exams', name: 'Other Exams', years: ['PTE Academic', 'Duolingo English Test', 'OET'] }
  ];
  const examLevelsFrench = [
    { id: 'delf-dalf', name: 'DELF / DALF', years: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] },
    { id: 'tcf', name: 'TCF', years: ['Tout Public', 'DAP', 'Canada'] },
    { id: 'tef', name: 'TEF', years: ['TEF Canada', 'TEF Naturalisation'] },
    { id: 'dcl', name: 'DCL', years: ['A1', 'A2', 'B1', 'B2', 'C1'] },
    { id: 'other-exams', name: 'Other Exams', years: ['Certificat Voltaire', 'Alliance Française Certificates'] }
  ];
  const examLevelsSpanish = [
    { id: 'dele', name: 'DELE', years: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] },
    { id: 'siele', name: 'SIELE', years: ['Digital', 'Score-based'] },
    { id: 'celu', name: 'CELU', years: ['Latin America'] },
    { id: 'other-exams', name: 'Other Exams', years: ['Citizenship', 'Regional certifications'] }
  ];
  const examLevelsGerman = [
    { id: 'goethe', name: 'Goethe-Zertifikat', years: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] },
    { id: 'testdaf', name: 'TestDaF', years: ['University focus'] },
    { id: 'telc-german', name: 'telc German', years: ['Professional', 'Immigration'] },
    { id: 'other-exams', name: 'Other Exams', years: ['DSH', 'University-specific'] }
  ];
  const examLevelsItalian = [
    { id: 'cils', name: 'CILS', years: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] },
    { id: 'celi', name: 'CELI', years: ['Academic', 'Professional'] },
    { id: 'plida', name: 'PLIDA', years: ['Immigration'] },
    { id: 'other-exams', name: 'Other Exams', years: ['University certifications', 'Regional certifications'] }
  ];
  const langNorm = selectedLearningLanguage ? String(selectedLearningLanguage).trim().toLowerCase() : '';
  const isFrenchExamLanguage = langNorm === 'french';
  const isSpanishExamLanguage = langNorm === 'spanish';
  const isGermanExamLanguage = langNorm === 'german';
  const isItalianExamLanguage = langNorm === 'italian';
  const examLevels = isFrenchExamLanguage ? examLevelsFrench : (isSpanishExamLanguage ? examLevelsSpanish : (isGermanExamLanguage ? examLevelsGerman : (isItalianExamLanguage ? examLevelsItalian : examLevelsEnglish)));

  const trExamLevelName = (levelOrId) => {
    const level =
      typeof levelOrId === 'string'
        ? examLevels.find((l) => l.id === levelOrId)
        : levelOrId;
    if (!level) return typeof levelOrId === 'string' ? levelOrId : '';
    const key = String(level.id).replace(/-/g, '_');
    return t(`user.examVocabulary.levelIds.${key}.name`, { defaultValue: level.name });
  };
  const trExamYear = (year) =>
    year == null || year === ''
      ? ''
      : t(`user.examVocabulary.variants.${examVariantI18nKey(year)}`, { defaultValue: year });

  const trSchoolName = (levelOrName) => {
    const level =
      levelOrName && typeof levelOrName === 'object'
        ? levelOrName
        : schoolLevels.find((l) => l.id === levelOrName || l.name === levelOrName);
    const raw = level?.name || (typeof levelOrName === 'string' ? levelOrName : '');
    const key = (level?.id || String(raw).trim().toLowerCase().replace(/\s+/g, '-')).replace(/-/g, '_');
    return t(`user.educationalVocabulary.schools.${key}`, { defaultValue: raw });
  };
  const trYearName = (year) => {
    if (year == null || year === '') return '';
    const raw = typeof year === 'object' ? (year.name || year.key || '') : String(year);
    const key = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
    return t(`user.educationalVocabulary.years.${key}`, { defaultValue: raw });
  };

  // ESP Vocabulary State
  const [showESPVocabulary, setShowESPVocabulary] = useState(false);
  const [activeFaculty, setActiveFaculty] = useState('science');
  const [activeSpecialty, setActiveSpecialty] = useState('');
  const [activeAcademicLevel, setActiveAcademicLevel] = useState('');
  const [espContent, setEspContent] = useState([]);

  // ESP Academic Levels Data - Convert centralized data to expected format
  const academicLevels = centralizedAcademicLevels.map(level => ({
    id: level.id,
    name: level.label,
    icon: level.icon,
    description: `${level.label} level`
  }));

  // ESP Faculty Data - Convert centralized data to expected format
  const facultyData = {};
  faculties.forEach(faculty => {
    facultyData[faculty.id] = {
      name: faculty.label,
      icon: faculty.icon,
      specialties: specialtiesData[faculty.id] || []
    };
  });

  // Languages for Explore Courses (cards). flagEmoji for header; flagCountryCode for flag image (flagcdn.com).
  const EXPLORE_LANGUAGES = [
    { code: 'en', flag: '🇬🇧', flagCountryCode: 'gb', label: 'English' },
    { code: 'fr', flag: '🇫🇷', flagCountryCode: 'fr', label: 'French' },
    { code: 'es', flag: '🇪🇸', flagCountryCode: 'es', label: 'Spanish' },
    { code: 'de', flag: '🇩🇪', flagCountryCode: 'de', label: 'German' },
    { code: 'it', flag: '🇮🇹', flagCountryCode: 'it', label: 'Italian' }
  ];
  const FLAG_IMG_BASE = 'https://flagcdn.com/w80';

  // The six built-in Syllera learning paths (not Course Library courses)
  const LEARNING_PATH_IDS = [
    'educational-levels-vocabulary',
    'international-exam-vocabulary',
    'general-vocabulary',
    'academic-vocabulary-esp',
    'dictation',
    'reading'
  ];

  // Explore Courses data - using translations (memoized to avoid recreation on each render)
  const exploreCourses = useMemo(() => [
    {
      id: 'educational-levels-vocabulary',
      title: t('user.exploreCourses.educationalLevels.title'),
      icon: '📚',
      description: t('user.exploreCourses.educationalLevels.description'),
      badge: { type: 'progress', value: 35 },
      features: [
        {
          title: t('user.exploreCourses.educationalLevels.feature1.title'),
          description: t('user.exploreCourses.educationalLevels.feature1.description')
        },
        {
          title: t('user.exploreCourses.educationalLevels.feature2.title'),
          description: t('user.exploreCourses.educationalLevels.feature2.description')
        },
        {
          title: t('user.exploreCourses.educationalLevels.feature3.title'),
          description: t('user.exploreCourses.educationalLevels.feature3.description')
        }
      ]
    },
    {
      id: 'international-exam-vocabulary',
      title: t('user.exploreCourses.internationalExam.title'),
      icon: '🌍',
      description: t('user.exploreCourses.internationalExam.description'),
      badge: { type: 'new' },
      features: t('user.exploreCourses.internationalExam.features', { returnObjects: true })
    },
    {
      id: 'general-vocabulary',
      title: t('user.exploreCourses.generalVocabulary.title'),
      icon: '📖',
      description: t('user.exploreCourses.generalVocabulary.description'),
      features: [
        {
          title: t('user.exploreCourses.generalVocabulary.feature1.title'),
          description: t('user.exploreCourses.generalVocabulary.feature1.description')
        },
        {
          title: t('user.exploreCourses.generalVocabulary.feature2.title'),
          description: t('user.exploreCourses.generalVocabulary.feature2.description')
        },
        {
          title: t('user.exploreCourses.generalVocabulary.feature3.title'),
          description: t('user.exploreCourses.generalVocabulary.feature3.description')
        },
        {
          title: t('user.exploreCourses.generalVocabulary.feature4.title'),
          description: t('user.exploreCourses.generalVocabulary.feature4.description')
        },
        {
          title: t('user.exploreCourses.generalVocabulary.feature5.title'),
          description: t('user.exploreCourses.generalVocabulary.feature5.description')
        }
      ],
      badge: { type: 'progress', value: 62 }
    },
    {
      id: 'academic-vocabulary-esp',
      title: t('user.exploreCourses.academicVocabulary.title'),
      icon: '🎓',
      description: t('user.exploreCourses.academicVocabulary.description'),
      badge: { type: 'progress', value: 10 },
      features: t('user.exploreCourses.academicVocabulary.features', { returnObjects: true })
    },
    {
      id: 'dictation',
      title: t('user.exploreCourses.dictation.title'),
      icon: '🎧',
      description: t('user.exploreCourses.dictation.description'),
      badge: { type: 'new' },
      features: t('user.exploreCourses.dictation.features', { returnObjects: true })
    },
    {
      id: 'reading',
      title: t('user.exploreCourses.reading.title'),
      icon: '📖',
      description: t('user.exploreCourses.reading.description'),
      badge: { type: 'progress', value: 0 },
      features: t('user.exploreCourses.reading.features', { returnObjects: true })
    },
    {
      id: 'vocabulary-placement-test',
      title: t('user.exploreCourses.vocabularyPlacementTest.title'),
      icon: '📊',
      description: t('user.exploreCourses.vocabularyPlacementTest.description'),
      badge: { type: 'new' },
      features: t('user.exploreCourses.vocabularyPlacementTest.features', { returnObjects: true })
    },
    {
      id: 'virtual-classroom',
      title: t('user.exploreCourses.virtualClassroom.title'),
      icon: '🏫',
      description: t('user.exploreCourses.virtualClassroom.description'),
      badge: { type: 'progress', value: 80 },
      features: t('user.exploreCourses.virtualClassroom.features', { returnObjects: true })
    }
  ], [t]);

  const visibleExploreCourses = useMemo(() => {
    if (!unlockExploreCourses) return exploreCourses;
    return exploreCourses.filter((course) => LEARNING_PATH_IDS.includes(course.id));
  }, [exploreCourses, unlockExploreCourses]);

  // API Base URL
  const API_BASE = '/api';
  const LIVE_LEARNING_API = `${API_BASE_URL}/api/live-learning`;
  const GENERAL_VOCAB_API = `${API_BASE_URL}/api/general-vocabulary`;
  const VOCAB_ANALYTICS_API = `${API_BASE_URL}/api/vocabulary-analytics`;
  const DEMO_USER_ID = 1;

  // Fetch General Vocabulary articles when entering the section or filters change
  useEffect(() => {
    async function fetchGv() {
      if (activeSection !== 'general-vocabulary') return;
      try {
        setGvLoading(true);
        setGvError('');
        const params = new URLSearchParams();
        if (gvFilterLevel !== 'all') params.set('level', gvFilterLevel);
        if (gvFilterGenre !== 'all') params.set('genre', gvFilterGenre);
        if (selectedLearningLanguage) params.set('language', selectedLearningLanguage);
        const token = getAuthToken();
        const res = await fetch(`${GENERAL_VOCAB_API}/articles?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error('Failed to load library');
        const data = await res.json();
        setGvArticles(Array.isArray(data) ? data : []);
      } catch (e) {
        setGvError('Unable to load library. Please try again later.');
        setGvArticles([]);
      } finally {
        setGvLoading(false);
      }
    }
    fetchGv();
  }, [activeSection, gvFilterLevel, gvFilterGenre, selectedLearningLanguage]);

  // Load shop data when shop section is active
  useEffect(() => {
    if (activeSection === 'shop') {
      initializeShop();
    }
  }, [activeSection]);

  // Load Live Learning data when live-learning section is active
  useEffect(() => {
    if (activeSection === 'live-learning') {
      initializeLiveLearning();
    }
  }, [activeSection]);

  // Refresh educational content when explore-courses or educational-levels (Explore path) is active
  useEffect(() => {
    const inExploreOrEducationalLevels = activeSection === 'explore-courses' || activeSection === 'educational-levels';
    if (inExploreOrEducationalLevels && activeSchoolLevel && activeYearLevel) {
      const selectedSchool = schoolLevels.find(level => level.id === activeSchoolLevel);
      if (selectedSchool) {
        const yearIndex = parseInt(activeYearLevel.split('-').pop(), 10) - 1;
        const yearObj = selectedSchool.years[yearIndex];
        if (yearObj) {
          const yearName = typeof yearObj === 'string' ? yearObj : yearObj.name;
          loadEducationalContent(activeSchoolLevel, yearName);
        }
      }
    }
  }, [activeSection, activeSchoolLevel, activeYearLevel]);

  // Fetch subscription status for Free/Premium badge in header (once on mount)
  // API returns { success, subscriptions: [...] }; backend only returns active (status=active, ends_at > NOW())
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setIsPremium(false);
      setSubscriptionEndsAt(null);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/me/subscription`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then((res) => {
        // A paying student whose check fails is shown as non-premium, so failures must not pass silently.
        if (!res.ok) {
          reportLoaderFailure({ feature: 'subscription_status', response: res });
          return res.json().catch(() => ({}));
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const list = data?.subscriptions;
        if (data?.success && Array.isArray(list) && list.length > 0) {
          const latest = list[0];
          setIsPremium(true);
          setSubscriptionEndsAt(latest?.ends_at ?? null);
          return;
        }
        setIsPremium(false);
        setSubscriptionEndsAt(list?.[0]?.ends_at || null);
      })
      .catch((error) => {
        reportLoaderFailure({ feature: 'subscription_status', error, endpoint: '/api/me/subscription' });
        if (!cancelled) {
          setIsPremium(false);
          setSubscriptionEndsAt(null);
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch learning progress for Dashboard (overview cards + recent activity badges)
  useEffect(() => {
    if (activeSection !== 'dashboard') return;
    const token = getAuthToken();
    if (!token) {
      setDashboardProgress(null);
      setDashboardProgressLoading(false);
      return;
    }
    let cancelled = false;
    setDashboardProgressLoading(true);
    fetch(`${API_BASE_URL}/api/learning-progress`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
    })
      .then((res) => {
        if (!res.ok) {
          reportLoaderFailure({ feature: 'learning_progress', response: res });
          return res.json().catch(() => ({}));
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data?.success) {
          setDashboardProgress({
            progress: data.progress || {},
            badges: data.badges || { total: 0, list: [] },
            certificates: data.certificates || [],
            streak: data.streak || { displayText: '0 days' }
          });
        } else {
          setDashboardProgress(null);
        }
      })
      .catch((error) => {
        reportLoaderFailure({ feature: 'learning_progress', error, endpoint: '/api/learning-progress' });
        if (!cancelled) setDashboardProgress(null);
      })
      .finally(() => { if (!cancelled) setDashboardProgressLoading(false); });
    return () => { cancelled = true; };
  }, [activeSection]);

  // Path-specific retention list (existing GET /retention/:userId?path=)
  useEffect(() => {
    if (activeSection !== 'learning-path-retention' || !pathRetentionFilter?.id || !currentUser?.id) {
      return;
    }
    const uid = currentUser.id;
    let cancelled = false;
    setPathRetentionLoading(true);
    fetch(`${VOCAB_ANALYTICS_API}/retention/${uid}?path=${encodeURIComponent(pathRetentionFilter.id)}${pathRetentionFilter.language ? `&language=${encodeURIComponent(pathRetentionFilter.language)}` : ''}`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setPathRetentionList(data);
        else if (!cancelled) setPathRetentionList([]);
      })
      .catch(() => { if (!cancelled) setPathRetentionList([]); })
      .finally(() => { if (!cancelled) setPathRetentionLoading(false); });
    return () => { cancelled = true; };
  }, [activeSection, pathRetentionFilter?.id, pathRetentionFilter?.language, currentUser?.id, retentionRefreshTick]);

  // Translate a single text to Arabic (for retention quiz options). Word stays in article language; options = meanings in Arabic.
  const translateToArabic = async (text) => {
    if (!text || typeof text !== 'string') return text;
    const rootUrl = getApiBaseUrl().replace(/\/+$/, '');
    const url = `${rootUrl}/translate?text=${encodeURIComponent(text)}&target=ar&source=en`;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const translated = data.translated && String(data.translated).trim();
          if (translated) return translated;
        }
      } catch {
        /* retry once, then keep the original text */
      }
      if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 400));
    }
    return text;
  };

  // Fetch retention quiz when quiz modal opens. The word stays in English; every option is its meaning in Arabic.
  useEffect(() => {
    if (!showRetentionQuizModal || !retentionQuizArticle?.id || !currentUser?.id) return;
    if (retentionQuizData !== null) return; // already loaded for this open
    let cancelled = false;
    const articleId = retentionQuizArticle.id;
    const userId = currentUser.id;
    setRetentionQuizLoading(true);

    const loadQuiz = async () => {
      const url = `${VOCAB_ANALYTICS_API}/retention/${userId}/quiz/${articleId}`;
      // iPhone Safari cancels a request that starts in the same turn as the article modal closing.
      await new Promise((resolve) => setTimeout(resolve, QUIZ_FETCH_DEFER_MS));
      if (cancelled) return null;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const res = await fetch(url, { cache: 'no-store' });
          const data = await res.json().catch(() => ({}));
          if (res.ok) return data;
        } catch (err) {
          console.warn('Retention quiz fetch failed:', err);
        }
        if (cancelled) return null;
        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
      }
      return { questions: [], fetchError: true };
    };

    loadQuiz()
      .then(async (data) => {
        if (cancelled || !data) return;
        if (data?.fetchError) {
          setRetentionQuizData({ questions: [], fetchError: true });
          return;
        }
        if (!data?.questions?.length) {
          setRetentionQuizData({ questions: [], alreadyCompleted: !!data?.alreadyCompleted });
          return;
        }
        const questions = data.questions;
        const uniqueTexts = [...new Set(questions.flatMap((q) => (q.options || []).map((o) => o.text)).filter(Boolean))];
        const textToAr = {};
        const queue = uniqueTexts.slice();
        const workers = Array.from({ length: Math.min(4, queue.length) }, async () => {
          while (queue.length && !cancelled) {
            const txt = queue.shift();
            textToAr[txt] = await translateToArabic(txt);
          }
        });
        await Promise.all(workers);
        if (cancelled) return;
        setRetentionQuizData({
          questions: questions.map((q) => ({
            ...q,
            options: (q.options || []).map((o) => ({ ...o, text: textToAr[o.text] ?? o.text }))
          }))
        });
        setRetentionQuizSelected(Array(questions.length).fill(null));
      })
      .catch(() => {
        if (!cancelled) {
          setRetentionQuizData((prev) => (prev?.questions?.length ? prev : { questions: [], fetchError: true }));
        }
      })
      .finally(() => { if (!cancelled) setRetentionQuizLoading(false); });
    return () => { cancelled = true; };
  }, [showRetentionQuizModal, retentionQuizArticle?.id, currentUser?.id]);

  const readerModalLocked = showArticleModal || showRetentionQuizModal;
  useEffect(() => {
    if (!readerModalLocked) return undefined;
    // A class keeps the lock steady while the article closes and the quiz opens.
    // Toggling documentElement overflow here makes iPhone Safari cancel the quiz request.
    document.body.classList.add('syllera-modal-open');
    return () => {
      document.body.classList.remove('syllera-modal-open');
    };
  }, [readerModalLocked]);

  // Fetch paid/unlocked learning paths (My Courses tab + dashboard overview + My Learning Paths)
  useEffect(() => {
    if (activeSection !== 'courses' && activeSection !== 'dashboard' && activeSection !== 'learning-paths') return;
    const token = getAuthToken();
    if (!token) {
      setMyCourses([]);
      return;
    }
    let cancelled = false;
    setMyCoursesLoading(true);
    fetch(`${API_BASE_URL}/api/me/my-courses`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success && Array.isArray(data.courses)) {
          setMyCourses(data.courses);
        } else if (!cancelled) {
          setMyCourses([]);
        }
      })
      .catch(() => {
        if (!cancelled) setMyCourses([]);
      })
      .finally(() => {
        if (!cancelled) setMyCoursesLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeSection]);

  // Per-path progress for My Learning Paths (paid six paths only)
  useEffect(() => {
    if (activeSection !== 'dashboard' && activeSection !== 'learning-paths') return;
    const token = getAuthToken();
    if (!token) {
      setLearningPathsProgress([]);
      return;
    }
    let cancelled = false;
    setLearningPathsProgressLoading(true);
    fetch(`${API_BASE_URL}/api/me/learning-paths-progress`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success && Array.isArray(data.paths)) {
          setLearningPathsProgress(data.paths);
        } else if (!cancelled) {
          setLearningPathsProgress([]);
        }
      })
      .catch(() => {
        if (!cancelled) setLearningPathsProgress([]);
      })
      .finally(() => {
        if (!cancelled) setLearningPathsProgressLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeSection]);

  // Learning path badges summary (dashboard card + My Learning Path Badges)
  useEffect(() => {
    if (activeSection !== 'dashboard' && activeSection !== 'learning-path-badges') return;
    const token = getAuthToken();
    if (!token) {
      setPathBadgesSummary(null);
      setPathBadgesError(null);
      setPathBadgesLoading(false);
      return;
    }
    let cancelled = false;
    setPathBadgesLoading(true);
    setPathBadgesError(null);
    fetch(`${API_BASE_URL}/api/me/path-badges`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    })
      .then(async (res) => {
        let data = null;
        try {
          data = await res.json();
        } catch {
          data = null;
        }
        if (cancelled) return;
        if (!res.ok || !data || data.success !== true) {
          setPathBadgesSummary(null);
          setPathBadgesError('failed');
          return;
        }
        setPathBadgesSummary(data);
        setPathBadgesError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setPathBadgesSummary(null);
          setPathBadgesError('failed');
        }
      })
      .finally(() => {
        if (!cancelled) setPathBadgesLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeSection, pathBadgesRetryTick]);

  // When in educational-levels (from My Courses), fetch year levels and (optionally) filter to allowed ids.
  // Explore path (Educational Levels card) should NOT use /api/year-levels at all – it uses the hierarchical schoolLevels config only.
  useEffect(() => {
    if (activeSection !== 'educational-levels') return;
    // If there is no subscription-scoped list of allowed year levels, we are in Explore,
    // so skip fetching /api/year-levels and keep the hierarchical layout.
    if (!allowedEducationalYearLevelIds || allowedEducationalYearLevelIds.length === 0) {
      setYearLevelsFromApi([]);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/year-levels`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.success || !Array.isArray(data.data)) return;
        let list = data.data;
        if (allowedEducationalYearLevelIds && allowedEducationalYearLevelIds.length > 0) {
          const allowed = new Set(allowedEducationalYearLevelIds.map(Number));
          list = data.data.filter((yl) => allowed.has(Number(yl.id)));
        }
        setYearLevelsFromApi(list);
        if (list.length > 0 && !selectedScopedYearLevelId) setSelectedScopedYearLevelId(list[0].id);
      })
      .catch(() => { if (!cancelled) setYearLevelsFromApi([]); });
    return () => { cancelled = true; };
  }, [activeSection, allowedEducationalYearLevelIds]);

  // Fetch unit card placeholder banners (Unit Banners) when on Educational Levels; pass language so backend returns language-specific + default
  useEffect(() => {
    if (activeSection !== 'educational-levels') return;
    let cancelled = false;
    const lang = selectedLearningLanguage ? encodeURIComponent(String(selectedLearningLanguage).trim()) : '';
    const url = `${API_BASE_URL}/api/banners?type=unit_card_placeholder&active=true${lang ? `&language=${lang}` : ''}`;
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.success || !Array.isArray(data.banners)) return;
        setUnitCardBanners(data.banners.map((b) => ({
          unit_id: b.unit_id ?? null,
          image_url: b.image_url,
          language: b.language != null ? String(b.language).trim() : null
        })));
      })
      .catch(() => setUnitCardBanners([]));
    return () => { cancelled = true; };
  }, [activeSection, selectedLearningLanguage]);

  // Pick best unit card banner for a unit: prefer language-specific, then default (no language)
  const getUnitCardBanner = (unitId, banners, selectedLang) => {
    if (!Array.isArray(banners) || banners.length === 0) return null;
    const langNorm = (selectedLang || '').trim().toLowerCase();
    const forUnit = banners.filter((b) => b.unit_id != null && Number(b.unit_id) === Number(unitId));
    const defaults = banners.filter((b) => b.unit_id == null);
    const pick = (list) => {
      if (!list.length) return null;
      const withLang = list.find((b) => b.language && String(b.language).trim().toLowerCase() === langNorm);
      if (withLang) return withLang;
      const noLang = list.find((b) => !b.language || String(b.language).trim() === '');
      return noLang || list[0];
    };
    return pick(forUnit) || pick(defaults);
  };

  // Map year_levels.school_level to same format as articles table (Explore uses "High School", "Middle School", "Primary School")
  const normalizeSchoolForArticles = (schoolLevel) => {
    if (!schoolLevel) return schoolLevel;
    const s = String(schoolLevel).trim().toLowerCase();
    if (s === 'high' || s === 'high school' || s === 'highschool') return 'High School';
    if (s === 'middle' || s === 'middle school' || s === 'middleschool') return 'Middle School';
    if (s === 'primary' || s === 'primary school' || s === 'primaryschool') return 'Primary School';
    return schoolLevel.trim();
  };

  // Map display name to slug for loadEducationalContent (high-school, middle-school, primary-school)
  const getSchoolLevelSlug = (schoolLevelDisplay) => {
    if (!schoolLevelDisplay) return 'high-school';
    const s = String(schoolLevelDisplay).trim().toLowerCase();
    if (s.includes('high')) return 'high-school';
    if (s.includes('middle')) return 'middle-school';
    if (s.includes('primary')) return 'primary-school';
    return 'high-school';
  };

  // Load articles for My Courses using the same API as Explore: GET /articles?school=...&year=...&type=vocabulary (guarded: auth + subscription)
  const loadEducationalContentByYearLevelId = async (yearLevelId) => {
    const yearLevel = yearLevelsFromApi.find((yl) => Number(yl.id) === Number(yearLevelId));
    if (!yearLevel?.school_level || !yearLevel?.year_name) {
      setEducationalContentScoped([]);
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setEducationalContentScoped([]);
      return;
    }
    const school = normalizeSchoolForArticles(yearLevel.school_level);
    const year = String(yearLevel.year_name).trim();
    setEducationalScopedLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/articles?school=${encodeURIComponent(school)}&year=${encodeURIComponent(year)}&type=vocabulary&language=${encodeURIComponent(selectedLearningLanguage || 'English')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (res.ok && Array.isArray(data)) {
        setEducationalContentScoped(data);
      } else {
        setEducationalContentScoped([]);
      }
    } catch {
      setEducationalContentScoped([]);
    } finally {
      setEducationalScopedLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedScopedYearLevelId || !allowedEducationalYearLevelIds?.length || yearLevelsFromApi.length === 0) return;
    const yearLevel = yearLevelsFromApi.find((yl) => Number(yl.id) === Number(selectedScopedYearLevelId));
    if (!yearLevel?.school_level || !yearLevel?.year_name) return;
    const school = normalizeSchoolForArticles(yearLevel.school_level);
    const year = String(yearLevel.year_name).trim();
    const slug = getSchoolLevelSlug(yearLevel.school_level);
    scopedSchoolSlugRef.current = slug;
    scopedYearNameRef.current = year;
    setUnitsLoading(true);
    setEducationalScopedLoading(true);
    setSelectedUnitId(null);
    fetch(`${API_BASE_URL}/api/explore/units?school=${encodeURIComponent(school)}&year=${encodeURIComponent(year)}&language=${encodeURIComponent(selectedLearningLanguage || 'English')}`)
      .then((res) => res.json())
      .then((data) => {
        const unitList = (data && data.units) ? data.units : [];
        setUnits(unitList);
        setUnitsLoading(false);
        setEducationalScopedLoading(false);
        if (unitList.length > 0) {
          setEducationalContentScoped([]);
          return;
        }
        loadEducationalContentByYearLevelId(selectedScopedYearLevelId);
      })
      .catch(() => {
        setUnits([]);
        setUnitsLoading(false);
        setEducationalScopedLoading(false);
        loadEducationalContentByYearLevelId(selectedScopedYearLevelId);
      });
  }, [selectedScopedYearLevelId, allowedEducationalYearLevelIds?.length, yearLevelsFromApi, selectedLearningLanguage]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  // Initialize Live Learning data
  const initializeLiveLearning = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTeachers(),
        loadBookings(),
        loadMyTeachers(),
        loadRatings()
      ]);
    } catch (error) {
      console.error('Error initializing live learning:', error);
      showNotification('Failed to load live learning data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Live Learning API Functions
  const loadTeachers = async () => {
    try {
      const response = await fetch(`${LIVE_LEARNING_API}/api/teachers`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const teachersData = await response.json();
      setTeachers(teachersData);
      setFilteredTeachers(teachersData);
    } catch (error) {
      console.error('Error loading teachers:', error);
      showNotification('Failed to load teachers. Please try again.', 'error');
      
      // Fallback to mock data for development
      const mockTeachers = [
        {
          id: 1,
          name: 'Mike Johnson',
          specialty: 'English, IELTS',
          rating: 4.8,
          hourlyRate: 45,
          availability: 'Available Today',
          image: 'https://via.placeholder.com/80',
          bio: 'Experienced English teacher with 5+ years of teaching experience.',
          subjects: ['English', 'IELTS'],
          totalStudents: 156,
          totalHours: 1200
        },
        {
          id: 2,
          name: 'Maria Garcia',
          specialty: 'Spanish, English',
          rating: 4.9,
          hourlyRate: 40,
          availability: 'Available Tomorrow',
          image: 'https://via.placeholder.com/80',
          bio: 'Native Spanish speaker with teaching certification.',
          subjects: ['Spanish', 'English'],
          totalStudents: 89,
          totalHours: 650
        },
        {
          id: 3,
          name: 'Sarah Chen',
          specialty: 'Chinese, TOEFL',
          rating: 4.7,
          hourlyRate: 50,
          availability: 'This Week',
          image: 'https://via.placeholder.com/80',
          bio: 'Certified Chinese teacher with TOEFL expertise.',
          subjects: ['Chinese', 'TOEFL'],
          totalStudents: 203,
          totalHours: 1500
        }
      ];
      setTeachers(mockTeachers);
      setFilteredTeachers(mockTeachers);
    }
  };
  const loadBookings = async () => {
    try {
      const response = await fetch(`${LIVE_LEARNING_API}/api/bookings`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const allBookings = await response.json();
      
      // Group bookings by status
      const groupedBookings = {
        upcoming: allBookings.filter(booking => booking.status === 'upcoming'),
        completed: allBookings.filter(booking => booking.status === 'completed'),
        cancelled: allBookings.filter(booking => booking.status === 'cancelled')
      };
      
      setBookings(groupedBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
      showNotification('Failed to load bookings. Please try again.', 'error');
      
      // Fallback to mock data for development
      const mockBookings = {
        upcoming: [
          {
            id: 1,
            teacherName: 'Mike Johnson',
            teacherImage: 'https://via.placeholder.com/50',
            subject: 'IELTS Speaking',
            date: '2024-01-20',
            time: '14:00',
            duration: 60,
            status: 'upcoming'
          },
          {
            id: 2,
            teacherName: 'Maria Garcia',
            teacherImage: 'https://via.placeholder.com/50',
            subject: 'Spanish Conversation',
            date: '2024-01-22',
            time: '16:00',
            duration: 60,
            status: 'upcoming'
          }
        ],
        completed: [
          {
            id: 3,
            teacherName: 'Sarah Chen',
            teacherImage: 'https://via.placeholder.com/50',
            subject: 'Chinese Basics',
            date: '2024-01-15',
            time: '10:00',
            duration: 60,
            status: 'completed'
          }
        ],
        cancelled: [
          {
            id: 4,
            teacherName: 'Mike Johnson',
            teacherImage: 'https://via.placeholder.com/50',
            subject: 'English Grammar',
            date: '2024-01-10',
            time: '12:00',
            duration: 60,
            status: 'cancelled'
          }
        ]
      };
      setBookings(mockBookings);
    }
  };

  const loadMyTeachers = async () => {
    // Simulate API call
    const mockMyTeachers = [
      {
        id: 1,
        name: 'Mike Johnson',
        image: 'https://via.placeholder.com/80',
        specialty: 'English, IELTS',
        rating: 4.8,
        lessonsCompleted: 12,
        lastLesson: '2024-01-15'
      },
      {
        id: 2,
        name: 'Maria Garcia',
        image: 'https://via.placeholder.com/80',
        specialty: 'Spanish, English',
        rating: 4.9,
        lessonsCompleted: 8,
        lastLesson: '2024-01-14'
      }
    ];
    setMyTeachers(mockMyTeachers);
  };

  const loadRatings = async () => {
    // Simulate API call
    const mockRatings = [
      {
        id: 1,
        teacherName: 'Sarah Chen',
        subject: 'Chinese Basics',
        date: '2024-01-15',
        canRate: true
      }
    ];
    setRatings(mockRatings);
  };

  // Filter teachers based on search and filters
  const filterTeachers = () => {
    const searchTermLower = teacherSearch.toLowerCase();
    const filtered = teachers.filter(teacher => {
      const matchesSearch = teacher.name.toLowerCase().includes(searchTermLower) || 
                          teacher.specialty.toLowerCase().includes(searchTermLower);
      const matchesSpecialty = !specialtyFilter || teacher.subjects.includes(specialtyFilter);
      const matchesRating = !ratingFilter || teacher.rating >= parseFloat(ratingFilter);
      const matchesAvailability = !availabilityFilter || teacher.availability.includes(availabilityFilter);

      return matchesSearch && matchesSpecialty && matchesRating && matchesAvailability;
    });
    setFilteredTeachers(filtered);
  };

  // Update filters and re-filter
  useEffect(() => {
    filterTeachers();
  }, [teacherSearch, specialtyFilter, ratingFilter, availabilityFilter, teachers]);

  // Booking functions
  const joinLesson = (bookingId) => {
    console.log('Joining lesson:', bookingId);
    showNotification('Joining lesson...', 'info');
  };

  const rescheduleBooking = (bookingId) => {
    console.log('Rescheduling booking:', bookingId);
    showNotification('Reschedule functionality coming soon!', 'info');
  };

  const cancelBooking = (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      console.log('Cancelling booking:', bookingId);
      showNotification('Booking cancelled successfully!', 'success');
      loadBookings();
    }
  };

  const rateLesson = (bookingId) => {
    console.log('Rating lesson:', bookingId);
    openRatingModal(bookingId);
  };

  const viewTeacherProfile = (teacherId) => {
    console.log('Viewing teacher profile:', teacherId);
    showNotification('Teacher profile view coming soon!', 'info');
  };

  // Calculate booking amount
  const calculateBookingAmount = (duration, hourlyRate) => {
    return (parseInt(duration) / 60) * hourlyRate;
  };
  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Update booking counts
  const updateBookingCounts = () => {
    return {
      upcoming: bookings.upcoming.length,
      completed: bookings.completed.length,
      cancelled: bookings.cancelled.length
    };
  };

  // Initialize shop data
  const initializeShop = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCategories(),
        loadCart(),
        loadWishlist()
      ]);
    } catch (error) {
      console.error('Error initializing shop:', error);
      showNotification('Failed to load shop data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/categories`);
      const categories = await response.json();
      setCategories(categories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Load cart from API
  const loadCart = async () => {
    try {
      const response = await fetch(`${API_BASE}/cart?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setCart(data.items || []);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  // Load wishlist from API
  const loadWishlist = async () => {
    try {
      const response = await fetch(`${API_BASE}/wishlist?userId=${DEMO_USER_ID}`);
      const data = await response.json();
      setWishlist(data.items || []);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  // Load products from API
  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        category: currentCategory,
        page: currentPage,
        limit: 12,
        ...currentFilters
      });

      const response = await fetch(`${API_BASE}/products?${params}`);
      const data = await response.json();
      
      setFilteredProducts(data.products || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error loading products:', error);
      showNotification('Failed to load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart
  const addToCart = async (productId, quantity = 1) => {
    try {
      const response = await fetch(`${API_BASE}/cart`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId,
          quantity,
          userId: DEMO_USER_ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadCart();
        showNotification('Item added to cart!', 'success');
      } else {
        showNotification(result.error || 'Failed to add item to cart', 'error');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showNotification('Failed to add item to cart', 'error');
    }
  };

  // Update cart item quantity
  const updateCartQuantity = async (productId, quantity) => {
    try {
      const response = await fetch(`${API_BASE}/cart/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quantity,
          userId: DEMO_USER_ID
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadCart();
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };
  // Remove item from cart
  const removeFromCart = async (productId) => {
    try {
      const response = await fetch(`${API_BASE}/cart/${productId}?userId=${DEMO_USER_ID}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        await loadCart();
        showNotification('Item removed from cart', 'success');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  // Toggle wishlist item
  const toggleWishlistItem = async (productId) => {
    try {
      const isInWishlist = wishlist.some(item => item.product_id === productId);
      
      if (isInWishlist) {
        // Remove from wishlist
        const response = await fetch(`${API_BASE}/wishlist/${productId}?userId=${DEMO_USER_ID}`, {
          method: 'DELETE'
        });
        
        const result = await response.json();
        if (result.success) {
          await loadWishlist();
          showNotification('Removed from wishlist', 'success');
        }
      } else {
        // Add to wishlist
        const response = await fetch(`${API_BASE}/wishlist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            productId,
            userId: DEMO_USER_ID
          })
        });
        
        const result = await response.json();
        if (result.success) {
          await loadWishlist();
          showNotification('Added to wishlist!', 'success');
        }
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
    }
  };

  // Handle search
  const handleSearch = async () => {
    setCurrentFilters(prev => ({ ...prev, search: searchTerm }));
    setCurrentPage(1);
    await loadProducts();
  };

  // Handle filter changes
  const handleFilterChange = async () => {
    const newFilters = { ...currentFilters };
    
    if (priceFilter) {
      const [min, max] = priceFilter.split('-');
      newFilters.minPrice = min;
      newFilters.maxPrice = max === '+' ? null : max;
    } else {
      delete newFilters.minPrice;
      delete newFilters.maxPrice;
    }
    
    setCurrentFilters(newFilters);
    setCurrentPage(1);
    await loadProducts();
  };

  // Handle sort changes
  const handleSortChange = async () => {
    const newFilters = { ...currentFilters };
    
    switch (sortBy) {
      case 'price-low':
        newFilters.sortBy = 'price';
        newFilters.sortOrder = 'ASC';
        break;
      case 'price-high':
        newFilters.sortBy = 'price';
        newFilters.sortOrder = 'DESC';
        break;
      case 'rating':
        newFilters.sortBy = 'rating';
        newFilters.sortOrder = 'DESC';
        break;
      case 'newest':
        newFilters.sortBy = 'created_at';
        newFilters.sortOrder = 'DESC';
        break;
      default:
        newFilters.sortBy = 'name';
        newFilters.sortOrder = 'ASC';
    }
    
    setCurrentFilters(newFilters);
    setCurrentPage(1);
    await loadProducts();
  };

  // Change page
  const changePage = async (page) => {
    if (page < 1 || page > totalPages) return;
    
    setCurrentPage(page);
    await loadProducts();
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  // Generate star rating JSX
  const generateStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    const stars = [];
    
    // Add full stars
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={`full-${i}`} className="material-icons">star</span>);
    }
    
    // Add half star
    if (hasHalfStar) {
      stars.push(<span key="half" className="material-icons">star_half</span>);
    }
    
    // Add empty stars
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="material-icons">star_border</span>);
    }
    
    return stars;
  };

  // Calculate cart total
  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate cart count
  const getCartCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  // Support Classes Functions
  const openSupportClassModal = async (type) => {
    // If it's workshops, open workshop view directly
    if (type === 'workshops') {
      setShowWorkshopView(true);
      await loadWorkshops();
      return;
    }
    // If it's normal classes, open normal classes view directly
    if (type === 'normal') {
      setShowNormalClassesView(true);
      await loadNormalClasses();
      return;
    }
    setSupportClassModal({ show: true, type });
    setSelectedSchoolLevel('');
    setSelectedSubject('');
    setSelectedYear('');
  };

  const loadWorkshops = async () => {
    setLoadingWorkshop(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/workshops?status=upcoming`);
      if (response.ok) {
        const data = await response.json();
        // Store workshops for display
        setRecordedCourses(data.data || []);
      }
    } catch (error) {
      console.error('Error loading workshops:', error);
    } finally {
      setLoadingWorkshop(false);
    }
  };

  const loadNormalClasses = async () => {
    setLoadingNormalClasses(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/support-classes?type=normal&status=active`);
      if (response.ok) {
        const data = await response.json();
        setNormalClasses(data.data || []);
      }
    } catch (error) {
      console.error('Error loading normal classes:', error);
    } finally {
      setLoadingNormalClasses(false);
    }
  };

  const openNormalClassDetail = async (classId) => {
    setLoadingNormalClasses(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/support-classes/${classId}`);
      if (!response.ok) throw new Error('Failed to load class');
      
      const data = await response.json();
      setSelectedNormalClass(data.data);
      setShowNormalClassesView(true);
    } catch (error) {
      console.error('Error opening normal class:', error);
      alert('Failed to load class details');
    } finally {
      setLoadingNormalClasses(false);
    }
  };

  const openWorkshopDetail = async (workshopId) => {
    setLoadingWorkshop(true);
    try {
      // Fetch workshop details
      const workshopResponse = await fetch(`${API_BASE_URL}/api/workshops/${workshopId}`);
      if (!workshopResponse.ok) throw new Error('Failed to load workshop');

      const workshopData = await workshopResponse.json();
      setSelectedWorkshop(workshopData.data);

      // Check enrollment
      let isEnrolled = false;
      const enrollmentResponse = await fetch(
        `${API_BASE_URL}/api/workshops/${workshopId}/check-enrollment?user_id=${DEMO_USER_ID}`
      );
      if (enrollmentResponse.ok) {
        const enrollmentData = await enrollmentResponse.json();
        isEnrolled = enrollmentData.is_enrolled || false;
        setIsEnrolledInWorkshop(isEnrolled);
      }

      // Load channel posts if enrolled
      if (isEnrolled) {
        await loadWorkshopChannel(workshopId);
        await loadWorkshopSessions(workshopId);
      }

      setShowWorkshopView(true);
      setActiveWorkshopTab('overview');
    } catch (error) {
      console.error('Error opening workshop:', error);
      alert('Failed to load workshop details');
    } finally {
      setLoadingWorkshop(false);
    }
  };

  const loadWorkshopChannel = async (workshopId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/workshops/${workshopId}/channel?user_id=${DEMO_USER_ID}`
      );
      if (response.ok) {
        const data = await response.json();
        setWorkshopChannelPosts(data.data || []);
      } else if (response.status === 403) {
        // Not enrolled, clear channel
        setWorkshopChannelPosts([]);
      }
    } catch (error) {
      console.error('Error loading workshop channel:', error);
    }
  };

  const loadWorkshopSessions = async (workshopId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/workshops/${workshopId}/sessions?user_id=${DEMO_USER_ID}`
      );
      if (response.ok) {
        const data = await response.json();
        setWorkshopSessions(data.data || []);
      } else if (response.status === 403) {
        // Not enrolled, clear sessions
        setWorkshopSessions([]);
      }
    } catch (error) {
      console.error('Error loading workshop sessions:', error);
    }
  };

  const enrollInWorkshop = async (workshopId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workshops/${workshopId}/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: DEMO_USER_ID,
          payment_status: 'paid',
          payment_amount: selectedWorkshop?.price || 0
        })
      });

      if (response.ok) {
        setIsEnrolledInWorkshop(true);
        await loadWorkshopChannel(workshopId);
        await loadWorkshopSessions(workshopId);
        showNotification('Successfully enrolled in workshop!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to enroll', 'error');
      }
    } catch (error) {
      console.error('Error enrolling in workshop:', error);
      showNotification('Failed to enroll in workshop', 'error');
    }
  };

  const closeSupportClassModal = () => {
    setSupportClassModal({ show: false, type: null });
    setSelectedSchoolLevel('');
    setSelectedSubject('');
    setSelectedYear('');
  };

  const getSchoolLevels = () => {
    return [
      { value: 'primary', label: 'Primary School', years: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'] },
      { value: 'middle', label: 'Middle School', years: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4'] },
      { value: 'high', label: 'High School', years: ['Grade 1', 'Grade 2', 'Grade 3'] }
    ];
  };

  const getSubjectsByLevel = (level) => {
    const subjects = {
      primary: ['English', 'Math', 'Science', 'Social Studies', 'Arabic'],
      middle: ['English', 'Math', 'Physics', 'Chemistry', 'Biology', 'Arabic', 'Islamic Studies'],
      high: ['English', 'Math', 'Physics', 'Chemistry', 'Biology', 'Arabic', 'Islamic Studies', 'Computer Science']
    };
    return subjects[level] || [];
  };

  const loadRecordedCourses = async (schoolLevel, subject, year) => {
    setLoadingRecordedCourses(true);
    try {
      // TODO: Replace with actual API call
      // const response = await fetch(`/api/support-classes/recorded?schoolLevel=${schoolLevel}&subject=${subject}&year=${year}`);
      // const data = await response.json();
      
      // Mock data for now
      const mockCourses = [
        {
          id: 1,
          title: `${subject} - ${year} - Lesson 1`,
          instructor: 'Expert Teacher',
          rating: 4.8,
          reviewCount: 124,
          duration: '2h 30m',
          students: 450,
          image: `https://via.placeholder.com/300x200/667eea/ffffff?text=${encodeURIComponent(subject)}`,
          featured: false
        },
        {
          id: 2,
          title: `${subject} - ${year} - Lesson 2`,
          instructor: 'Expert Teacher',
          rating: 4.9,
          reviewCount: 89,
          duration: '3h 15m',
          students: 320,
          image: `https://via.placeholder.com/300x200/764ba2/ffffff?text=${encodeURIComponent(subject)}`,
          featured: true
        },
        {
          id: 3,
          title: `${subject} - ${year} - Lesson 3`,
          instructor: 'Expert Teacher',
          rating: 4.7,
          reviewCount: 156,
          duration: '2h 45m',
          students: 280,
          image: `https://via.placeholder.com/300x200/f093fb/ffffff?text=${encodeURIComponent(subject)}`,
          featured: false
        }
      ];
      
      setRecordedCourses(mockCourses);
    } catch (error) {
      console.error('Error loading recorded courses:', error);
      showNotification('Failed to load courses', 'error');
    } finally {
      setLoadingRecordedCourses(false);
    }
  };

  const handleSupportClassSubmit = () => {
    if (!selectedSchoolLevel || !selectedSubject || !selectedYear) {
      showNotification('Please select school level, subject, and year', 'error');
      return;
    }

    const supportClassData = {
      type: supportClassModal.type,
      schoolLevel: selectedSchoolLevel,
      subject: selectedSubject,
      year: selectedYear
    };

    // If it's recorded courses, show the course cards view
    if (supportClassModal.type === 'recorded') {
      closeSupportClassModal();
      setShowRecordedCoursesView(true);
      loadRecordedCourses(selectedSchoolLevel, selectedSubject, selectedYear);
      showNotification(`Loading ${selectedSubject} courses for ${selectedYear}`, 'success');
    } else {
      // For workshops and normal classes, use the old behavior
      showNotification(`Starting ${supportClassModal.type} for ${selectedSubject} - ${selectedYear}`, 'success');
      closeSupportClassModal();
    }
    
    console.log('Support Class Data:', supportClassData);
  };

  const handleBackToSupportClasses = () => {
    setShowRecordedCoursesView(false);
    setRecordedCourses([]);
    setSelectedSchoolLevel('');
    setSelectedSubject('');
    setSelectedYear('');
  };

  // Live Learning Functions
  const openBookingModal = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId) || filteredTeachers.find(t => t.id === teacherId);
    if (teacher) {
      setCurrentTeacherId(teacherId);
      setCurrentTeacherRate(teacher.hourlyRate || teacher.rate);
      setBookingModal({ show: true, teacher: teacher });
    }
  };

  const closeBookingModal = () => {
    setBookingModal({ show: false, teacher: null });
    setCurrentTeacherId(null);
    setCurrentTeacherRate(0);
  };

  const openRatingModal = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId) || filteredTeachers.find(t => t.id === teacherId);
    if (teacher) {
      setRatingModal({ show: true, teacher: teacher });
    }
  };
  const closeRatingModal = () => {
    setRatingModal({ show: false, teacher: null });
  };

  const handleBookingSubmit = async (bookingData) => {
    try {
      const bookingPayload = {
        student_id: DEMO_USER_ID,
        teacher_id: currentTeacherId,
        booking_date: `${bookingData.date} ${bookingData.time}`,
        duration: parseInt(bookingData.duration),
        subject: bookingData.subject,
        amount: calculateBookingAmount(bookingData.duration, currentTeacherRate),
        notes: bookingData.notes
      };

      const response = await fetch(`${LIVE_LEARNING_API}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bookingPayload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      showNotification('Booking confirmed successfully!', 'success');
      closeBookingModal();
      loadBookings();
    } catch (error) {
      console.error('Error creating booking:', error);
      showNotification('Failed to create booking. Please try again.', 'error');
    }
  };

  const handleRatingSubmit = async (ratingData) => {
    try {
      const ratingPayload = {
        overallRating: ratingData.overallRating,
        comment: ratingData.comment,
        teachingQuality: ratingData.teachingQuality,
        communication: ratingData.communication,
        punctuality: ratingData.punctuality,
        teacherId: ratingModal.teacher?.id,
        lessonId: ratingData.lessonId
      };

      const response = await fetch(`${LIVE_LEARNING_API}/api/ratings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ratingPayload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      showNotification('Rating submitted successfully!', 'success');
      closeRatingModal();
      loadRatings();
    } catch (error) {
      console.error('Error submitting rating:', error);
      showNotification('Failed to submit rating. Please try again.', 'error');
    }
  };

  // Course Library Functions
  const loadCourses = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      if (courseSearch) params.append('search', courseSearch);
      if (categoryFilter) params.append('category', categoryFilter);
      if (levelFilter) params.append('level', levelFilter);
      if (priceFilterCourse) {
        if (priceFilterCourse === 'free') {
          params.append('price_min', '0');
          params.append('price_max', '0');
        } else if (priceFilterCourse === 'under50') {
          params.append('price_max', '50');
        } else if (priceFilterCourse === 'under100') {
          params.append('price_max', '100');
        }
      }
      if (ratingFilterCourse) params.append('rating_min', ratingFilterCourse);
      if (sortFilter) params.append('sort_by', sortFilter);
      
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/courses?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform the data to match our component structure
      const transformedCourses = data.courses.map(course => ({
        id: course.id,
        title: course.title,
        instructor: course.instructor_name || 'Instructor',
        instructorImage: course.instructor_avatar || (course.instructor_name ? `https://via.placeholder.com/50/667eea/ffffff?text=${encodeURIComponent(course.instructor_name.charAt(0))}` : 'https://via.placeholder.com/50/667eea/ffffff?text=I'),
        category: course.category,
        level: course.level,
        rating: parseFloat(course.avg_rating) || 0,
        reviewCount: course.review_count || 0,
        price: parseFloat(course.price) || 0,
        originalPrice: parseFloat(course.original_price) || parseFloat(course.price) || 0,
        duration: course.duration || 'Duration not specified',
        students: course.students_enrolled || 0,
        image: course.cover_image_url || course.thumbnail_url || (course.title ? `https://via.placeholder.com/300x200/667eea/ffffff?text=${encodeURIComponent(course.title.charAt(0))}` : 'https://via.placeholder.com/300x200/667eea/ffffff'),
        description: course.description || 'No description available',
        featured: course.is_featured === 1,
        learningObjectives: course.curriculum ? course.curriculum.map(item => item.title) : []
      }));
      
      setCourses(transformedCourses);
      setFilteredCourses(transformedCourses);
    } catch (error) {
      console.error('Error loading courses:', error);
      showNotification('Failed to load courses', 'error');
      
      // Fallback to mock data if API fails
    const mockCourses = [
      {
        id: 1,
        title: 'Complete JavaScript Masterclass',
        instructor: 'John Smith',
        instructorImage: 'https://via.placeholder.com/50',
        category: 'programming',
        level: 'intermediate',
        rating: 4.8,
        reviewCount: 1247,
        price: 89.99,
        originalPrice: 129.99,
        duration: '12h 30m',
        students: 15420,
        image: 'https://via.placeholder.com/300x200',
        description: 'Master JavaScript from basics to advanced concepts including ES6+, DOM manipulation, and modern frameworks.',
        learningObjectives: [
          'Understand JavaScript fundamentals and ES6+ features',
          'Build interactive web applications',
          'Master DOM manipulation and event handling',
          'Learn modern JavaScript frameworks'
        ],
        featured: true
      },
      {
        id: 2,
        title: 'UI/UX Design Fundamentals',
        instructor: 'Sarah Johnson',
        instructorImage: 'https://via.placeholder.com/50',
        category: 'design',
        level: 'beginner',
        rating: 4.9,
        reviewCount: 892,
        price: 69.99,
        originalPrice: 99.99,
        duration: '8h 45m',
        students: 8920,
        image: 'https://via.placeholder.com/300x200',
        description: 'Learn the fundamentals of UI/UX design with practical projects and real-world examples.',
        learningObjectives: [
          'Understand design principles and user psychology',
          'Create wireframes and prototypes',
          'Design user interfaces for web and mobile',
          'Conduct user research and testing'
        ],
        featured: true
      }
    ];
    setCourses(mockCourses);
    setFilteredCourses(mockCourses);
    } finally {
      setLoading(false);
    }
  };

  const loadMyLearning = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/enrolled/${DEMO_USER_ID}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const enrolledCourses = await response.json();
      
      // Transform the data to match our component structure
      const transformedMyLearning = enrolledCourses.map(course => ({
        id: course.id,
        title: course.title,
        instructor: course.instructor_name || 'Instructor',
        progress: course.progress_percentage || 0,
        lastAccessed: course.enrolled_at ? new Date(course.enrolled_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        totalLessons: Math.floor(Math.random() * 50) + 20, // Mock data for now
        completedLessons: Math.floor((course.progress_percentage || 0) / 100 * (Math.floor(Math.random() * 50) + 20)),
        nextLesson: 'Introduction' // Mock data for now
      }));
      
      setMyLearning(transformedMyLearning);
    } catch (error) {
      console.error('Error loading my learning:', error);
      showNotification('Failed to load enrolled courses', 'error');
      
      // Fallback to mock data
    const mockMyLearning = [
      {
        id: 1,
        title: 'Complete JavaScript Masterclass',
        instructor: 'John Smith',
        progress: 75,
        lastAccessed: '2024-01-15',
        totalLessons: 45,
        completedLessons: 34,
        nextLesson: 'DOM Manipulation Advanced'
      },
      {
        id: 2,
        title: 'UI/UX Design Fundamentals',
        instructor: 'Sarah Johnson',
        progress: 45,
        lastAccessed: '2024-01-14',
        totalLessons: 32,
        completedLessons: 14,
        nextLesson: 'Wireframing Techniques'
      }
    ];
    setMyLearning(mockMyLearning);
    }
  };

  const loadCourseWishlist = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/wishlist/${DEMO_USER_ID}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const wishlistCourses = await response.json();
      
      // Transform the data to match our component structure
      const transformedWishlist = wishlistCourses.map(course => ({
        id: course.id,
        title: course.title,
        instructor: course.instructor_name || 'Instructor',
        price: parseFloat(course.price) || 0,
        originalPrice: parseFloat(course.original_price) || parseFloat(course.price) || 0,
        rating: parseFloat(course.avg_rating) || 0,
        reviewCount: course.review_count || 0,
        image: course.cover_image_url || course.thumbnail_url || (course.title ? `https://via.placeholder.com/300x200/667eea/ffffff?text=${encodeURIComponent(course.title.charAt(0))}` : 'https://via.placeholder.com/300x200/667eea/ffffff'),
        description: course.description || 'No description available'
      }));
      
      setCourseWishlist(transformedWishlist);
    } catch (error) {
      console.error('Error loading wishlist:', error);
      showNotification('Failed to load wishlist', 'error');
      
      // Fallback to mock data
    const mockWishlist = [
      {
        id: 3,
        title: 'Business Strategy & Management',
        instructor: 'Michael Chen',
        price: 149.99,
        originalPrice: 199.99,
        rating: 4.7,
        reviewCount: 567
      },
      {
        id: 4,
        title: 'Digital Marketing Mastery',
        instructor: 'Emily Davis',
        price: 79.99,
        originalPrice: 119.99,
        rating: 4.6,
        reviewCount: 734
      }
    ];
    setCourseWishlist(mockWishlist);
    }
  };

  const loadCertificates = async () => {
    // Mock data for certificates
    const mockCertificates = [
      {
        id: 1,
        title: 'JavaScript Fundamentals',
        instructor: 'John Smith',
        completedDate: '2024-01-10',
        certificateUrl: '#',
        grade: 'A+',
        hours: 12
      },
      {
        id: 2,
        title: 'Web Design Basics',
        instructor: 'Sarah Johnson',
        completedDate: '2023-12-15',
        certificateUrl: '#',
        grade: 'A',
        hours: 8
      }
    ];
    setCertificates(mockCertificates);
  };
  // Filter courses based on search and filters
  const filterCourses = () => {
    if (!courses.length) return;
    
    const searchTermLower = courseSearch.toLowerCase();
    const filtered = courses.filter(course => {
      const matchesSearch = course.title.toLowerCase().includes(searchTermLower) || 
                          course.instructor.toLowerCase().includes(searchTermLower);
      const matchesCategory = !categoryFilter || course.category === categoryFilter;
      const matchesLevel = !levelFilter || course.level === levelFilter;
      const matchesRating = !ratingFilterCourse || course.rating >= parseFloat(ratingFilterCourse);
      
      let matchesPrice = true;
      if (priceFilterCourse === 'free') {
        matchesPrice = course.price === 0;
      } else if (priceFilterCourse === 'paid') {
        matchesPrice = course.price > 0;
      } else if (priceFilterCourse === 'under50') {
        matchesPrice = course.price < 50;
      } else if (priceFilterCourse === 'under100') {
        matchesPrice = course.price < 100;
      }

      return matchesSearch && matchesCategory && matchesLevel && matchesRating && matchesPrice;
    });

    // Sort courses
    let sorted = [...filtered];
    switch (sortFilter) {
      case 'newest':
        sorted.sort((a, b) => b.id - a.id);
        break;
      case 'rating':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'price-low':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        sorted.sort((a, b) => b.price - a.price);
        break;
      default: // popular
        sorted.sort((a, b) => b.students - a.students);
    }

    setFilteredCourses(sorted);
  };

  // Update filters and re-filter
  useEffect(() => {
    filterCourses();
  }, [courseSearch, categoryFilter, levelFilter, priceFilterCourse, ratingFilterCourse, sortFilter, courses]);

  // Load Course Library data when section is active
  useEffect(() => {
    if (activeSection === 'course-library') {
      initializeCourseLibrary();
    }
  }, [activeSection]);

  // Load course type images on component mount
  useEffect(() => {
    loadCourseTypeImages();
  }, []);

  const loadCourseTypeImages = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/course-type-images/course-type-images`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.images) {
          setCourseTypeImages(prev => ({
            ...prev,
            ...data.images
          }));
        }
      }
    } catch (error) {
      console.error('Error loading course type images:', error);
    }
  };

  // Load Talk data when section is active
  useEffect(() => {
    if (activeSection === 'talk') {
      loadRooms();
      loadMyActivity();
    }
  }, [activeSection]);

  // Filter rooms when filters change
  useEffect(() => {
    if (activeSection === 'talk' && activeTalkTab === 'communication-hub') {
      filterRooms();
    }
  }, [roomSearch, languageFilter, typeFilter, levelFilterTalk, categoryFilterTalk, rooms]);

  const initializeCourseLibrary = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadCourses(),
        loadMyLearning(),
        loadCourseWishlist(),
        loadCertificates()
      ]);
    } catch (error) {
      console.error('Error initializing course library:', error);
      showNotification('Failed to load course library data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Course Library action functions
  const openCourseDetailModal = async (courseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/courses/${courseId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const courseData = await response.json();
      
      // Check enrollment status
      let isEnrolled = false;
      try {
        const enrollmentResponse = await fetch(`${API_BASE_URL}/api/user-course-library/courses/${courseId}/check-enrollment?user_id=${DEMO_USER_ID}`);
        if (enrollmentResponse.ok) {
          const enrollmentData = await enrollmentResponse.json();
          isEnrolled = enrollmentData.is_enrolled || false;
        }
      } catch (err) {
        console.error('Error checking enrollment:', err);
      }
      
      // Get course videos with access control
      let videos = courseData.videos || [];
      try {
        const videosResponse = await fetch(`${API_BASE_URL}/api/user-course-library/courses/${courseId}/videos?user_id=${DEMO_USER_ID}`);
        if (videosResponse.ok) {
          const videosData = await videosResponse.json();
          videos = videosData.videos || courseData.videos || [];
          isEnrolled = videosData.is_enrolled !== undefined ? videosData.is_enrolled : isEnrolled;
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        // Fallback to videos from courseData if available
        videos = courseData.videos || [];
      }
      
      // Transform the data to match our component structure
      const transformedCourse = {
        id: courseData.id,
        title: courseData.title,
        instructor: courseData.instructor_name || 'Instructor',
        instructorImage: courseData.instructor_avatar || (courseData.instructor_name ? `https://via.placeholder.com/50/667eea/ffffff?text=${encodeURIComponent(courseData.instructor_name.charAt(0))}` : 'https://via.placeholder.com/50/667eea/ffffff?text=I'),
        category: courseData.category,
        level: courseData.level,
        rating: parseFloat(courseData.avg_rating) || 0,
        reviewCount: courseData.review_count || 0,
        price: parseFloat(courseData.price) || 0,
        originalPrice: parseFloat(courseData.original_price) || parseFloat(courseData.price) || 0,
        duration: courseData.duration || 'Duration not specified',
        students: courseData.students_enrolled || 0,
        image: courseData.cover_image_url || courseData.thumbnail_url || (courseData.title ? `https://via.placeholder.com/300x200/667eea/ffffff?text=${encodeURIComponent(courseData.title.charAt(0))}` : 'https://via.placeholder.com/300x200/667eea/ffffff'),
        description: courseData.description || 'No description available',
        featured: courseData.is_featured === 1,
        learningObjectives: courseData.curriculum ? courseData.curriculum.map(item => item.title) : [],
        instructorBio: courseData.instructor_bio || 'Experienced educator with years of teaching experience in this field.',
        reviews: courseData.reviews || []
      };
      
      setCourseDetailModal({ 
        show: true, 
        course: transformedCourse,
        videos: videos,
        isEnrolled: isEnrolled
      });
    } catch (error) {
      console.error('Error fetching course details:', error);
      showNotification('Failed to load course details', 'error');
      
      // Fallback to local course data
      const course = courses.find(c => c.id === courseId);
      if (course) {
        setCourseDetailModal({ 
          show: true, 
          course: course,
          videos: [],
          isEnrolled: false
        });
      }
    }
  };

  const closeCourseDetailModal = () => {
    setCourseDetailModal({ show: false, course: null, videos: [], isEnrolled: false });
  };
  const enrollCourse = async (courseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          course_id: courseId,
          user_id: DEMO_USER_ID
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Enrollment failed');
      }
      
      const result = await response.json();
      
      // Refresh course detail modal to show unlocked content
      if (courseDetailModal.show && courseDetailModal.course?.id === courseId) {
        openCourseDetailModal(courseId);
      }
      
      // Update local state
    const course = courses.find(c => c.id === courseId);
    if (course) {
      const enrolledCourse = {
        id: course.id,
        title: course.title,
        instructor: course.instructor,
        progress: 0,
        lastAccessed: new Date().toISOString().split('T')[0],
        totalLessons: Math.floor(Math.random() * 50) + 20,
        completedLessons: 0,
        nextLesson: 'Introduction'
      };
      setMyLearning(prev => [...prev, enrolledCourse]);
      
      // Remove from wishlist if present
      setCourseWishlist(prev => prev.filter(c => c.id !== courseId));
      }
      
      showNotification(result.message || 'Successfully enrolled in course!', 'success');
      closeCourseDetailModal();
    } catch (error) {
      console.error('Error enrolling in course:', error);
      showNotification(error.message || 'Failed to enroll in course', 'error');
    }
  };

  const addToCourseWishlist = async (courseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/wishlist/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          course_id: courseId,
          user_id: DEMO_USER_ID
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add to wishlist');
      }
      
      const result = await response.json();
      
      // Update local state
    const course = courses.find(c => c.id === courseId);
    if (course) {
      const wishlistItem = {
        id: course.id,
        title: course.title,
        instructor: course.instructor,
        price: course.price,
        originalPrice: course.originalPrice,
        rating: course.rating,
          reviewCount: course.reviewCount,
          image: course.image,
          description: course.description
      };
      setCourseWishlist(prev => [...prev, wishlistItem]);
      }
      
      showNotification(result.message || 'Course added to wishlist!', 'success');
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      showNotification(error.message || 'Failed to add to wishlist', 'error');
    }
  };

  const removeFromCourseWishlist = async (courseId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-course-library/wishlist/remove`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          course_id: courseId,
          user_id: DEMO_USER_ID
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove from wishlist');
      }
      
      const result = await response.json();
      
      // Update local state
    setCourseWishlist(prev => prev.filter(c => c.id !== courseId));
      
      showNotification(result.message || 'Removed from wishlist!', 'success');
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showNotification(error.message || 'Failed to remove from wishlist', 'error');
    }
  };
  // Talk functionality functions
  const loadRooms = async () => {
    // Mock data for rooms - in real app, this would come from API
    const mockRooms = [
      {
        id: 'room1',
        title: 'Bac English Practice',
        language: 'English',
        type: 'both',
        level: 'intermediate',
        category: 'bac',
        tags: ['#Bac', '#Grammar', '#ExamPrep'],
        creator: { name: 'Miss Rania', country: '🇩🇿 Algeria', avatar: 'MR' },
        users: 6,
        maxUsers: 10,
        isPrivate: false,
        description: 'Practice English for Bac exam with grammar focus'
      },
      {
        id: 'room2',
        title: 'IELTS Speaking Club',
        language: 'English',
        type: 'voice',
        level: 'advanced',
        category: 'ielts',
        tags: ['#IELTS', '#Speaking', '#Advanced'],
        creator: { name: 'Sarah', country: '🇺🇸 USA', avatar: 'S' },
        users: 4,
        maxUsers: 8,
        isPrivate: false,
        description: 'Advanced IELTS speaking practice sessions'
      },
      {
        id: 'room3',
        title: 'Fun French Chat',
        language: 'French',
        type: 'both',
        level: 'beginner',
        category: 'fun',
        tags: ['#French', '#Fun', '#Beginner'],
        creator: { name: 'Pierre', country: '🇫🇷 France', avatar: 'P' },
        users: 8,
        maxUsers: 12,
        isPrivate: false,
        description: 'Casual French conversation for beginners'
      },
      {
        id: 'room4',
        title: 'Business English',
        language: 'English',
        type: 'voice',
        level: 'intermediate',
        category: 'business',
        tags: ['#Business', '#English', '#Professional'],
        creator: { name: 'Alex', country: '🇬🇧 UK', avatar: 'A' },
        users: 3,
        maxUsers: 6,
        isPrivate: true,
        description: 'Professional business English practice'
      }
    ];
    
    setRooms(mockRooms);
    setFilteredRooms(mockRooms);
  };

  // Initialize Talk data when section is active
  useEffect(() => {
    if (activeSection === 'talk') {
      loadRooms();
      loadMyActivity();
    }
  }, [activeSection]);

  // Filter rooms when search or filters change
  useEffect(() => {
    filterRooms();
  }, [roomSearch, languageFilter, typeFilter, levelFilterTalk, categoryFilterTalk, rooms]);

  const loadMyActivity = async () => {
    // Mock data for my activity
    const mockMyRooms = [
      {
        id: 'myroom1',
        title: 'My Grammar Practice',
        language: 'English',
        type: 'both',
        level: 'intermediate',
        category: 'grammar',
        users: 3,
        maxUsers: 8,
        isActive: true
      },
      {
        id: 'myroom2',
        title: 'IELTS Prep Group',
        language: 'English',
        type: 'voice',
        level: 'advanced',
        category: 'ielts',
        users: 5,
        maxUsers: 10,
        isActive: false
      }
    ];
    
    const mockRecentRooms = [
      {
        id: 'recent1',
        title: 'Bac English Practice',
        joinedAt: '2024-01-15',
        duration: '2h 30m'
      },
      {
        id: 'recent2',
        title: 'Fun French Chat',
        joinedAt: '2024-01-14',
        duration: '1h 45m'
      }
    ];
    
    const mockMyPosts = [
      {
        id: 'post1',
        content: 'Great practice session today!',
        room: 'Bac English Practice',
        time: '2 hours ago'
      },
      {
        id: 'post2',
        content: 'Anyone up for grammar practice?',
        room: 'Grammar Practice',
        time: '1 day ago'
      }
    ];
    
    const mockReportedUsers = [
      {
        id: 'user1',
        name: 'SpamUser123',
        reason: 'Inappropriate behavior',
        reportedAt: '2024-01-10'
      }
    ];
    
    setMyRooms(mockMyRooms);
    setRecentRooms(mockRecentRooms);
    setMyPosts(mockMyPosts);
    setReportedUsers(mockReportedUsers);
  };

  const filterRooms = () => {
    if (!rooms.length) return;
    
    const searchTermLower = roomSearch.toLowerCase();
    const filtered = rooms.filter(room => {
      const matchesSearch = room.title.toLowerCase().includes(searchTermLower) || 
                          room.description.toLowerCase().includes(searchTermLower) ||
                          room.tags.some(tag => tag.toLowerCase().includes(searchTermLower));
      const matchesLanguage = !languageFilter || room.language.toLowerCase() === languageFilter.toLowerCase();
      const matchesType = !typeFilter || room.type === typeFilter;
      const matchesLevel = !levelFilterTalk || room.level === levelFilterTalk;
      const matchesCategory = !categoryFilterTalk || room.category === categoryFilterTalk;

      return matchesSearch && matchesLanguage && matchesType && matchesLevel && matchesCategory;
    });

    setFilteredRooms(filtered);
  };

  const joinRoom = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;
    
    setCurrentRoom(room);
    setRoomModal({ show: true, room: room });
    
    // Mock participants and messages
    const mockParticipants = [
      { id: 'p1', name: 'John Doe', country: '🇩🇿 Algeria', avatar: 'JD', isMuted: false, isOwner: false },
      { id: 'p2', name: 'Miss Rania', country: '🇩🇿 Algeria', avatar: 'MR', isMuted: true, isOwner: true },
      { id: 'p3', name: 'Ahmed', country: '🇪🇬 Egypt', avatar: 'A', isMuted: false, isOwner: false },
      { id: 'p4', name: 'Sarah', country: '🇺🇸 USA', avatar: 'S', isMuted: false, isOwner: false },
      { id: 'p5', name: 'Maria', country: '🇪🇸 Spain', avatar: 'M', isMuted: true, isOwner: false },
      { id: 'p6', name: 'David', country: '🇬🇧 UK', avatar: 'D', isMuted: false, isOwner: false }
    ];
    
    const mockMessages = [
      { id: 'm1', author: 'Miss Rania', content: 'Welcome everyone! Let\'s start with grammar practice.', time: '14:30', isOwn: false },
      { id: 'm2', author: 'John Doe', content: 'Hi everyone! Ready to practice.', time: '14:31', isOwn: true },
      { id: 'm3', author: 'Ahmed', content: 'Hello! I\'m here to improve my English.', time: '14:32', isOwn: false },
      { id: 'm4', author: 'Sarah', content: 'Great! Let\'s begin with some vocabulary.', time: '14:33', isOwn: false }
    ];
    
    setParticipants(mockParticipants);
    setChatMessages(mockMessages);
  };

  const leaveRoom = () => {
    if (window.confirm('Are you sure you want to leave this room?')) {
      setRoomModal({ show: false, room: null });
      setCurrentRoom(null);
      setParticipants([]);
      setChatMessages([]);
    }
  };



  const toggleMic = () => {
    setIsMicOn(!isMicOn);
  };

  const toggleSpeaker = () => {
    setIsSpeakerOn(!isSpeakerOn);
  };

  const shareScreen = () => {
    showNotification('Screen sharing feature coming soon!', 'info');
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const newMessageObj = {
      id: `msg${Date.now()}`,
      author: currentUser.name,
      content: newMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isOwn: true
    };
    
    setChatMessages(prev => [...prev, newMessageObj]);
    setNewMessage('');
  };

  const handleMessageKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  const openReportModal = (target) => {
    setReportModal({ show: true, target: target });
  };

  const closeReportModal = () => {
    setReportModal({ show: false, target: null });
  };

  const reportRoom = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    openReportModal({ type: 'room', id: roomId, name: room?.title });
  };

  const reportUser = (userId) => {
    const user = participants.find(p => p.id === userId);
    openReportModal({ type: 'user', id: userId, name: user?.name });
  };

  const submitReport = (reportData) => {
    showNotification('Report submitted successfully', 'success');
    closeReportModal();
  };

  const createRoom = (roomData) => {
    // Validate required fields
    if (!roomData.title || !roomData.language || !roomData.type || !roomData.category) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    const newRoom = {
      id: `room${Date.now()}`,
      ...roomData,
      creator: { name: currentUser.name, country: currentUser.country, avatar: currentUser.avatar },
      users: 1,
      isActive: true,
      tags: roomData.tags || []
    };
    
    setRooms(prev => [newRoom, ...prev]);
    setFilteredRooms(prev => [newRoom, ...prev]);
    setMyRooms(prev => [newRoom, ...prev]);
    
    // Reset form
    setCreateRoomForm({
      title: '',
      language: '',
      type: '',
      category: '',
      level: 'any',
      maxUsers: 10,
      tags: '',
      description: '',
      isPrivate: false
    });
    
    showNotification('Room created successfully!', 'success');
    setActiveTalkTab('communication-hub');
  };

  const handleCreateRoomSubmit = (e) => {
    e.preventDefault();
    createRoom(createRoomForm);
  };

  const handleProfileSettingsSubmit = (e) => {
    e.preventDefault();
    // Update current user profile
    currentUser.name = profileSettings.username;
    currentUser.country = profileSettings.country;
    showNotification('Profile settings saved successfully!', 'success');
  };

  const handleReportSubmit = (e) => {
    e.preventDefault();
    
    if (!reportForm.reason) {
      showNotification('Please select a reason for the report', 'error');
      return;
    }
    
    // Here you would typically send the report to the server
    console.log('Report submitted:', reportForm);
    
    // Close modal and show success message
    closeReportModal();
    showNotification('Report submitted successfully. Thank you for helping keep our community safe.', 'success');
  };

  const deleteRoom = (roomId) => {
    setMyRooms(prev => prev.filter(room => room.id !== roomId));
    showNotification('Room deleted successfully', 'success');
  };

  const editRoom = (roomId) => {
    showNotification('Edit room feature coming soon!', 'info');
  };

  // Educational Levels Vocabulary Functions (full-page view, like original)
  const openEducationalLevels = () => {
    // Clear any scoped year-level limits (My Courses) and any leftover scoped data,
    // then open the dedicated Educational Levels section with the classic High/Middle/Primary layout.
    setAllowedEducationalYearLevelIds(null);
    setYearLevelsFromApi([]);
    setEducationalContentScoped([]);
    setUnits([]);
    setSelectedScopedYearLevelId(null);
    setSelectedUnitId(null);
    setActiveSection('educational-levels');
    // Use language-aware schoolLevels so e.g. German opens on High School + Second Year
    const firstLevel = schoolLevels[0];
    const firstYearName = firstLevel?.years?.[0];
    const schoolId = firstLevel?.id || 'high-school';
    setActiveSchoolLevel(schoolId);
    setActiveYearLevel(`${schoolId}-1`);
    if (schoolId && firstYearName) loadEducationalContent(schoolId, firstYearName);
  };

  const closeEducationalLevels = () => {
    setAllowedEducationalYearLevelIds(null);
    setYearLevelsFromApi([]);
    setEducationalContentScoped([]);
    setUnits([]);
    setSelectedUnitId(null);
    setActiveSection('explore-courses');
  };

  // International Exam Levels Functions (language-aware: English vs French exam types)
  const openExamLevels = () => {
    setExploreExamSequentialNotice('');
    setShowExamLevels(true);
    setExamArticles([]);
    setExamCatalogStatus('loading');
    setActiveSection('exam-levels');
    const first = examLevels[0];
    const firstId = first?.id || 'ielts';
    const firstYear = first?.years?.[0];
    setActiveExamLevel(firstId);
    setActiveExamYearLevel(`${firstId}-1`);
    // Entering the section is loaded by the exam-levels effect below.
    if (activeSection === 'exam-levels') loadExamContent(firstId, firstYear);
  };

  const closeExamLevels = () => {
    setExploreExamSequentialNotice('');
    setShowExamLevels(false);
    setActiveSection('explore-courses');
    const first = examLevels[0];
    const firstId = first?.id || 'ielts';
    setActiveExamLevel(firstId);
    setActiveExamYearLevel(`${firstId}-1`);
    setExamArticles([]);
  };

  const handleExamLevelChange = (examLevel) => {
    setActiveExamLevel(examLevel);
    setActiveExamYearLevel(`${examLevel}-1`);
    loadExamContent(examLevel, examLevels.find(level => level.id === examLevel)?.years[0]);
  };

  const handleExamYearLevelChange = (yearLevel, yearName) => {
    setActiveExamYearLevel(yearLevel);
    loadExamContent(activeExamLevel, yearName);
  };
  const loadExamContent = async (examLevel, yearName, { keepVisibleList = false } = {}) => {
    const requestKey = beginExamLoad(examLoadKeyRef.current);
    examLoadKeyRef.current = requestKey;
    setExamCatalogStatus('loading');
    if (!keepVisibleList) setExamArticles([]);
    setExploreExamSequentialNotice('');
    try {
      const examMapEnglish = {
        'ielts': 'IELTS',
        'toefl': 'TOEFL',
        'cambridge': 'CAMBRIDGE',
        'other-exams': 'OTHER_EXAMS'
      };
      const examMapFrench = {
        'delf-dalf': 'DELF_DALF',
        'tcf': 'TCF',
        'tef': 'TEF',
        'dcl': 'DCL',
        'other-exams': 'OTHER_EXAMS'
      };
      const examMapSpanish = {
        'dele': 'DELE',
        'siele': 'SIELE',
        'celu': 'CELU',
        'other-exams': 'OTHER_EXAMS'
      };
      const examMapGerman = {
        'goethe': 'GOETHE_ZERTIFIKAT',
        'testdaf': 'TESTDAF',
        'telc-german': 'TELC_GERMAN',
        'other-exams': 'OTHER_EXAMS'
      };
      const examMapItalian = {
        'cils': 'CILS',
        'celi': 'CELI',
        'plida': 'PLIDA',
        'other-exams': 'OTHER_EXAMS'
      };
      const examMap = isFrenchExamLanguage ? examMapFrench : (isSpanishExamLanguage ? examMapSpanish : (isGermanExamLanguage ? examMapGerman : (isItalianExamLanguage ? examMapItalian : examMapEnglish)));
      const dbExamType = examMap[examLevel] || (isFrenchExamLanguage || isSpanishExamLanguage || isGermanExamLanguage || isItalianExamLanguage ? examLevel.replace(/-/g, '_').toUpperCase() : examLevel.toUpperCase());

      const headers = {};
      const token = getAuthToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const params = new URLSearchParams({ examType: dbExamType });
      if (yearName) params.set('category', yearName);
      if (selectedLearningLanguage) params.set('language', selectedLearningLanguage);
      const response = await fetchExamCatalog(`${API_BASE_URL}/api/explore/international-articles?${params.toString()}`, { headers });
      if (!shouldApplyExamLoad(requestKey, examLoadKeyRef.current)) return;
      if (!response.ok) {
        reportLoaderFailure({ feature: 'exam_vocab', response });
        setExamCatalogStatus('error');
        showNotification(t('user.examVocabulary.loadError'), 'error');
        return;
      }
      const data = await response.json();
      if (!shouldApplyExamLoad(requestKey, examLoadKeyRef.current)) return;
      const result = catalogFromResponse({ ok: true, body: data });
      setExamContentEntitled(result.entitled);
      setExamArticles(result.articles);
      setExamCatalogStatus(result.status);
    } catch (error) {
      if (!shouldApplyExamLoad(requestKey, examLoadKeyRef.current)) return;
      console.error('❌ Error loading exam content:', error);
      reportLoaderFailure({ feature: 'exam_vocab', error, endpoint: '/api/explore/international-articles' });
      setExamCatalogStatus('error');
      showNotification(t('user.examVocabulary.loadError'), 'error');
    }
  };

  useEffect(() => {
    if (activeSection !== 'exam-levels') return;
    const selection = yearNameForExamTab(examLevels, activeExamLevel, activeExamYearLevel);
    if (selection.examLevelId !== activeExamLevel) setActiveExamLevel(selection.examLevelId);
    if (selection.yearLevelKey !== activeExamYearLevel) setActiveExamYearLevel(selection.yearLevelKey);
    loadExamContent(selection.examLevelId, selection.yearName);
    return () => {
      examLoadKeyRef.current += 1;
    };
    // Reload when this screen is opened or the learning language changes.
    // Tab clicks call loadExamContent directly and must not be duplicated here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, selectedLearningLanguage]);

  const handleSchoolLevelChange = (schoolLevel) => {
    setActiveSchoolLevel(schoolLevel);
    const firstYear = schoolLevels.find(level => level.id === schoolLevel)?.years[0];
    setActiveYearLevel(`${schoolLevel}-1`);
    loadEducationalContent(schoolLevel, firstYear);
  };

  const handleYearLevelChange = (yearLevel, yearName) => {
    setActiveYearLevel(yearLevel);
    loadEducationalContent(activeSchoolLevel, yearName);
  };

  // Explore: one request per tab. Backend returns { entitled, articles }. Optional unitId filters by unit.
  const loadEducationalContent = async (schoolLevel, yearName, unitId = null, unitDisplayTitleForLang = null) => {
    if (!schoolLevel || !yearName) {
      console.log('[Educational] ⏭️ Skipping fetch: schoolLevel or yearName not set yet');
      return;
    }
    const requestKey = `${schoolLevel}-${yearName}-${unitId ?? 'all'}`;
    educationalLoadKeyRef.current = requestKey;
    educationalReloadParamsRef.current = {
      schoolLevel,
      yearName,
      unitId,
      unitDisplayTitle: unitDisplayTitleForLang
    };
    setExploreExamSequentialNotice('');

    const schoolMap = {
      'high-school': 'High School',
      'middle-school': 'Middle School',
      'primary-school': 'Primary School'
    };
    const yearMap = {
      'First Year': 'First Year',
      'Second Year': 'Second Year',
      'Third Year': 'Third Year',
      'Fourth Year': 'Fourth Year'
    };
    const dbSchool = schoolMap[schoolLevel] || schoolLevel;
    const dbYear = yearMap[yearName] || yearName;

    console.log('[Educational] loadEducationalContent: schoolLevel=', schoolLevel, 'yearName=', yearName, 'unitId=', unitId, 'dbSchool=', dbSchool, 'dbYear=', dbYear, 'language=', selectedLearningLanguage);

    try {
      // When no unit selected: fetch units first; if we have units, show unit cards and don't fetch articles
      if (unitId == null) {
        setUnitsLoading(true);
        const unitsUrl = `${API_BASE_URL}/api/explore/units?school=${encodeURIComponent(dbSchool)}&year=${encodeURIComponent(dbYear)}&language=${encodeURIComponent(selectedLearningLanguage || 'English')}`;
        console.log('[Educational] Fetching units:', unitsUrl);
        const unitsRes = await fetch(unitsUrl);
        if (!unitsRes.ok) reportLoaderFailure({ feature: 'educational_units', response: unitsRes });
        const unitsData = unitsRes.ok ? await unitsRes.json() : { units: [] };
        const unitList = unitsData.units || [];
        console.log('[Educational] Units response: unitsCount=', unitList.length, 'units=', unitList.map((u) => u.title));
        if (educationalLoadKeyRef.current !== requestKey) return;
        setUnits(unitList);
        setSelectedUnitId(null);
        setUnitsLoading(false);
        if (unitList.length > 0) {
          setArticles([]);
          setEducationalContent([]);
          setEducationalContentFromSubscription(false);
          return;
        }
      }

      let url = `${API_BASE_URL}/api/explore/articles?school=${encodeURIComponent(dbSchool)}&year=${encodeURIComponent(dbYear)}&type=vocabulary&language=${encodeURIComponent(selectedLearningLanguage || 'English')}`;
      if (unitId != null && unitId !== '') url += `&unit_id=${encodeURIComponent(unitId)}`;
      // Only send unit_title for non-English (German, French, Italian, Spanish) so backend can match language-specific unit names; English uses DB title only
      if (unitId != null && unitId !== '' && unitDisplayTitleForLang) url += `&unit_title=${encodeURIComponent(unitDisplayTitleForLang)}`;
      const headers = {};
      const token = getAuthToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      console.log('[Educational] Fetching articles:', url);
      const res = await fetch(url, { headers });
      if (!res.ok) reportLoaderFailure({ feature: 'educational_articles', response: res });
      const data = res.ok ? await res.json() : { entitled: false, articles: [] };
      const { entitled = false, articles: rawArticles = [] } = data;

      const rawCount = (rawArticles || []).length;
      console.log('[Educational] Articles API response: entitled=', entitled, 'rawCount=', rawCount, 'titles=', (rawArticles || []).map((a) => a.title), 'years=', (rawArticles || []).map((a) => a.year), 'languages=', (rawArticles || []).map((a) => a.language));

      if (educationalLoadKeyRef.current !== requestKey) return;

      const norm = (y) => (y ? String(y).replace(/\s*\(BAC\)\s*/gi, '').trim().toLowerCase() : '');
      const filtered = (rawArticles || []).filter(
        (a) =>
          (a.school === dbSchool || !a.school) &&
          (!a.year || norm(a.year) === norm(dbYear)) &&
          (a.type === 'vocabulary' || !a.type)
      );
      console.log('[Educational] After filter: filteredCount=', filtered.length, 'dbSchool=', dbSchool, 'dbYear=', dbYear);
      setEducationalContentFromSubscription(entitled);
      if (filtered.length > 0) {
        setArticles(filtered);
        setEducationalContent([]);
      } else {
        setArticles([]);
        setEducationalContent([]);
      }
    } catch (error) {
      if (educationalLoadKeyRef.current !== requestKey) return;
      setUnitsLoading(false);
      setUnits([]);
      setSelectedUnitId(null);
      console.error('❌ Error loading educational content:', error);
      reportLoaderFailure({ feature: 'educational_content', error, endpoint: unitId == null ? '/api/explore/units' : '/api/explore/articles' });
      showNotification('Failed to load content. Please try again.', 'error');
      setArticles([]);
      setEducationalContent([]);
      setEducationalContentFromSubscription(false);
    }
  };

  // ESP Vocabulary Functions
  const openESPVocabulary = () => {
    setShowESPVocabulary(true);
    setActiveSection('esp-vocabulary');
    // Reset ESP content when opening the section
    setEspContent([]);
    setActiveFaculty('');
    setActiveSpecialty('');
    setActiveAcademicLevel('');
  };

  const closeESPVocabulary = () => {
    setShowESPVocabulary(false);
    setActiveSection('explore-courses');
  };

  const handleFacultyChange = (facultyId) => {
    setActiveFaculty(facultyId);
    setActiveSpecialty('');
    setActiveAcademicLevel('');
    setEspContent([]);
  };

  const handleSpecialtyChange = (specialty) => {
    setActiveSpecialty(specialty);
    setActiveAcademicLevel('');
    setEspContent([]);
  };

  const handleAcademicLevelChange = (academicLevel) => {
    setActiveAcademicLevel(academicLevel);
    loadESPContent(activeFaculty, activeSpecialty, academicLevel);
  };

  const loadESPContent = async (facultyId, specialty, academicLevel) => {
    try {
      // Fetch real articles from the ESP API
      const response = await fetch(`${API_BASE_URL}/api/esp-articles?specialty=${encodeURIComponent(specialty)}&academicLevel=${encodeURIComponent(academicLevel)}`);
      
      if (!response.ok) {
        reportLoaderFailure({ feature: 'esp_articles', response });
        throw markHandled(new Error('Failed to fetch ESP articles'));
      }
      
      const articles = await response.json();
      
      // Transform the articles to match the expected format
      const transformedContent = articles.map(article => {
        // Determine content type based on content analysis
        const content = article.content || '';
        const lowerContent = content.toLowerCase();
        
        let contentType = 'Article';
        let difficulty = 'Intermediate';
        
        if (lowerContent.includes('vocabulary') || lowerContent.includes('terms') || lowerContent.includes('definitions')) {
          contentType = 'Vocabulary';
          difficulty = 'Intermediate';
        } else if (lowerContent.includes('reading') || lowerContent.includes('comprehension') || lowerContent.includes('passage')) {
          contentType = 'Reading';
          difficulty = 'Intermediate';
        } else if (lowerContent.includes('writing') || lowerContent.includes('essay') || lowerContent.includes('composition')) {
          contentType = 'Writing';
          difficulty = 'Advanced';
        } else if (lowerContent.includes('grammar') || lowerContent.includes('syntax') || lowerContent.includes('structure')) {
          contentType = 'Grammar';
          difficulty = 'Intermediate';
        }
        
        return {
          id: article.id,
          title: article.title,
          description: content ? (content.replace(/<[^>]*>/g, '').substring(0, 100) + (content.replace(/<[^>]*>/g, '').length > 100 ? '...' : '')) : 'No content available',
          type: contentType.toLowerCase(),
          difficulty: difficulty,
          duration: '25 min', // Default duration since it's not in the database
          content: content,
          language: article.language,
          created_at: article.created_at,
          specialty: article.specialty,
          faculty: article.faculty,
          academic_year: article.academic_year
        };
      });
      
      setEspContent(transformedContent);
    } catch (error) {
      console.error('Error loading ESP content:', error);
      reportLoaderFailure({ feature: 'esp_articles', error, endpoint: '/api/esp-articles' });
      // Set empty content instead of mock data
      setEspContent([]);
    }
  };



  // Interactive Article Features (from userart.js)
  const showExamMasteryLockModal = (noticeTarget) => {
    const msg =
      noticeTarget === 'educational'
        ? t('user.educationalSequential.masteryLockToast')
        : t('user.readingExamSequential.masteryLockToast');
    if (noticeTarget === 'reading') setReadingExamLockNotice(msg);
    else setExploreExamSequentialNotice(msg);
    setExamMasteryLockModalOpen(true);
  };

  const openArticleModal = (article) => {
    articleClosingRef.current = false;
    articleGestureRef.current = { x: 0, y: 0, moved: false, downOnOverlay: false };
    if (activeSection === 'exam-levels' && article?.exam_sequential_access === 'mastery_locked') {
      showExamMasteryLockModal('explore');
      return;
    }
    if (activeSection === 'educational-levels' && article?.educational_sequential_access === 'mastery_locked') {
      showExamMasteryLockModal('educational');
      return;
    }
    setSelectedArticle(article);
    setShowArticleModal(true);
    if (article?.id) {
      const isExam = !!article.exam_type;
      readingSessionStartedAtRef.current = Date.now();
      readingActiveSecondsRef.current = 0;
      readingVisibilityStartRef.current = document.visibilityState === 'visible' ? Date.now() : null;
      const handler = () => {
        if (document.visibilityState === 'visible') {
          readingVisibilityStartRef.current = Date.now();
        } else {
          if (readingVisibilityStartRef.current) {
            readingActiveSecondsRef.current += (Date.now() - readingVisibilityStartRef.current) / 1000;
            readingVisibilityStartRef.current = null;
          }
        }
      };
      readingVisibilityHandlerRef.current = handler;
      document.addEventListener('visibilitychange', handler);
      fetch(`${VOCAB_ANALYTICS_API}/start-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: String(currentUser.id), articleId: article.id, isExamArticle: isExam })
      })
        .then(async (r) => {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            console.warn('📖 start-session failed:', r.status, err?.error || err?.hint || err);
            return null;
          }
          return r.json();
        })
        .then((d) => d && console.log('📖 Reading session started:', d.sessionId, isExam ? '(exam)' : ''))
        .catch((e) => console.warn('📖 start-session error:', e));
    } else {
      readingSessionStartedAtRef.current = null;
    }
  };

  const getQuizSubmitGuard = () => {
    if (!quizSubmitGuardRef.current) quizSubmitGuardRef.current = createQuizSubmitGuard();
    return quizSubmitGuardRef.current;
  };

  const closeRetentionQuizModal = () => {
    getQuizSubmitGuard().release();
    const shouldRefreshPathRetention = activeSection === 'learning-path-retention' && !!retentionQuizResult;
    setShowRetentionQuizModal(false);
    setRetentionQuizArticle(null);
    setRetentionQuizData(null);
    setRetentionQuizSelected([]);
    setRetentionQuizResult(null);
    setRetentionQuizLoading(false);
    if (shouldRefreshPathRetention) {
      setRetentionRefreshTick((n) => n + 1);
    }
  };

  const presentRetentionQuiz = (payload) => {
    if (!payload?.id) return;
    setRetentionQuizArticle(payload);
    setRetentionQuizData(null);
    setRetentionQuizSelected([]);
    setRetentionQuizResult(null);
    setRetentionQuizLoading(true);
    quizOverlayReadyAtRef.current = Date.now() + QUIZ_OPEN_GUARD_MS;
    setShowRetentionQuizModal(true);
  };

  const setRetentionQuizOption = (questionIndex, optionIndex) => {
    setRetentionQuizSelected((prev) => {
      const next = [...(prev || [])];
      next[questionIndex] = optionIndex;
      return next;
    });
  };

  const submitRetentionQuiz = async () => {
    if (retentionQuizResult || retentionQuizSubmitting) return;
    if (!retentionQuizData?.questions?.length || !retentionQuizArticle?.id || !currentUser?.id) return;
    const selected = retentionQuizSelected || [];
    const allAnswered = retentionQuizData.questions.every((_, i) => selected[i] != null);
    if (!allAnswered) return;
    const guard = getQuizSubmitGuard();
    if (!guard.tryBegin()) return;
    const answers = retentionQuizData.questions.map((q, i) => {
      const optIndex = selected[i];
      const correct = q.options && q.options[optIndex] && q.options[optIndex].correct;
      return { word: q.word, correct: !!correct };
    });
    setRetentionQuizSubmitting(true);
    let keepLocked = false;
    try {
      const res = await fetch(`${VOCAB_ANALYTICS_API}/retention/${currentUser.id}/quiz/${retentionQuizArticle.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        reportLoaderFailure({ feature: 'quiz_submit', response: res });
        setRetentionQuizResult({ submitError: true });
        return;
      }
      keepLocked = true;
      setRetentionQuizResult(data);
      if (activeSection === 'exam-levels' && activeExamLevel) {
        const idx = Math.max(0, Number(String(activeExamYearLevel || '').split('-').pop()) - 1);
        const yearName = examLevels.find((l) => l.id === activeExamLevel)?.years?.[idx] ?? null;
        loadExamContent(activeExamLevel, yearName, { keepVisibleList: true });
      }
      if (res.ok && activeSection === 'educational-levels') {
        const p = educationalReloadParamsRef.current;
        if (p?.schoolLevel && p?.yearName) {
          loadEducationalContent(p.schoolLevel, p.yearName, p.unitId, p.unitDisplayTitle);
        } else if (selectedScopedYearLevelId) {
          loadEducationalContentByYearLevelId(selectedScopedYearLevelId);
        }
      }
      if (data?.status === 'completed') setTimeout(closeRetentionQuizModal, 2000);
    } catch (error) {
      console.warn('Retention quiz submit failed:', error);
      reportLoaderFailure({ feature: 'quiz_submit', error, endpoint: '/api/vocabulary-analytics/retention/:userId/quiz/:articleId' });
      setRetentionQuizResult({ submitError: true });
    } finally {
      if (!keepLocked) guard.release();
      setRetentionQuizSubmitting(false);
    }
  };

  const onArticlePointerDown = (e) => {
    const point = e.touches && e.touches[0] ? e.touches[0] : e;
    articleGestureRef.current = {
      x: point.clientX,
      y: point.clientY,
      moved: false,
      downOnOverlay: e.target === e.currentTarget
    };
  };

  const onArticlePointerMove = (e) => {
    const point = e.touches && e.touches[0] ? e.touches[0] : e;
    const gesture = articleGestureRef.current;
    if (Math.abs(point.clientX - gesture.x) > 10 || Math.abs(point.clientY - gesture.y) > 10) {
      gesture.moved = true;
    }
  };

  const onArticleOverlayClick = (e) => {
    if (e.target !== e.currentTarget) return;
    const gesture = articleGestureRef.current;
    if (gesture.moved || !gesture.downOnOverlay) return;
    closeArticleModal();
  };

  const endOpenReadingSession = async (article) => {
    const startedAt = readingSessionStartedAtRef.current;
    const bodyEl = articleModalBodyRef.current;
    const handler = readingVisibilityHandlerRef.current;
    if (handler) {
      document.removeEventListener('visibilitychange', handler);
      readingVisibilityHandlerRef.current = null;
    }
    if (readingVisibilityStartRef.current) {
      readingActiveSecondsRef.current += (Date.now() - readingVisibilityStartRef.current) / 1000;
      readingVisibilityStartRef.current = null;
    }
    let completionPercentage = 0;
    let isExam = false;
    let endSessionOk = false;
    if (article?.id && currentUser?.id && startedAt) {
      isExam = !!article.exam_type;
      const readingTimeSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
      if (bodyEl && bodyEl.scrollHeight > 0) {
        const scrollHeight = bodyEl.scrollHeight - bodyEl.clientHeight;
        completionPercentage =
          scrollHeight <= 0 ? 100 : Math.min(100, Math.round(((bodyEl.scrollTop + bodyEl.clientHeight) / bodyEl.scrollHeight) * 100));
      }
      const activeSeconds = Math.round(readingActiveSecondsRef.current);
      try {
        const res = await fetch(`${VOCAB_ANALYTICS_API}/end-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: String(currentUser.id),
            articleId: article.id,
            readingTime: readingTimeSeconds,
            completionPercentage,
            activeReadingSeconds: activeSeconds,
            isExamArticle: isExam
          })
        });
        endSessionOk = res.ok;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.completionPercentage != null) completionPercentage = data.completionPercentage;
          console.log('📖 Reading session ended:', completionPercentage + '%', isExam ? '(exam)' : '');
        } else {
          const errBody = await res.json().catch(() => ({}));
          console.warn('📖 end-session failed:', res.status, errBody?.hint || errBody?.error);
        }
      } catch (e) {
        console.warn('📖 end-session request error:', e);
      }
    }
    readingSessionStartedAtRef.current = null;
    readingActiveSecondsRef.current = 0;
    return { completionPercentage, endSessionOk, isExam };
  };

  const dismissArticleReader = async (intent) => {
    if (articleClosingRef.current) return;
    const article = selectedArticle;
    articleClosingRef.current = true;
    if (intent === 'start-quiz') setArticleQuizStarting(true);
    try {
      const session = await endOpenReadingSession(article);
      const isEducationalSequential =
        activeSection === 'educational-levels' &&
        !article?.exam_type &&
        (educationalContentFromSubscription || (allowedEducationalYearLevelIds && allowedEducationalYearLevelIds.length > 0));
      const decision = resolveReaderDismiss(intent, article, {
        endSessionOk: session.endSessionOk && !!currentUser?.id,
        completionPercentage: session.completionPercentage,
        isEducationalSequential
      });
      setShowArticleModal(false);
      setSelectedArticle(null);
      setFloatingBox(null);
      if (decision.launchQuiz) {
        presentRetentionQuiz(decision.launchQuiz);
      } else if (intent !== 'start-quiz' && article?.id && currentUser?.id && !article.exam_type) {
        console.warn('📖 Retention quiz not shown:', {
          completionPercentage: session.completionPercentage,
          endSessionOk: session.endSessionOk,
          reason: !session.endSessionOk
            ? 'end-session failed (check Network tab or server logs)'
            : 'educational preview: completion < 90%'
        });
      }
    } finally {
      articleClosingRef.current = false;
      setArticleQuizStarting(false);
    }
  };

  const closeArticleModal = () => dismissArticleReader('close');

  const startArticleVocabularyQuiz = () => dismissArticleReader('start-quiz');

  // Reading System Functions - ONLY for Reading Section
  const startModelReading = async () => {
    try {
      const articleText = readingCurrentArticle?.content || '';
      
      console.log('🎯 Getting Azure TTS audio data...');
      
      // Check if SpeechSDK is available
      if (!AzureSpeechService.isSpeechSDKAvailable()) {
        throw new Error('Microsoft Speech SDK not loaded. Please refresh the page and try again.');
      }

      // Initialize Azure service if not already done
      if (!azureSpeechService.current.isReady()) {
        // Use credentials from config file
        if (azureConfig.subscriptionKey === 'YOUR_AZURE_SUBSCRIPTION_KEY_HERE') {
          throw new Error('Azure credentials not configured. Please update src/config/azureConfig.js with your Azure Speech Service key and region.');
        }
        
        await azureSpeechService.current.initialize(azureConfig.subscriptionKey, azureConfig.region);
      }
      
      // Start Azure TTS with browser audio control
      const audioResult = await azureSpeechService.current.startModelReading(articleText);
      
      if (audioResult === 'browser-controlled') {
        // Azure TTS is playing through browser - use browser controls
        console.log('✅ Azure TTS started successfully with browser audio control');
        setIsModelReading(true);
        setShowRemark(true); // Show remark when button is clicked
        } else {
        console.log('⚠️ Unexpected result:', audioResult);
      }
      
    } catch (error) {
      console.error('❌ Error starting model reading:', error);
      alert(`Could not start model reading: ${error.message}`);
    }
  };

  const startReadingSession = async () => {
    try {
      console.log('🎯 Starting reading session...');
      
      // Check if SpeechSDK is available
      if (!AzureSpeechService.isSpeechSDKAvailable()) {
        throw new Error('Microsoft Speech SDK not loaded. Please refresh the page and try again.');
      }

      // Initialize Azure service if not already done
      if (!azureSpeechService.current.isReady()) {
        // Use credentials from config file
        if (azureConfig.subscriptionKey === 'YOUR_AZURE_SUBSCRIPTION_KEY_HERE') {
          throw new Error('Azure credentials not configured. Please update src/config/azureConfig.js with your Azure Speech Service key and region.');
        }
        
        await azureSpeechService.current.initialize(azureConfig.subscriptionKey, azureConfig.region);
      }
      
      // Clear previous recognition display
      const liveTextElement = document.getElementById('liveRecognitionText');
      if (liveTextElement) {
        liveTextElement.innerHTML = '<span class="recognition-placeholder">🎤 Ready to listen... Start speaking!</span>';
      }
      
      // Set up real-time recognition callbacks
      azureSpeechService.current.onRecognizing = (text) => {
        // Update live recognition display with highlighted words
        const liveTextElement = document.getElementById('liveRecognitionText');
        if (liveTextElement) {
          if (text) {
            // Split text into words and highlight each one
            const words = text.split(' ');
            const highlightedWords = words.map(word => 
              `<span class="highlighted-word">${word}</span>`
            ).join(' ');
            liveTextElement.innerHTML = highlightedWords;
          } else {
            liveTextElement.innerHTML = '<span class="recognition-placeholder">Listening...</span>';
          }
        }
      };
      
      azureSpeechService.current.onRecognized = (text) => {
        // Update final recognition display with highlighted words
        const liveTextElement = document.getElementById('liveRecognitionText');
        if (liveTextElement) {
          if (text) {
            // Split text into words and highlight each one
            const words = text.split(' ');
            const highlightedWords = words.map(word => 
              `<span class="highlighted-word">${word}</span>`
            ).join(' ');
            liveTextElement.innerHTML = highlightedWords;
          } else {
            liveTextElement.innerHTML = '<span class="recognition-placeholder">Recognition complete</span>';
          }
        }
      };
      
      // Start Azure STT with Pronunciation Assessment
      const result = await azureSpeechService.current.startReadingSession();
      
      if (result === 'started') {
        console.log('✅ Reading session started successfully');
        setIsReadingActive(true);
        
        // Show success message
        alert('🎤 Reading session started! Speak clearly into your microphone to practice reading the article.');
      } else {
        console.log('⚠️ Unexpected result:', result);
        throw new Error('Failed to start reading session');
      }
      
    } catch (error) {
      console.error('❌ Error starting reading session:', error);
      setIsReadingActive(false);
      alert(`Could not start reading session: ${error.message}`);
    }
  };

  const stopReadingSession = async () => {
    try {
      console.log('🛑 Stopping reading session...');
      
      // Stop Azure STT session
      if (azureSpeechService.current && azureSpeechService.current.isReady()) {
        const result = await azureSpeechService.current.stopReadingSession();
        console.log('✅ Reading session stopped:', result);
        
        // Get recognition results for analysis
        const recognitionResults = azureSpeechService.current.getLastRecognitionResults();
        console.log('📊 Recognition results:', recognitionResults);
        
        // TODO: Show analysis overlay with pronunciation results
        // This will be implemented in Phase 3
        
        } else {
        console.log('⚠️ Azure service not ready');
      }
      
      setIsReadingActive(false);
      console.log('✅ Reading session stopped successfully');
      
    } catch (error) {
      console.error('❌ Error stopping reading session:', error);
      setIsReadingActive(false);
    }
  };

  const stopModelReading = async () => {
    try {
      console.log('🛑 Stop button clicked!');
      
      if (azureSpeechService.current && azureSpeechService.current.isReady()) {
        console.log('🎯 Azure service ready, stopping audio...');
        azureSpeechService.current.pauseBrowserAudio();
        console.log('✅ Audio stop command sent');
        } else {
        console.log('⚠️ Azure service not ready');
      }
      
      setIsModelReading(false);
      setShowRemark(false); // Hide remark when stopping
      console.log('✅ Model reading stopped');
      
    } catch (error) {
      console.error('❌ Error stopping model reading:', error);
    }
  };















  // Details Modal Functions
  const openDetailsModal = (article, isLocked = false) => {
    setDetailsArticle(article);
    setDetailsArticleIsLocked(!!isLocked);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsArticle(null);
    setDetailsArticleIsLocked(false);
  };

  // General Vocabulary Details Modal Functions
  const openGvDetailsModal = (article) => {
    // Format the General Vocabulary article to be compatible with ArticleDetailsModal
    const formattedArticle = {
      ...article,
      // Add any missing properties that ArticleDetailsModal expects
      school: 'General Vocabulary',
      year: article.cefr_level || 'All Levels',
      // Ensure the article has an id for analytics tracking
      id: article.id || `gv-${Date.now()}`
    };
    setDetailsArticle(formattedArticle);
    setShowDetailsModal(true);
  };

  // Dictation Module Functions
  const openDictationPaths = () => {
    setDictationPathsOpen(true);
    setActiveDictationPath(null);
    setDictationContent(null);
  };

  const closeDictationPaths = () => {
    setDictationPathsOpen(false);
    setActiveDictationPath(null);
    setDictationContent(null);
  };

  const openDictationPath = (pathId) => {
    setActiveDictationPath(pathId);
    setDictationContent(null);
  };

  const closeDictationPath = () => {
    setActiveDictationPath(null);
    setDictationContent(null);
  };

  const startDictationExercise = (article) => {
    if (typeof article === 'object' && article.id) {
      // Real article object from the database
      setCurrentDictationArticle(article);
      setDictationOverlayOpen(true);
      console.log('🎧 Starting dictation exercise with article:', article);
    } else {
      // Fallback for string-based content (legacy)
      setDictationContent({ title: 'Dictation Exercise', text_content: article });
      console.log('🎧 Starting dictation exercise with legacy content:', article);
    }
  };

  // Reading Module Functions
  const openReadingPaths = () => {
    setReadingPathsOpen(true);
    setActiveReadingPath(null);
  };
  const closeReadingPaths = () => {
    setReadingPathsOpen(false);
    setActiveReadingPath(null);
  };

  const LEARNING_LANGUAGE_LABELS = {
    english: 'English',
    french: 'French',
    spanish: 'Spanish',
    german: 'German',
    italian: 'Italian',
    arabic: 'Arabic'
  };
  const LEARNING_LANGUAGE_CODES = {
    english: 'en',
    french: 'fr',
    spanish: 'es',
    german: 'de',
    italian: 'it'
  };

  const applyCardLearningLanguage = (language) => {
    if (!language) return;
    const key = String(language).trim().toLowerCase();
    const label = LEARNING_LANGUAGE_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1));
    setSelectedLearningLanguage(label);
    try {
      localStorage.setItem('selected-learning-language', label);
    } catch {
      // ignore
    }
    if (LEARNING_LANGUAGE_CODES[key]) {
      setSelectedExploreLanguage(LEARNING_LANGUAGE_CODES[key]);
    }
  };

  const pathLanguageLabel = (language) => {
    if (!language) return '';
    const aliases = { en: 'english', fr: 'french', es: 'spanish', de: 'german', it: 'italian', ar: 'arabic' };
    const key = aliases[String(language).trim().toLowerCase()] || String(language).trim().toLowerCase();
    return t(`user.myLearningPaths.languageNames.${key}`, {
      defaultValue: LEARNING_LANGUAGE_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1))
    });
  };

  const openPaidLearningPath = (course) => {
    if (!course) return;
    applyCardLearningLanguage(course.language);
    if (course.section === 'educational-levels' && course.allowedYearLevelIds && course.allowedYearLevelIds.length > 0) {
      setAllowedEducationalYearLevelIds(course.allowedYearLevelIds);
      setYearLevelsFromApi([]);
      setEducationalContentScoped([]);
      setSelectedScopedYearLevelId(null);
    } else if (course.section === 'educational-levels') {
      setAllowedEducationalYearLevelIds(null);
    }
    if (course.id === 'dictation') openDictationPaths();
    else if (course.id === 'reading') openReadingPaths();
    else if (course.id === 'international-exam-vocabulary' || course.section === 'exam-levels') openExamLevels();
    else setActiveSection(course.section);
  };

  const openPathRetention = (path, title) => {
    if (!path?.id) return;
    applyCardLearningLanguage(path.language);
    setPathRetentionFilter({
      id: path.id,
      feature: path.feature,
      title: title || path.titleKey || path.title || '',
      language: path.language || null
    });
    setPathRetentionList([]);
    setActiveSection('learning-path-retention');
  };

  const openPathRetentionReview = (row) => {
    if (!row?.article_id || !currentUser?.id) return;
    setRetentionQuizArticle({
      id: row.article_id,
      title: row.article_title || 'Article',
      isExam: pathRetentionFilter?.feature === 'exam_vocab' || pathRetentionFilter?.id === 'international-exam-vocabulary'
    });
    setRetentionQuizData(null);
    setRetentionQuizSelected([]);
    setRetentionQuizResult(null);
    setRetentionQuizLoading(true);
    setShowRetentionQuizModal(true);
  };

  const closeReadingModal = () => {
    // Stop any ongoing audio
    if (azureSpeechService.current && azureSpeechService.current.isReady()) {
      azureSpeechService.current.stopAudio();
      azureSpeechService.current.cleanupAudio();
    }
    
    setReadingReaderOpen(false);
    // Clean up reading states
    setIsReadingActive(false);
    setIsModelReading(false);
    setShowRemark(false); // Hide remark when modal is closed
  };
  const getMockReadingArticlesForPath = (pathId) => {
    const common = (overrides = {}) => ({
      id: Math.floor(Math.random() * 1e9),
      language: 'English',
      path_type: 'general',
      estimated_read_time: 3,
      word_count: 120,
      difficulty: 'Easy',
      cover_image: null,
      tags: [],
      status: 'published',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...overrides,
    });
    if (pathId === 'educational-levels') {
      return [
        common({ title: 'School Morning', description: 'A short text about a school morning.', content: '<p>It is Monday morning. Students walk to school. The bell rings at eight o\'clock.</p>', path_type: 'levels', difficulty: 'Easy', estimated_read_time: 2 }),
        common({ title: 'Library Visit', description: 'Students visit the library to borrow books.', content: '<p>The class goes to the library. They choose books and read quietly.</p>', path_type: 'levels', difficulty: 'Easy', estimated_read_time: 3 }),
        common({ title: 'Science Class', description: 'An easy passage about a science experiment.', content: '<p>In science, we mix water and salt. We observe how the salt dissolves.</p>', path_type: 'levels', difficulty: 'Medium', estimated_read_time: 4 }),
      ];
    }
    if (pathId === 'international-exam') {
      return [
        common({ title: 'IELTS Passage: Urban Parks', description: 'An exam-style passage on public spaces.', content: '<p>Urban parks provide spaces for rest and recreation, improving city life quality.</p>', path_type: 'exam', difficulty: 'Medium', estimated_read_time: 5 }),
        common({ title: 'TOEFL Passage: Renewable Energy', description: 'A TOEFL-style paragraph about energy.', content: '<p>Renewable energy sources, such as wind and solar, are expanding worldwide.</p>', path_type: 'exam', difficulty: 'Medium', estimated_read_time: 5 }),
      ];
    }
    if (pathId === 'esp') {
      return [];
    }
    // general
    return [
      common({ title: 'Weekend Market', description: 'A daily-life passage about a weekend market.', content: '<p>On Saturday, families visit the market to buy fresh fruit and bread.</p>', path_type: 'general', difficulty: 'Easy', estimated_read_time: 3 }),
      common({ title: 'City Walk', description: 'A short text about walking in the city.', content: '<p>People walk along the river. Cafés are open and children play in the park.</p>', path_type: 'general', difficulty: 'Easy', estimated_read_time: 2 }),
    ];
  };

  const fetchReadingArticles = async (pathId) => {
    try {
      setReadingLoading(true);
      setReadingError('');
      setReadingExamLockNotice('');
      const params = new URLSearchParams();
      const apiBase = `${API_BASE_URL}/api/reading`;
      const mapDifficulty = (d) => {
        if (!d || d === 'all') return '';
        if (d === 'Intermediate') return 'Medium';
        if (d === 'Advanced') return 'Hard';
        return d; // 'Easy'
      };

      const pathType = pathId === 'educational-levels' ? 'levels' : pathId === 'international-exam' ? 'exam' : pathId === 'esp' ? 'esp' : 'general';
      params.set('path_type', pathType);
      if (selectedLearningLanguage) params.set('language', selectedLearningLanguage);

      if (pathType === 'levels') {
        const levelObj = schoolLevels.find(l => l.id === readingActiveSchoolLevel);
        const idxStr = (readingActiveYearLevel || '').split('-').pop();
        const idx = Math.max(1, parseInt(idxStr || '1', 10)) - 1;
        const schoolName = levelObj?.name || '';
        const yearName = levelObj?.years?.[idx] || '';
        if (schoolName) params.set('school', schoolName);
        if (yearName) params.set('year', yearName);
        const d = mapDifficulty(readingDifficulty);
        if (d) params.set('difficulty', d);
        if (readingSearch && readingSearch.trim()) params.set('search', readingSearch.trim());
      } else if (pathType === 'exam') {
        const typeMap = { ielts: 'IELTS', toefl: 'TOEFL', cambridge: 'Cambridge' };
        const examType = typeMap[readingActiveExam] || '';
        if (examType) params.set('exam_type', examType);
        if (readingActiveExamVariant) params.set('exam_section', readingActiveExamVariant);
        const d = mapDifficulty(readingDifficulty);
        if (d) params.set('exam_difficulty', d);
        if (readingSearch && readingSearch.trim()) params.set('search', readingSearch.trim());
      } else if (pathType === 'general') {
        if (readingGenre) params.set('genre', readingGenre);
        const d = mapDifficulty(readingDifficulty);
        if (d) params.set('difficulty', d);
        if (readingMinTime) {
          if (readingMinTime === '0-3') {
            params.set('max_read_time', '3');
          } else if (readingMinTime === '3-6' || readingMinTime === '4-6') {
            params.set('min_read_time', '3');
            params.set('max_read_time', '6');
          } else if (readingMinTime === '>6') {
            params.set('min_read_time', '6');
          }
        }
        if (readingSearch && readingSearch.trim()) params.set('search', readingSearch.trim());
      }

      // Try reading API first
      console.log('🔍 Fetching from reading API with params:', params.toString());
      const token = getAuthToken();
      const readHeaders = { Accept: 'application/json' };
      if (token) readHeaders.Authorization = `Bearer ${token}`;
      let response = await fetch(`${apiBase}/articles?${params.toString()}`, { headers: readHeaders });
      let data = [];
      
      if (response.ok) {
        data = await response.json();
        console.log('✅ Reading API response:', data);
        if (Array.isArray(data) && data.length > 0) {
          console.log('📊 First article structure:', data[0]);
          console.log('📊 First article has content:', !!data[0].content);
          console.log('📊 First article content length:', data[0].content ? data[0].content.length : 'N/A');
        }
      } else {
        console.log('❌ Reading API failed with status:', response.status);
        reportLoaderFailure({ feature: 'reading_articles', response });
        
        // Fallback to main articles endpoint
        const mainParams = new URLSearchParams();
        if (pathType === 'levels') {
          const levelObj = schoolLevels.find(l => l.id === readingActiveSchoolLevel);
          const idxStr = (readingActiveYearLevel || '').split('-').pop();
          const idx = Math.max(1, parseInt(idxStr || '1', 10)) - 1;
          const schoolName = levelObj?.name || '';
          const yearName = levelObj?.years?.[idx] || '';
          if (schoolName) mainParams.set('school', schoolName);
          if (yearName) mainParams.set('year', yearName);
          mainParams.set('type', 'reading');
        }
        
        try {
          console.log('🔍 Trying main articles API with params:', mainParams.toString());
          const mainResponse = await fetch(`${API_BASE_URL}/articles?${mainParams.toString()}`);
          if (mainResponse.ok) {
            data = await mainResponse.json();
            console.log('✅ Main articles API response:', data);
          } else {
            console.log('❌ Main articles API failed with status:', mainResponse.status);
          }
        } catch (fallbackErr) {
          console.log('❌ Main articles API error:', fallbackErr);
        }
      }
      
      const arr = Array.isArray(data) ? data : [];
      console.log('🎯 Final articles array length:', arr.length);
      if (arr.length > 0) {
        console.log('🎯 Final first article:', arr[0]);
        console.log('🎯 Final first article content exists:', !!arr[0].content);
      }
      setReadingArticles(arr);
      
    } catch (err) {
      console.error('Error fetching reading articles:', err);
      reportLoaderFailure({ feature: 'reading_articles', error: err, endpoint: '/api/reading/articles' });
      setReadingArticles([]);
      setReadingError('');
    } finally {
      setReadingLoading(false);
    }
  };

  const fetchDictationArticles = async (schoolLevel, year) => {
    try {
      setDictationLoading(true);
      setDictationError('');
      
      const yearNumber = year.split('-').pop();
      const params = new URLSearchParams({ school_level: schoolLevel, year: yearNumber });
      if (selectedLearningLanguage) params.set('language', selectedLearningLanguage);
      const token = getAuthToken();
      const response = await fetch(`/api/dictation/articles?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setDictationArticles(data.data || []);
          console.log('✅ Dictation articles fetched:', data.data);
        } else {
          setDictationError(data.message || 'Failed to fetch dictation articles');
          setDictationArticles([]);
        }
      } else {
        reportLoaderFailure({ feature: 'dictation_articles', response });
        setDictationError('Failed to fetch dictation articles');
        setDictationArticles([]);
      }
    } catch (error) {
      console.error('❌ Error fetching dictation articles:', error);
      reportLoaderFailure({ feature: 'dictation_articles', error, endpoint: '/api/dictation/articles' });
      setDictationError('Failed to fetch dictation articles. Please try again.');
      setDictationArticles([]);
    } finally {
      setDictationLoading(false);
    }
  };

  const handleDictationSchoolLevelChange = (schoolLevel) => {
    setActiveDictationSchoolLevel(schoolLevel);
    const firstYear = `${schoolLevel}-1`;
    setActiveDictationYear(firstYear);
    fetchDictationArticles(schoolLevel, firstYear);
  };

  const handleDictationYearChange = (yearLevel, yearName) => {
    setActiveDictationYear(yearLevel);
    fetchDictationArticles(activeDictationSchoolLevel, yearName);
  };

  const closeDictationOverlay = () => {
    setDictationOverlayOpen(false);
    setCurrentDictationArticle(null);
  };

  const openDictationDetailsOverlay = (article) => {
    setCurrentDictationDetailsArticle(article);
    setDictationDetailsOverlayOpen(true);
  };

  const closeDictationDetailsOverlay = () => {
    setDictationDetailsOverlayOpen(false);
    setCurrentDictationDetailsArticle(null);
  };

  const saveMistakesToVocabulary = (mistakes) => {
    // TODO: Implement saving mistakes to user's vocabulary
    console.log('Saving mistakes to vocabulary:', mistakes);
    // This would typically save to a user's personal vocabulary list
  };
  const openReadingPath = (pathId) => {
    // Open in-place path library (independent from other modules)
    setActiveReadingPath(pathId);
    setReadingArticles([]);
    setReadingError('');
    setReadingExamLockNotice('');
    fetchReadingArticles(pathId);
  };

  const openReadingFromList = async (article) => {
    if (activeReadingPath === 'international-exam' && article?.exam_sequential_access === 'mastery_locked') {
      showExamMasteryLockModal('reading');
      return;
    }

    let articleToOpen = article;
    if (activeReadingPath === 'international-exam' && article?.id && article?.exam_sequential_access !== 'mastery_locked') {
      const token = getAuthToken();
      const qs = new URLSearchParams();
      const typeMap = { ielts: 'IELTS', toefl: 'TOEFL', cambridge: 'Cambridge' };
      const examType = typeMap[readingActiveExam] || '';
      if (examType) qs.set('exam_type', examType);
      if (readingActiveExamVariant) qs.set('exam_section', readingActiveExamVariant);
      const mapDiff = (d) => {
        if (!d || d === 'all') return '';
        if (d === 'Intermediate') return 'Medium';
        if (d === 'Advanced') return 'Hard';
        return d;
      };
      const d = mapDiff(readingDifficulty);
      if (d) qs.set('exam_difficulty', d);
      if (selectedLearningLanguage) qs.set('language', selectedLearningLanguage);
      try {
        const res = await fetch(`${API_BASE_URL}/api/reading/articles/${article.id}?${qs}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (res.status === 403) {
          const body = await res.json().catch(() => ({}));
          if (body.code === 'EXAM_SEQUENTIAL_LOCK') {
            showExamMasteryLockModal('reading');
            fetchReadingArticles('international-exam');
            return;
          }
        }
        if (res.ok) {
          const full = await res.json();
          const { meta, ...rest } = full;
          articleToOpen = { ...rest, ...(meta || {}) };
        }
      } catch (e) {
        console.warn('Exam article verify fetch failed:', e);
      }
    }

    setReadingExamLockNotice('');
    setReadingCurrentArticle(articleToOpen);
    setReadingReaderOpen(true);

    // Apply word highlighting after modal opens
    setTimeout(() => {
      if (readingContentRef.current) {
        // For HTML content, we need to highlight words differently
        highlightWordsForHTML(readingContentRef.current);
        
        // Add a mutation observer to re-highlight if content changes
        const observer = new MutationObserver(() => {
          if (readingContentRef.current && !readingContentRef.current.querySelector('.clickable-word')) {
            highlightWordsForHTML(readingContentRef.current);
          }
        });
        
        observer.observe(readingContentRef.current, { 
          childList: true, 
          subtree: true 
        });
      }
    }, 100);
  };

  // Track word clicks for vocabulary analytics (throttled: one request per 400ms to avoid 429)
  const sendOneWordInteraction = async (word, article) => {
    if (!currentUser?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/vocabulary-analytics/word-interaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUser.id,
          articleId: article.id,
          wordText: word,
          interactionType: 'click',
          context: 'Reading session word click'
        })
      });
      if (response.ok) {
        console.log(`📝 Tracked word click: ${word} in article ${article.title}`);
      }
    } catch (error) {
      console.warn('Could not track word click:', error);
    }
  };

  const drainWordInteractionQueue = () => {
    const queue = wordInteractionQueueRef.current;
    if (queue.length === 0) {
      wordInteractionDrainRef.current = null;
      return;
    }
    wordInteractionDrainRef.current = {}; // mark drain in progress (no timeout id yet)
    const { word, article } = queue.shift();
    sendOneWordInteraction(word, article).finally(() => {
      wordInteractionDrainRef.current = setTimeout(drainWordInteractionQueue, 400);
    });
  };

  const trackWordClick = (word, article) => {
    if (!article) return;
    wordInteractionQueueRef.current.push({ word, article });
    if (!wordInteractionDrainRef.current) drainWordInteractionQueue();
  };
  const highlightWords = (container) => {
    highlightWordsForHTML(container, selectedArticle);
  };

  const highlightWordsForHTML = (container, article = readingCurrentArticle) => {
    if (!container || container.querySelector('.clickable-word')) return;
    
    // Use a more reliable approach - process text nodes directly
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          if (!node.textContent.trim().length) return NodeFilter.FILTER_REJECT;
          const parent = node.parentElement;
          if (parent && parent.closest('button, .article-vocab-quiz-action, [data-skip-word-highlight]')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      },
      false
    );
    
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }
    
    // Process each text node
    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      const words = text.split(/\b/); // Split on word boundaries to preserve punctuation
      
      if (words.length <= 1) return; // Skip if no words to process
      
      const fragment = document.createDocumentFragment();
      
      words.forEach(word => {
        if (word.trim() === '') {
          // Add empty text nodes for spacing
          fragment.appendChild(document.createTextNode(word));
          return;
        }
        
        // Check if this looks like a word (contains letters)
        if (/\w/.test(word)) {
          const wordSpan = document.createElement("span");
          wordSpan.textContent = word;
          wordSpan.style.cursor = "pointer";
          wordSpan.className = "clickable-word";

          // Highlight word on hover
          wordSpan.addEventListener("mouseenter", () => {
            wordSpan.style.backgroundColor = "#ffeb3b";
          });

          wordSpan.addEventListener("mouseleave", () => {
            wordSpan.style.backgroundColor = "";
          });

          // Show floating box on click
          wordSpan.addEventListener("click", async (e) => {
            e.stopPropagation();
            if (articleGestureRef.current.moved) return;

            const wordText = wordSpan.textContent;
            
            // Track word click for analytics
            const cleanWord = wordText.replace(/[.,!?;:"'()]/g, '').toLowerCase();
            await trackWordClick(cleanWord, article);
            
            // Remove any existing floating boxes
            const existingBoxes = document.querySelectorAll('.floating-box');
            existingBoxes.forEach(box => box.remove());
            
            showFloatingBox(wordText, e.clientX, e.clientY, wordSpan);
          });

          fragment.appendChild(wordSpan);
        } else {
          // Add non-word content as text node
          fragment.appendChild(document.createTextNode(word));
        }
      });
      
      // Replace the text node with the fragment
      textNode.parentNode.replaceChild(fragment, textNode);
    });
    
    // Store the container reference for re-highlighting if needed
    container.dataset.highlighted = 'true';
  };

  const openWordTranslation = async (wordText, event, article) => {
    event.stopPropagation();
    if (articleGestureRef.current.moved) return;
    const wordElement = event.currentTarget;
    const x = event.clientX;
    const y = event.clientY;
    const cleanWord = String(wordText || '').replace(/[.,!?;:"'()]/g, '').toLowerCase();
    if (!cleanWord || !wordElement) return;
    await trackWordClick(cleanWord, article);
    document.querySelectorAll('.floating-box').forEach((box) => box.remove());
    showFloatingBox(wordText, x, y, wordElement);
  };

  const renderTranslatableText = (text, article) => {
    const parts = String(text || '').split(/(\s+)/);
    return parts.map((part, index) => {
      if (!part || !/[A-Za-z0-9\u00C0-\u024F]/.test(part)) {
        return <span key={`title-part-${index}`}>{part}</span>;
      }
      return (
        <span
          key={`title-part-${index}`}
          className="clickable-word"
          onClick={(event) => openWordTranslation(part, event, article)}
        >
          {part}
        </span>
      );
    });
  };

  // Map learning language (e.g. "German", "French") to API codes for translate (source) and pronounce (accent/voice)
  const getLanguageCodesForWordFeature = () => {
    const lang = (selectedLearningLanguage || 'English').toString().trim().toLowerCase();
    const map = {
      german: { source: 'de', accent: 'de-DE', voice: 'de-DE-Wavenet-A' },
      french: { source: 'fr', accent: 'fr-FR', voice: 'fr-FR-Wavenet-A' },
      spanish: { source: 'es', accent: 'es-ES', voice: 'es-ES-Standard-A' },
      italian: { source: 'it', accent: 'it-IT', voice: 'it-IT-Wavenet-A' },
      english: { source: 'en', accent: 'en-US', voice: 'en-US-Standard-A' }
    };
    return map[lang] || map.english;
  };

  const showFloatingBox = async (word, x, y, wordElement) => {
    // Root backend URL (do NOT include "/api" here because /translate and /pronounce are root endpoints)
    // Empty = relative paths. Local: proxy. Production: Nginx.
    const ROOT_API_URL = getApiBaseUrl().replace(/\/+$/, '');
    const langCodes = getLanguageCodesForWordFeature();
    const floatingBox = document.createElement("div");
    floatingBox.classList.add("floating-box");
    floatingBox.style.position = "absolute";
    
    // Add click handler to close floating box when clicking outside
    const closeFloatingBox = (e) => {
      if (!floatingBox.contains(e.target)) {
        floatingBox.remove();
        document.removeEventListener('click', closeFloatingBox);
      }
    };
    
    // Delay adding the click handler to avoid immediate closure
    setTimeout(() => {
      document.addEventListener('click', closeFloatingBox);
    }, 100);

    // Get the word element's position
    const wordRect = wordElement.getBoundingClientRect();
    
    // Position the box above the word
    const boxWidth = 300;
    const boxHeight = 120;
    
    let left = wordRect.left + (wordRect.width / 2) - (boxWidth / 2);
    let top = wordRect.top - boxHeight - 10;
    
    // Adjust if the box would go off-screen
    if (left < 10) left = 10;
    if (left + boxWidth > window.innerWidth - 10) left = window.innerWidth - boxWidth - 10;
    if (top < 10) {
      top = wordRect.bottom + 10;
    }
    
    floatingBox.style.left = `${left}px`;
    floatingBox.style.top = `${top}px`;

    // Initial content
    floatingBox.innerHTML = `
      <p><strong>Word:</strong> ${word}</p>
      <p><strong>Translation:</strong> Loading...</p>
      <button class="know-it-btn">I know it</button>
      <button class="save-word-btn">Save Word</button>
      <button class="pronounce-btn">Pronounce</button>
    `;

    document.body.appendChild(floatingBox);
    setFloatingBox(floatingBox);

    try {
      // Fetch translation: source = article language (e.g. de for German), target = Arabic
      const targetLang = 'ar';
      const translateUrl = `${ROOT_API_URL}/translate?text=${encodeURIComponent(word)}&target=${targetLang}&source=${encodeURIComponent(langCodes.source)}`;
      const response = await fetch(translateUrl);
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Translation API error (${response.status}):`, errorText);
        throw new Error(`Failed to fetch translation: ${response.status}`);
      }

      const data = await response.json();
      const translatedText = data.translated || "Translation unavailable";

      floatingBox.querySelector("p:nth-child(2)").textContent = `Translation: ${translatedText}`;
    } catch (error) {
      console.error("Error fetching translation:", error.message);
      // Fallback translation for common words
      const fallbackTranslations = {
        'hello': 'مرحبا',
        'goodbye': 'وداعا',
        'thank you': 'شكرا لك',
        'please': 'من فضلك',
        'yes': 'نعم',
        'no': 'لا',
        'water': 'ماء',
        'food': 'طعام',
        'house': 'بيت',
        'car': 'سيارة',
        'book': 'كتاب',
        'school': 'مدرسة',
        'teacher': 'معلم',
        'student': 'طالب',
        'family': 'عائلة',
        'mother': 'أم',
        'father': 'أب',
        'sister': 'أخت',
        'brother': 'أخ',
        'friend': 'صديق',
        'time': 'وقت',
        'day': 'يوم',
        'night': 'ليل',
        'morning': 'صباح',
        'evening': 'مساء',
        'red': 'أحمر',
        'blue': 'أزرق',
        'green': 'أخضر',
        'yellow': 'أصفر',
        'black': 'أسود',
        'white': 'أبيض',
        'one': 'واحد',
        'two': 'اثنان',
        'three': 'ثلاثة',
        'four': 'أربعة',
        'five': 'خمسة'
      };
      
      const lowerWord = word.toLowerCase();
      const fallbackTranslation = fallbackTranslations[lowerWord];
      
      if (fallbackTranslation) {
        floatingBox.querySelector("p:nth-child(2)").textContent = `Translation: ${fallbackTranslation}`;
      } else {
        floatingBox.querySelector("p:nth-child(2)").textContent = `Translation: Translation service unavailable`;
      }
    }

    // Hide floating box when clicking anywhere else
    const hideBox = (e) => {
      if (!floatingBox.contains(e.target) && !wordElement.contains(e.target)) {
        floatingBox.remove();
        setFloatingBox(null);
        document.removeEventListener('click', hideBox);
        document.removeEventListener('mousemove', hideBoxOnMouseMove);
      }
    };

    // Hide floating box when mouse moves to another word
    const hideBoxOnMouseMove = (e) => {
      // Check if mouse is over a different clickable word
      const targetWord = e.target.closest('span');
      if (targetWord && targetWord.style.cursor === 'pointer' && targetWord !== wordElement) {
        floatingBox.remove();
        setFloatingBox(null);
        document.removeEventListener('click', hideBox);
        document.removeEventListener('mousemove', hideBoxOnMouseMove);
        return;
      }
      
      // Check if mouse is not over the floating box or the original word
      if (!floatingBox.contains(e.target) && !wordElement.contains(e.target)) {
        floatingBox.remove();
        setFloatingBox(null);
        document.removeEventListener('click', hideBox);
        document.removeEventListener('mousemove', hideBoxOnMouseMove);
      }
    };

    document.addEventListener('click', hideBox);
    document.addEventListener('mousemove', hideBoxOnMouseMove);

    // Auto-hide after 3 seconds
    setTimeout(() => {
      if (floatingBox && document.body.contains(floatingBox)) {
        floatingBox.remove();
        setFloatingBox(null);
        document.removeEventListener('click', hideBox);
        document.removeEventListener('mousemove', hideBoxOnMouseMove);
      }
    }, 3000);

    // Save word to user's list
    floatingBox.querySelector(".save-word-btn").addEventListener("click", () => {
      saveWordToUserList(word);
      floatingBox.remove();
      setFloatingBox(null);
      document.removeEventListener('click', hideBox);
      document.removeEventListener('mousemove', hideBoxOnMouseMove);
    });

    // Pronounce word (use article language for accent/voice so e.g. German words get German TTS)
    floatingBox.querySelector(".pronounce-btn").addEventListener("click", async () => {
      try {
        const pronounceUrl = `${ROOT_API_URL}/pronounce?word=${encodeURIComponent(word)}&accent=${encodeURIComponent(langCodes.accent)}&voice=${encodeURIComponent(langCodes.voice)}`;
        const response = await fetch(pronounceUrl);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const details = data?.details || data?.error || `HTTP ${response.status}`;
          throw new Error(details);
        }
        const audioUrl = data.audio;

        const audio = new Audio(audioUrl);
        audio.play();
      } catch (error) {
        console.error("Error fetching pronunciation:", error.message);
        showNotification(`Failed to pronounce word: ${error.message}`, 'error');
      }
    });
  };

  const saveWordToUserList = (word) => {
    console.log(`Saving word: ${word}`);
    showNotification(`Word "${word}" saved to your vocabulary list!`, 'success');
    // TODO: Implement backend logic to save the word to the user's list
  };
  // Get user's first name for welcome message
  const getUserFirstName = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const capitalizeName = (name) => {
      if (!name) return '';
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };
    if (user.first_name) {
      return capitalizeName(user.first_name);
    }
    if (user.name) {
      const nameParts = user.name.split(' ');
      return capitalizeName(nameParts[0]);
    }
    return 'there';
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffMins < 60) return diffMins <= 1 ? '1 min ago' : `${diffMins} mins ago`;
      if (diffHours < 24) return diffHours === 1 ? t('user.dashboard.recentActivity.hourAgo') : t('user.dashboard.recentActivity.hoursAgo_plural', { count: diffHours });
      if (diffDays < 7) return `${diffDays} ${t('user.dashboard.days')} ago`;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
    } catch (e) {
      return dateStr;
    }
  };

  const renderDashboardContent = () => {
    const userFirstName = getUserFirstName();
    const progress = dashboardProgress?.progress || {};
    const vocabulary = progress.vocabulary || {};
    const streak = dashboardProgress?.streak?.displayText || '0 days';
    // Build recent activity from real badges + certificates, sorted by date (newest first)
    const badgeList = Array.isArray(dashboardProgress?.badges?.list) ? dashboardProgress.badges.list : [];
    const certList = Array.isArray(dashboardProgress?.certificates) ? dashboardProgress.certificates : [];
    const recentActivityList = [
      ...badgeList.map((b) => ({ type: 'badge', title: b.title || b.code || 'Badge', date: b.earnedAt, key: `badge-${b.code}-${b.earnedAt}` })),
      ...certList.map((c, i) => ({ type: 'certificate', title: c.title || c.levelTitle || 'Certificate', date: c.issuedAt, key: `cert-${c.id || i}-${c.issuedAt}` }))
    ]
      .filter((a) => a.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
    const paidLearningPathsCount = myCourses.filter((c) => LEARNING_PATH_IDS.includes(c.id)).length;
    const learningPathsTotal = LEARNING_PATH_IDS.length;
    const vocabValue = vocabulary.wordsMastered ?? vocabulary.wordsLearned ?? 0;
    const streakNum = (typeof streak === 'string' && streak.replace(/\D/g, '')) ? parseInt(streak.replace(/\D/g, ''), 10) : (typeof streak === 'number' ? streak : 0);
    const overviewPathsLoading = dashboardProgressLoading || myCoursesLoading;

    return (
      <div className="user-main-content">
        <div className="user-content-header">
          <h1 className="user-content-title">{t('user.dashboard.welcomeBack', { name: userFirstName })}</h1>
          <p className="user-content-subtitle">{t('user.dashboard.subtitle')}</p>
        </div>

        {/* Dashboard Overview Cards — learning paths from GET /api/me/my-courses; streak/vocab from GET /api/learning-progress */}
        <div className="user-section">
          <div className="user-section-header">
            <div className="user-section-title">
              <i className="material-icons">dashboard</i>
              {t('user.dashboard.learningOverview')}
            </div>
          </div>

          {overviewPathsLoading ? (
            <div className="user-overview-grid" style={{ opacity: 0.8 }}>
              <div className="user-overview-card">
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.dashboard.learningPathsInProgress')}</span>
                  <div className="user-overview-icon"><i className="material-icons">explore</i></div>
                </div>
                <div className="user-overview-value">—</div>
              </div>
              <div className="user-overview-card success">
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.dashboard.studyStreak')}</span>
                  <div className="user-overview-icon"><i className="material-icons">local_fire_department</i></div>
                </div>
                <div className="user-overview-value">—</div>
              </div>
              <div className="user-overview-card warning">
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.dashboard.vocabularyMastered')}</span>
                  <div className="user-overview-icon"><i className="material-icons">book</i></div>
                </div>
                <div className="user-overview-value">—</div>
              </div>
              <div className="user-overview-card">
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.pathBadges.title')}</span>
                  <div className="user-overview-icon"><i className="material-icons">emoji_events</i></div>
                </div>
                <div className="user-overview-value">—</div>
              </div>
            </div>
          ) : (
            <div className="user-overview-grid">
              <div
                className="user-overview-card clickable"
                role="button"
                tabIndex={0}
                onClick={() => setActiveSection('learning-paths')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveSection('learning-paths');
                  }
                }}
                aria-label={t('user.myLearningPaths.title')}
              >
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.dashboard.learningPathsInProgress')}</span>
                  <div className="user-overview-icon">
                    <i className="material-icons">explore</i>
                  </div>
                </div>
                <div className="user-overview-value">{paidLearningPathsCount}</div>
                <div className="user-overview-change">
                  <span className="user-overview-subtitle">
                    {t('user.dashboard.learningPathsPaidSubtitle', {
                      count: paidLearningPathsCount,
                      total: learningPathsTotal
                    })}
                  </span>
                  <i className="material-icons" aria-hidden="true">chevron_right</i>
                </div>
              </div>

              <div className="user-overview-card success">
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.dashboard.studyStreak')}</span>
                  <div className="user-overview-icon">
                    <i className="material-icons">local_fire_department</i>
                  </div>
                </div>
                <div className="user-overview-value">{streakNum || streak}</div>
                <div className="user-overview-change">
                  <i className="material-icons">trending_up</i>
                  <span>{streak}</span>
                </div>
              </div>

              <div className="user-overview-card warning">
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.dashboard.vocabularyMastered')}</span>
                  <div className="user-overview-icon">
                    <i className="material-icons">book</i>
                  </div>
                </div>
                <div className="user-overview-value">{vocabValue}</div>
                <div className="user-overview-change">
                  {vocabulary.displayText && <span className="user-overview-subtitle">{vocabulary.displayText}</span>}
                </div>
              </div>

              <div
                className="user-overview-card clickable"
                role="button"
                tabIndex={0}
                onClick={() => setActiveSection('learning-path-badges')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveSection('learning-path-badges');
                  }
                }}
                aria-label={t('user.pathBadges.title')}
              >
                <div className="user-overview-header">
                  <span className="user-overview-title">{t('user.pathBadges.title')}</span>
                  <div className="user-overview-icon">
                    <i className="material-icons">emoji_events</i>
                  </div>
                </div>
                {pathBadgesError ? (
                  <>
                    <div className="user-overview-value path-badge-card-error-value">—</div>
                    <p className="learning-path-stat path-badge-card-error" style={{ marginBottom: 8 }}>
                      {t('user.pathBadges.loadError')}
                    </p>
                    <div className="user-overview-change">
                      <button
                        type="button"
                        className="path-badge-retry"
                        disabled={pathBadgesLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPathBadgesRetryTick((n) => n + 1);
                        }}
                      >
                        {t('user.pathBadges.retry')}
                      </button>
                    </div>
                  </>
                ) : pathBadgesSummary == null ? (
                  <>
                    <div className="user-overview-value path-badge-card-loading">{t('user.pathBadges.loadingShort')}</div>
                    <p className="learning-path-stat" style={{ marginBottom: 8 }}>{t('user.pathBadges.earnedLabel')}</p>
                  </>
                ) : (
                  <>
                    <div className="user-overview-value">{pathBadgesSummary?.summary?.earned ?? 0}</div>
                    <p className="learning-path-stat" style={{ marginBottom: 8 }}>{t('user.pathBadges.earnedLabel')}</p>
                    <div className="user-overview-change">
                      <span className="user-overview-subtitle">
                        {t('user.pathBadges.cardSubtitle', {
                          earned: pathBadgesSummary?.summary?.earned ?? 0,
                          paths: pathBadgesSummary?.summary?.pathsWithBadges ?? 0
                        })}
                      </span>
                      <i className="material-icons" aria-hidden="true">chevron_right</i>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity - real data: badges + certificates from API */}
        <div className="user-section">
          <div className="user-section-header">
            <div className="user-section-title">
              <i className="material-icons">schedule</i>
              {t('user.dashboard.recentActivity.title')}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {dashboardProgressLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('user.sidebar.loading')}</div>
            ) : recentActivityList.length > 0 ? (
              recentActivityList.map((item) => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'center', padding: '16px', background: 'var(--hover)', borderRadius: '8px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: item.type === 'certificate' ? 'var(--success)' : 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px' }}>
                    <i className="material-icons" style={{ color: 'white', fontSize: '20px' }}>{item.type === 'certificate' ? 'card_membership' : 'emoji_events'}</i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: 'var(--text)', marginBottom: '4px' }}>
                      {item.type === 'certificate' ? t('user.dashboard.recentActivity.certificateEarned') : t('user.dashboard.recentActivity.earnedAchievement')}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{item.title}</div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{formatTimeAgo(item.date)}</div>
                </div>
              ))
            ) : (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--hover)', borderRadius: '8px' }}>
                {t('user.dashboard.recentActivity.noRecentActivity')}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCoursesContent = () => {
    return (
      <div className="user-main-content">
        <div className="user-content-header">
          <h1 className="user-content-title">{t('user.myCourses.title')}</h1>
          <p className="user-content-subtitle">{t('user.myCourses.subtitle')}</p>
        </div>

        <div className="user-section">
          <div className="user-section-header">
            <div className="user-section-title">
              <i className="material-icons">school</i>
              {t('user.myCourses.activeCourses')}
            </div>
          </div>

          {myCoursesLoading ? (
            <div className="user-courses-loading">
              <p>{t('user.myCourses.loading') || 'Loading your courses...'}</p>
            </div>
          ) : myCourses.length === 0 ? (
            <div className="user-courses-empty">
              <i className="material-icons">info</i>
              <h3>{t('user.myCourses.noSubscriptionPlansYet') || 'No subscription plans yet'}</h3>
              <p>{t('user.myCourses.noSubscriptionPlansNote') || 'You don\'t have an active subscription plan. Contact support or check back later for available plans.'}</p>
              <button type="button" className="user-course-btn primary" onClick={() => setActiveSection('subscription')}>
                <i className="material-icons">card_membership</i>
                {t('user.myCourses.viewSubscription') || 'View subscription'}
              </button>
            </div>
          ) : (
            <div className="user-courses-grid">
              {myCourses.map((course) => {
                const explore = exploreCourses.find(c => c.id === course.id);
                const title = explore?.title ?? course.title;
                const description = explore?.description ?? course.description;
                const iconContent = explore?.icon ? <span className="explore-tab-icon">{explore.icon}</span> : <i className="material-icons">{course.icon || 'school'}</i>;
                const onContinue = () => openPaidLearningPath(course);
                return (
                  <div key={course.id} className="user-course-card">
                    <div className="user-course-image">
                      <div className="placeholder">
                        {iconContent}
                      </div>
                    </div>
                    <div className="user-course-content">
                      <h3 className="user-course-title">{title}</h3>
                      <p className="user-course-description">{description}</p>
                      <div className="user-course-meta">
                        <span className="user-course-status active">{t('user.myCourses.active')}</span>
                      </div>
                      <div className="user-course-actions">
                        <button
                          type="button"
                          className="user-course-btn primary"
                          onClick={onContinue}
                        >
                          <i className="material-icons">play_arrow</i>
                          {t('user.myCourses.continue')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMyLearningPathsContent = () => {
    const fallbackPaths = myCourses.filter((c) => LEARNING_PATH_IDS.includes(c.id));
    const rawPathCards = learningPathsProgress.length > 0 ? learningPathsProgress : fallbackPaths;
    const pathCards = rawPathCards.flatMap((path) => {
      if (path.language) return [path];
      const langs = [
        ...(Array.isArray(path.languages) ? path.languages : []),
        ...(Array.isArray(path.scope?.languages) ? path.scope.languages : [])
      ];
      const unique = [...new Set(langs.map((l) => String(l).trim().toLowerCase()).filter(Boolean))];
      if (!unique.length) return [path];
      return unique.map((language) => ({ ...path, language }));
    });
    const isLoading = learningPathsProgressLoading || (myCoursesLoading && pathCards.length === 0);

    const progressLabel = (path) => {
      const completed = Number(path.completed) || 0;
      const total = Number(path.total) || 0;
      const unit = path.unit || 'articles';
      if (unit === 'exercises') {
        return t('user.myLearningPaths.exercisesCompleted', { completed, total });
      }
      if (unit === 'texts') {
        return t('user.myLearningPaths.textsCompleted', { completed, total });
      }
      return t('user.myLearningPaths.articlesCompleted', { completed, total });
    };

    return (
      <div className="user-main-content">
        <div className="user-content-header">
          <div className="user-content-header-top">
            <button type="button" className="back-btn" onClick={() => setActiveSection('dashboard')}>
              <i className="material-icons">arrow_back</i>
              <span>{t('user.myLearningPaths.backToDashboard')}</span>
            </button>
            <h1 className="user-content-title">{t('user.myLearningPaths.title')}</h1>
          </div>
          <p className="user-content-subtitle">{t('user.myLearningPaths.subtitle')}</p>
        </div>

        <div className="user-section">
          {isLoading ? (
            <div className="user-courses-loading">
              <p>{t('user.myLearningPaths.loading')}</p>
            </div>
          ) : pathCards.length === 0 ? (
            <div className="user-courses-empty">
              <i className="material-icons">info</i>
              <h3>{t('user.myLearningPaths.emptyTitle')}</h3>
              <p>{t('user.myLearningPaths.emptyNote')}</p>
              <button type="button" className="user-course-btn primary" onClick={() => setActiveSection('subscription')}>
                <i className="material-icons">card_membership</i>
                {t('user.myLearningPaths.viewSubscription')}
              </button>
            </div>
          ) : (
            <div className="user-courses-grid">
              {pathCards.map((path) => {
                const explore = exploreCourses.find((c) => c.id === path.id);
                const title = explore?.title ?? path.titleKey ?? path.title;
                const languageLabel = pathLanguageLabel(path.language);
                const iconContent = explore?.icon
                  ? <span className="explore-tab-icon">{explore.icon}</span>
                  : <i className="material-icons">{path.icon || 'school'}</i>;
                const completed = Number(path.completed) || 0;
                const percent = Number(path.percent) || 0;
                const started = path.started === true || completed > 0;
                const showRetention = path.hasReview === true
                  || path.id === 'educational-levels-vocabulary'
                  || path.id === 'international-exam-vocabulary';
                return (
                  <div key={path.language ? `${path.id}-${path.language}` : path.id} className="user-course-card learning-path-card">
                    <div className="user-course-image">
                      <div className="placeholder">{iconContent}</div>
                    </div>
                    <div className="user-course-content">
                      <h3 className="user-course-title">{title}</h3>
                      {languageLabel ? (
                        <p className="learning-path-language">{languageLabel}</p>
                      ) : null}
                      <div className="learning-path-stats">
                        <p className="learning-path-stat">{progressLabel(path)}</p>
                        {started ? (
                          <p className="learning-path-stat">{t('user.myLearningPaths.percentProgress', { percent })}</p>
                        ) : (
                          <p className="learning-path-stat not-started">{t('user.myLearningPaths.notStarted')}</p>
                        )}
                        <div className="user-course-progress">
                          <div className="user-course-progress-bar">
                            <div className="user-course-progress-fill" style={{ width: `${Math.min(100, percent)}%` }} />
                          </div>
                        </div>
                      </div>
                      <div className="user-course-actions">
                        <button
                          type="button"
                          className="user-course-btn primary"
                          onClick={() => openPaidLearningPath(path)}
                        >
                          <i className="material-icons">play_arrow</i>
                          {started ? t('user.myLearningPaths.continue') : t('user.myLearningPaths.start')}
                        </button>
                        {showRetention && (
                          <button
                            type="button"
                            className="user-course-btn"
                            onClick={() => openPathRetention(path, title)}
                          >
                            <i className="material-icons">replay</i>
                            {t('user.myLearningPaths.articlesInRetention')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPathRetentionContent = () => {
    const pathTitle = pathRetentionFilter?.title || t('user.dashboard.articlesInRetention');
    const pathLanguage = pathLanguageLabel(pathRetentionFilter?.language);
    return (
      <div className="user-main-content">
        <div className="user-content-header">
          <div className="user-content-header-top">
            <button type="button" className="back-btn" onClick={() => setActiveSection('learning-paths')}>
              <i className="material-icons">arrow_back</i>
              <span>{t('user.myLearningPaths.backToLearningPaths')}</span>
            </button>
            <h1 className="user-content-title">{pathTitle}</h1>
          </div>
          <p className="user-content-subtitle">
            {pathLanguage
              ? `${pathLanguage} · ${t('user.dashboard.articlesInRetention')}`
              : t('user.dashboard.articlesInRetention')}
          </p>
        </div>

        <div className="user-section">
          <div className="user-section-header">
            <div className="user-section-title">
              <i className="material-icons">replay</i>
              {t('user.dashboard.articlesInRetention')}
            </div>
          </div>
          {pathRetentionLoading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>{t('user.sidebar.loading')}</div>
          ) : pathRetentionList.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', background: 'var(--hover)', borderRadius: '8px' }}>
              {t('user.dashboard.noArticlesInRetention')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.dashboard.retentionArticle')}</th>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.dashboard.retentionMode')}</th>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.dashboard.retentionStage')}</th>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.dashboard.retentionNextReview')}</th>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.dashboard.retentionMemorized')}</th>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.dashboard.retentionStatus')}</th>
                    <th style={{ padding: '12px 8px', color: 'var(--text-secondary)' }}>{t('user.myLearningPaths.retentionReview')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pathRetentionList.map((r) => (
                    <tr key={r.id || `${r.article_id}-${r.source || 'path'}`} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 8px', color: 'var(--text)' }}>{r.article_title || `Article ${r.article_id}`}</td>
                      <td style={{ padding: '12px 8px', color: 'var(--text)' }}>
                        {r.mode === 'learning' && t('user.dashboard.retentionModeLearning')}
                        {r.mode === 'reinforcement' && t('user.dashboard.retentionModeReinforcement')}
                        {r.mode === 'weak_focus' && t('user.dashboard.retentionModeWeakFocus')}
                      </td>
                      <td style={{ padding: '12px 8px', color: 'var(--text)' }}>{r.stage != null ? r.stage : '—'}</td>
                      <td style={{ padding: '12px 8px', color: 'var(--text)' }}>{r.next_review_date ? new Date(r.next_review_date).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '12px 8px', color: 'var(--text)' }}>{r.memorized_percentage != null ? `${r.memorized_percentage}%` : '—'}</td>
                      <td style={{ padding: '12px 8px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: r.status === 'completed' ? 'var(--success)' : 'var(--accent)',
                          color: 'white'
                        }}>
                          {r.status === 'active' ? t('user.dashboard.retentionStatusActive') : t('user.dashboard.retentionStatusCompleted')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <button
                          type="button"
                          className="user-course-btn"
                          onClick={() => openPathRetentionReview(r)}
                        >
                          <i className="material-icons">replay</i>
                          {t('user.myLearningPaths.retentionReview')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSectionContent = () => {
    // Reading Paths view
    if (readingPathsOpen) {
      return (
        <div className="user-main-content">
          <div className="user-content-header">
            <div className="user-content-header-top">
              <button className="back-btn" onClick={closeReadingPaths}>
                <i className="material-icons">arrow_back</i>
                <span>Back to Explore Courses</span>
              </button>
              <h1 className="user-content-title">📖 Reading</h1>
            </div>
            <p className="user-content-subtitle">Choose a reading path to browse articles with filters and progress tracking</p>
          </div>

          {!activeReadingPath ? (
            <div className="user-section">
              <div className="dictation-paths-grid">
                <div className="dictation-path-card" onClick={() => openReadingPath('educational-levels')}>
                  <div className="path-icon">📚</div>
                  <h3>Educational Levels Reading</h3>
                  <p>National curriculum by school and year</p>
                </div>

                <div className="dictation-path-card" onClick={() => openReadingPath('international-exam')}>
                  <div className="path-icon">🌍</div>
                  <h3>International Exam Reading</h3>
                  <p>IELTS, TOEFL, Cambridge passages</p>
                </div>

                <div className="dictation-path-card" onClick={() => openReadingPath('esp')}>
                  <div className="path-icon">🎓</div>
                  <h3>ESP Reading</h3>
                  <p>Medicine, Engineering, Business</p>
                </div>

                <div className="dictation-path-card" onClick={() => openReadingPath('general')}>
                  <div className="path-icon">📖</div>
                  <h3>General English Reading</h3>
                  <p>Stories, news, blogs, dialogues</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="user-section">
              {/* Educational Levels Reading layout mirroring vocabulary */}
              {activeReadingPath === 'educational-levels' && (
                <>
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">library_books</i>
                      Educational Levels Vocabulary
                    </div>
                    <p>Select an educational level to access vocabulary content</p>
                  </div>
                  <div className="educational-tabs">
                    {schoolLevels.map(level => (
                      <button 
                        key={level.id}
                        className={`educational-tab ${readingActiveSchoolLevel === level.id ? 'active' : ''}`}
                        onClick={() => {
                          setReadingActiveSchoolLevel(level.id);
                          setReadingActiveYearLevel(`${level.id}-1`);
                          fetchReadingArticles('educational-levels');
                        }}
                      >
                        <i className="material-icons">school</i>
                        {level.name}
                      </button>
                    ))}
                  </div>
                  <div className="year-tabs">
                    {schoolLevels.find(level => level.id === readingActiveSchoolLevel)?.years.map((year, index) => (
                      <button 
                        key={index}
                        className={`year-tab ${readingActiveYearLevel === `${readingActiveSchoolLevel}-${index + 1}` ? 'active' : ''}`}
                        onClick={() => {
                          setReadingActiveYearLevel(`${readingActiveSchoolLevel}-${index + 1}`);
                          fetchReadingArticles('educational-levels');
                        }}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                  <div className="user-filters-bar" style={{ marginTop: 8 }}>
                    <div className="search-box">
                      <i className="material-icons">search</i>
                      <input value={readingSearch} onChange={(e)=> setReadingSearch(e.target.value)} placeholder="Search reading content..." />
                    </div>
                    <button className="user-course-btn" onClick={()=> fetchReadingArticles('educational-levels')}>
                      <i className="material-icons">refresh</i>
                      Refresh
                    </button>
                  </div>
                </>
              )}

              {activeReadingPath === 'international-exam' && (
                <>
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">library_books</i>
                      International Exam Reading
                    </div>
                    <p>Select an exam type to access reading content</p>
                  </div>

                  <div className="exam-tabs">
                    {[{ key: 'ielts', label: 'IELTS' }, { key: 'toefl', label: 'TOEFL' }, { key: 'cambridge', label: 'Cambridge' }, { key: 'other-exams', label: 'Other Exams' }].map(({ key, label }) => (
                      <button
                        key={key}
                        className={`exam-tab ${readingActiveExam === key ? 'active' : ''}`}
                        onClick={() => {
                          setReadingActiveExam(key);
                          setReadingActiveExamVariant('Academic');
                          fetchReadingArticles('international-exam');
                        }}
                      >
                        <i className="material-icons">school</i>
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="exam-tabs segmented">
                    {(examLevels.find(e => e.id === readingActiveExam)?.years || ['Academic']).map(variant => (
                      <button
                        key={variant}
                        className={`exam-tab ${readingActiveExamVariant === variant ? 'active' : ''}`}
                        onClick={() => {
                          setReadingActiveExamVariant(variant);
                          fetchReadingArticles('international-exam');
                        }}
                      >
                        {variant}
                      </button>
                    ))}
                  </div>

                  <div className="user-filters-bar" style={{ marginTop: 8 }}>
                    <div className="search-box">
                      <i className="material-icons">search</i>
                      <input value={readingSearch} onChange={(e)=> setReadingSearch(e.target.value)} placeholder="Search reading content..." />
                    </div>
                    <button className="user-course-btn" onClick={()=> fetchReadingArticles('international-exam')}>
                      <i className="material-icons">refresh</i>
                      Refresh
                    </button>
                  </div>
                </>
              )}

              {activeReadingPath === 'esp' && (
                <>
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">school</i>
                      Academic Reading (ESP)
                    </div>
                    <p className="user-section-subtitle">Select a faculty to access specialized academic reading</p>
                  </div>

                  {/* Faculty Tabs (mirror ESP Vocabulary) */}
                  <div className="esp-faculty-tabs">
                    {Object.entries(facultyData).map(([facultyId, faculty]) => (
                      <button 
                        key={facultyId}
                        className={`esp-faculty-tab ${activeFaculty === facultyId ? 'active' : ''}`}
                        onClick={() => handleFacultyChange(facultyId)}
                      >
                        <i className="material-icons" aria-hidden="true">{faculty.icon}</i>
                        {faculty.name}
                      </button>
                    ))}
                  </div>

                  {/* Specialties Grid */}
                  {activeFaculty && !activeSpecialty && (
                    <div className="user-section">
                      <div className="user-section-header">
                        <div className="user-section-title">
                          <i className="material-icons">{facultyData[activeFaculty].icon}</i>
                          {facultyData[activeFaculty].name} Specialties
                        </div>
                        <p className="user-section-subtitle">Select a specialty to access its reading content</p>
                      </div>

                      <div className="esp-specialties-grid">
                        {facultyData[activeFaculty].specialties.map(specialty => (
                          <div 
                            key={specialty}
                            className="esp-specialty-card"
                            onClick={() => handleSpecialtyChange(specialty)}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="esp-specialty-icon">
                              <i className="material-icons" aria-hidden="true">school</i>
                            </div>
                            <div className="esp-specialty-content">
                              <h3 className="esp-specialty-title">{specialty}</h3>
                              <p className="esp-specialty-description">Access reading content for {specialty}</p>
                            </div>
                            <div className="esp-specialty-arrow">
                              <i className="material-icons" aria-hidden="true">arrow_forward</i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Academic Level Grid */}
                  {activeFaculty && activeSpecialty && !activeAcademicLevel && (
                    <div className="user-section">
                      <div className="user-section-header">
                        <div className="user-section-header-top">
                          <button className="back-btn" onClick={() => setActiveSpecialty('')}>
                            <i className="material-icons">arrow_back</i>
                            <span>Back to Specialties</span>
                          </button>
                          <div className="user-section-title">
                            <i className="material-icons">{facultyData[activeFaculty].icon}</i>
                            {activeSpecialty} Academic Levels
                          </div>
                        </div>
                        <p className="user-section-subtitle">Select an academic level to access {activeSpecialty} reading</p>
                      </div>

                      <div className="esp-specialties-grid">
                        {academicLevels.map(level => (
                          <div 
                            key={level.id}
                            className="esp-specialty-card"
                            onClick={() => handleAcademicLevelChange(level.name)}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="esp-specialty-icon">
                              <i className="material-icons" aria-hidden="true">{level.icon}</i>
                            </div>
                            <div className="esp-specialty-content">
                              <h3 className="esp-specialty-title">{level.name}</h3>
                              <p className="esp-specialty-description">{level.description}</p>
                            </div>
                            <div className="esp-specialty-arrow">
                              <i className="material-icons" aria-hidden="true">arrow_forward</i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ESP Reading Content */}
                  {activeSpecialty && activeAcademicLevel && (
                    <div className="user-section">
                      <div className="user-section-header">
                        <div className="user-section-header-top">
                          <button className="back-btn" onClick={() => setActiveAcademicLevel('')}>
                            <i className="material-icons">arrow_back</i>
                            <span>Back to Academic Levels</span>
                          </button>
                          <div className="user-section-title">
                            <i className="material-icons">{facultyData[activeFaculty].icon}</i>
                            {activeSpecialty} - {activeAcademicLevel} Reading
                          </div>
                        </div>
                        <p className="user-section-subtitle">Browse reading content for {activeSpecialty} at {activeAcademicLevel} level</p>
                      </div>

                      {espContent.length > 0 ? (
                        <div className="educational-content-grid">
                          {espContent.map(content => (
                            <div key={content.id} className="educational-content-card article-card" onClick={() => openReadingFromList(content)}>
                              <div className="educational-content-header">
                                <div className="educational-content-icon">
                                  <i className="material-icons">article</i>
                                </div>
                                <div className="educational-content-info">
                                  <div className="content-type-badge">📖 Reading</div>
                                  <h3 className="educational-content-title">{content.title}</h3>
                                  <p className="educational-content-description">
                                    {cleanHtmlForPreview(content.content, 120)}
                                  </p>
                                </div>
                              </div>
                              <div className="educational-content-meta">
                                <span className="educational-content-difficulty">
                                  {content.difficulty || 'Intermediate'}
                                </span>
                                <span className="educational-content-duration">Reading Article</span>
                              </div>
                              <div className="educational-content-actions">
                                <button className="user-course-btn primary" onClick={(e) => { e.stopPropagation(); openReadingFromList(content); }}>
                                  <i className="material-icons">visibility</i>
                                  Read Article
                                </button>
                                <button 
                                  className="user-course-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDetailsModal(content);
                                  }}
                                >
                                  <i className="material-icons">info</i>
                                  Details
                                </button>
                              </div>
                              <div className="article-metadata">
                                <div className="metadata-left">
                                  <div className="metadata-item">
                                    <span className="material-icons">school</span>
                                    <span>{activeSpecialty}</span>
                                  </div>
                                  <div className="metadata-item">
                                    <span className="material-icons">calendar_today</span>
                                    <span>{activeAcademicLevel}</span>
                                  </div>
                                </div>
                                <div className="metadata-item">
                                  <span className="material-icons">schedule</span>
                                  <span>{content.created_at ? new Date(content.created_at).toLocaleDateString() : new Date().toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}

              {activeReadingPath === 'general' && (
                <>
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">library_books</i>
                      General English Reading
                    </div>
                    <p className="user-section-subtitle">Browse general English reading content</p>
                  </div>
                  <div className="filters-row">
                    <div className="filters-left" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <select value={readingLevel} onChange={(e)=> setReadingLevel(e.target.value)}>
                        <option value="all">Level: All</option>
                        <option value="A1">A1</option>
                        <option value="A2">A2</option>
                        <option value="B1">B1</option>
                        <option value="B2">B2</option>
                        <option value="C1">C1</option>
                        <option value="C2">C2</option>
                      </select>
                      <select value={readingGenre} onChange={(e)=> setReadingGenre(e.target.value)}>
                        <option value="">Genre: All</option>
                        <option value="story">Story</option>
                        <option value="news">News</option>
                        <option value="conversation">Conversation</option>
                        <option value="dialogue">Dialogue</option>
                        <option value="poem">Poem</option>
                        <option value="essay">Essay</option>
                        <option value="novel">Novel</option>
                        <option value="vocabulary">Vocabulary</option>
                      </select>
                      <select value={readingMinTime} onChange={(e)=> setReadingMinTime(e.target.value)}>
                        <option value="">Reading Time</option>
                        <option value="0-3">0–3 min</option>
                        <option value="3-6">3–6 min</option>
                        <option value=">6">6+ min</option>
                      </select>
                      <select value={readingDifficulty} onChange={(e)=> setReadingDifficulty(e.target.value)}>
                        <option value="all">Difficulty</option>
                        <option value="Easy">Easy</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Advanced">Advanced</option>
                      </select>
                    </div>
                    <div className="filters-right" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div className="search-box">
                        <i className="material-icons">search</i>
                        <input value={readingSearch} onChange={(e)=> setReadingSearch(e.target.value)} placeholder="Search reading content..." />
                      </div>
                      <button className="user-course-btn" onClick={()=> fetchReadingArticles('general')}>
                        <i className="material-icons">refresh</i>
                        Refresh
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Articles grid styled like educational levels */}
              {activeReadingPath !== 'esp' && (
                <>
                  {readingExamLockNotice && (
                    <div className="reading-exam-sequential-notice" role="status">
                      <i className="material-icons reading-exam-sequential-notice__lock" aria-hidden="true">lock</i>
                      <span>{readingExamLockNotice}</span>
                    </div>
                  )}
                  {readingLoading ? (
                  <div className="empty-state">Loading articles...</div>
                ) : readingError ? (
                  <div className="empty-state">{readingError}</div>
                ) : (readingArticles || []).length === 0 ? (
                  <div className="empty-state">No articles found</div>
                ) : (
                      <div className="educational-content-grid">
                    {(readingArticles || [])
                      .filter(a => !readingSearch || (a.title || '').toLowerCase().includes(readingSearch.toLowerCase()) || (a.description || '').toLowerCase().includes(readingSearch.toLowerCase()))
                      .map((article, index) => {
                        const isExamMasteryLocked =
                          activeReadingPath === 'international-exam' && article.exam_sequential_access === 'mastery_locked';
                        return (
                          <div
                            key={index}
                            className={`educational-content-card article-card${isExamMasteryLocked ? ' article-card--mastery-locked' : ''}`}
                            title={isExamMasteryLocked ? t('user.readingExamSequential.masteryLockTooltip') : undefined}
                            onClick={() => openReadingFromList(article)}
                          >
                        {isExamMasteryLocked && (
                          <span
                            className="article-card-lock-badge article-card-lock-badge--mastery material-icons"
                            title={t('user.readingExamSequential.masteryLockTooltip')}
                            aria-hidden="true"
                          >
                            lock
                          </span>
                        )}
                        <div className="educational-content-header">
                          <div className="educational-content-icon">
                            <i className="material-icons">article</i>
                          </div>
                          <div className="educational-content-info">
                            <h3 className="educational-content-title">{article.title}</h3>
                                                                                                                             <p className="educational-content-description">
                                     {cleanHtmlForPreview(article.content || article.description || '', 120)}
                                   </p>
                          </div>
                        </div>
                        <div className="educational-content-meta">
                          <span className="educational-content-difficulty">
                            {article.unit || article.difficulty || 'Intermediate'}
                          </span>
                          <span className="educational-content-duration">Reading Article</span>
                        </div>
                        <div className="educational-content-actions">
                              <button
                            className="user-course-btn primary"
                            disabled={isExamMasteryLocked}
                            onClick={(e) => { e.stopPropagation(); openReadingFromList(article); }}
                          >
                            <i className="material-icons">visibility</i>
                            Read Article
                          </button>
                          <button 
                            className="user-course-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailsModal(article);
                            }}
                          >
                            <i className="material-icons">info</i>
                            Details
                          </button>
                        </div>
                        <div className="article-metadata">
                          <div className="metadata-left">
                            {activeReadingPath === 'educational-levels' ? (
                              <>
                                <div className="metadata-item">
                                  <span className="material-icons">school</span>
                                  <span>{article.school || 'High School'}</span>
                                </div>
                                <div className="metadata-item">
                                  <span className="material-icons">calendar_today</span>
                                  <span>{article.year || 'First Year'}</span>
                                </div>
                              </>
                            ) : activeReadingPath === 'international-exam' ? (
                              <>
                                <div className="metadata-item">
                                  <span className="material-icons">assignment</span>
                                  <span>{(readingActiveExam === 'ielts' ? 'IELTS' : readingActiveExam === 'toefl' ? 'TOEFL' : readingActiveExam === 'cambridge' ? 'Cambridge' : 'Exam')} – {readingActiveExamVariant}</span>
                                </div>
                                {article.topic && (
                                  <div className="metadata-item">
                                    <span className="material-icons">label</span>
                                    <span>{article.topic}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {(article.exam || article.exam_variant) && (
                                  <div className="metadata-item">
                                    <span className="material-icons">assignment</span>
                                    <span>{`${article.exam || ''}${article.exam && article.exam_variant ? ' – ' : ''}${article.exam_variant || ''}`.trim()}</span>
                                  </div>
                                )}
                                {article.topic && (
                                  <div className="metadata-item">
                                    <span className="material-icons">label</span>
                                    <span>{article.topic}</span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          <div className="metadata-item">
                            <span className="material-icons">schedule</span>
                            <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : new Date().toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                        );
                      })}
                  </div>
                )}
                </>
              )}
            </div>
          )}
          {/* Shared reader overlay (identical to vocabulary reader modal structure) */}
          {readingReaderOpen && readingCurrentArticle && (
            <div className="modal-overlay article-read-overlay" onClick={closeReadingModal}>
              <div className="modal-content reading-article-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header article-title-header">
                  <h2 className="modal-title">{renderTranslatableText(readingCurrentArticle.title, readingCurrentArticle)}</h2>
                  <button className="modal-close" onClick={closeReadingModal}>
                    <i className="material-icons">close</i>
                  </button>
                </div>
                <div className="modal-body reading-article-body">
                  
                  {/* Reading Interface - ONLY for Reading Section - AT THE TOP */}
                  <div className="reading-interface">
                    <div className="reading-controls">
                                                                    <button 
                        className="reading-btn model-reading-btn"
                        onClick={isModelReading ? stopModelReading : startModelReading}
                        disabled={false}
                      >
                        <span className="material-icons">
                          {isModelReading ? 'stop' : 'play_arrow'}
                        </span>
                        Model Reading
                      </button>
                      
                      
                      

                      

                      
                      <button 
                        className="reading-btn start-reading-btn"
                        onClick={isReadingActive ? stopReadingSession : startReadingSession}
                        disabled={isModelReading}
                      >
                        <span className="material-icons">
                          {isReadingActive ? 'stop' : 'mic'}
                        </span>
                        {isReadingActive ? 'Stop Reading' : 'Start Reading'}
                      </button>
                  </div>
                    
                    {/* Remark about browser controls - show when button is clicked */}
                    {showRemark && (
                      <div className="reading-remark">
                        <span className="material-icons">info</span>
                        <span>To pause or cancel the reading, click the audio button in your browser tab</span>
                          </div>
                    )}
                    
                                        {isReadingActive && (
                      <div className="reading-status">
                        <span className="material-icons rotating">mic</span>
                        <span>🎤 Reading in progress... Speak clearly into your microphone. Click "Stop Reading" when finished.</span>
                      </div>
                    )}
                    
                    {/* Real-time recognition display */}
                    {isReadingActive && (
                      <div className="recognition-display">
                        <div className="recognition-header">
                          <span className="material-icons">record_voice_over</span>
                          <span>Live Recognition</span>
                        </div>
                        <div className="recognition-text">
                          <span className="recognition-label">Your pronunciation:</span>
                          <div className="recognition-content" id="liveRecognitionText">
                            <span className="recognition-placeholder">Start speaking to see your words highlighted...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hidden HTML5 audio element for Azure TTS playback control */}
                    <audio 
                      ref={(el) => {
                        if (el && azureSpeechService.current) {
                          azureSpeechService.current.setAudioElement(el);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </div>
                  

                  
                  {/* Article Content - Below the reading interface */}
                  <div className="article-reader-panel">
                    <div ref={readingContentRef} className="article-reader-text">
                      <div dangerouslySetInnerHTML={{ __html: formatArticleContent(readingCurrentArticle?.content) || 'No content available' }} />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
    // Check if dictation paths are open first
    if (dictationPathsOpen) {
      return (
        <div className="user-main-content">
          <div className="user-content-header">
            <div className="user-content-header-top">
              <button className="back-btn" onClick={closeDictationPaths}>
                <i className="material-icons">arrow_back</i>
                <span>Back to Explore Courses</span>
              </button>
              <h1 className="user-content-title">🎧 Dictation Practice</h1>
            </div>
            <p className="user-content-subtitle">Choose your learning path for listening and writing practice</p>
          </div>

          {/* Dictation Path Selection */}
          {!activeDictationPath ? (
            <div className="user-section">
              <div className="dictation-paths-grid">
                <div className="dictation-path-card" onClick={() => openDictationPath('educational-levels')}>
                  <div className="path-icon">📚</div>
                  <h3>Educational Levels Vocabulary</h3>
                  <p>Practice dictation with grade-specific vocabulary and content</p>
                </div>
                
                <div className="dictation-path-card" onClick={() => openDictationPath('international-exam')}>
                  <div className="path-icon">🌍</div>
                  <h3>{t('user.exploreCourses.internationalExam.title')}</h3>
                  <p>IELTS, TOEFL, and Cambridge exam-focused dictation practice</p>
                </div>
                
                <div className="dictation-path-card" onClick={() => openDictationPath('academic-esp')}>
                  <div className="path-icon">🎓</div>
                  <h3>Academic Vocabulary (ESP)</h3>
                  <p>Specialized dictation for Medicine, Engineering, and Business</p>
                </div>
                
                <div className="dictation-path-card" onClick={() => openDictationPath('general')}>
                  <div className="path-icon">📖</div>
                  <h3>General Vocabulary</h3>
                  <p>Everyday English, travel, and professional communication dictation</p>
                </div>
              </div>
            </div>
          ) : (
            /* Individual Path Content */
            <div className="dictation-path-content">
              {activeDictationPath === 'educational-levels' && (
                <div className="user-section">
                  <div className="user-section-header">
                    <h2>📚 Educational Levels Dictation</h2>
                    <p>Select your school level and year for targeted practice</p>
                  </div>
                  
                  {/* School Level Tabs */}
                  <div className="educational-tabs">
                    {schoolLevels.map(level => (
                      <button 
                        key={level.id}
                        className={`educational-tab ${activeDictationSchoolLevel === level.id ? 'active' : ''}`}
                        onClick={() => handleDictationSchoolLevelChange(level.id)}
                      >
                        <i className="material-icons">school</i>
                        {level.name}
                      </button>
                    ))}
                  </div>

                  {/* Year Level Sub-tabs */}
                  <div className="year-tabs">
                    {schoolLevels.find(level => level.id === activeDictationSchoolLevel)?.years.map((year, index) => (
                      <button 
                        key={index}
                        className={`year-tab ${activeDictationYear === `${activeDictationSchoolLevel}-${index + 1}` ? 'active' : ''}`}
                        onClick={() => handleDictationYearChange(`${activeDictationSchoolLevel}-${index + 1}`, year)}
                      >
                        {year}
                      </button>
                    ))}
                  </div>

                  {/* Dictation Articles Display */}
                  <div className="dictation-articles-section">
                    <h3>Available Dictation Exercises</h3>
                    {dictationLoading ? (
                      <div className="loading-message">Loading dictation articles...</div>
                    ) : dictationError ? (
                      <div className="error-message">{dictationError}</div>
                    ) : dictationArticles.length > 0 ? (
                      <div className="educational-content-grid">
                        {dictationArticles.map((article, index) => (
                          <div key={article.id || index} className="educational-content-card article-card" onClick={() => startDictationExercise(article)}>
                            {/* Article Image - covers half the card */}
                            <div 
                              className="educational-content-image"
                              style={{
                                backgroundImage: article.image_url ? `url(${getImageUrl(article.image_url) || article.image_url})` : 
                                                article.cover_image ? `url(${article.cover_image})` : 
                                                'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
                              }}
                            />
                            
                            {/* Article Content */}
                            <div className="educational-content-header">
                              <h3 className="educational-content-title">{article.title}</h3>
                              <p className="educational-content-description">
                                {article.text_content ? article.text_content.substring(0, 120) + '...' : 'No content available'}
                              </p>
                            </div>
                            <div className="educational-content-meta">
                              <span className="educational-content-difficulty">
                                Year {article.year}
                              </span>
                              <span className="educational-content-duration">
                                {article.estimated_read_time ? `${article.estimated_read_time} min` : 'Dictation Exercise'}
                              </span>
                            </div>
                            <div className="educational-content-actions">
                              <button className="user-course-btn primary" onClick={(e) => { e.stopPropagation(); startDictationExercise(article); }}>
                      <i className="material-icons">play_arrow</i>
                      Start Dictation
                    </button>
                              <button 
                                className="user-course-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDictationDetailsOverlay(article);
                                }}
                              >
                                <i className="material-icons">info</i>
                                Details
                    </button>
                            </div>
                            <div className="article-metadata">
                              <div className="metadata-left">
                                <div className="metadata-item">
                                  <span className="material-icons">school</span>
                                  <span>{article.school_level ? article.school_level.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}</span>
                                </div>
                                <div className="metadata-item">
                                  <span className="material-icons">calendar_today</span>
                                  <span>Year {article.year}</span>
                                </div>
                              </div>
                              <div className="metadata-item">
                                <span className="material-icons">schedule</span>
                                <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : '15/07/2025'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-articles-message">
                        <p>No dictation articles available for the selected level and year.</p>
                        <p>Please check back later or contact your teacher.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeDictationPath === 'international-exam' && (
                <div className="user-section">
                  <div className="user-section-header">
                    <h2>🌍 International Exam Dictation</h2>
                    <p>Practice with exam-specific vocabulary and content</p>
                  </div>
                  
                  <div className="exam-tabs">
                    <button className="exam-tab active">IELTS</button>
                    <button className="exam-tab">TOEFL</button>
                    <button className="exam-tab">Cambridge</button>
                  </div>

                  <div className="dictation-exercises">
                    <h3>Listening Dictation Exercises</h3>
                    <p>Practice with vocabulary from your selected exam</p>
                    <button className="user-course-btn primary" onClick={() => startDictationExercise('exam')}>
                      <i className="material-icons">play_arrow</i>
                      Start Dictation
                    </button>
                  </div>
                </div>
              )}

              {activeDictationPath === 'academic-esp' && (
                <div className="user-section">
                  <div className="user-section-header">
                    <h2>🎓 Academic Vocabulary (ESP) Dictation</h2>
                    <p>Practice with specialized academic terminology</p>
                  </div>
                  
                  <div className="esp-tabs">
                    <button className="esp-tab active">Medicine</button>
                    <button className="esp-tab">Engineering</button>
                    <button className="esp-tab">Business</button>
                  </div>

                  <div className="dictation-exercises">
                    <h3>Listening Dictation Exercises</h3>
                    <p>Practice with vocabulary from your selected field</p>
                    <button className="user-course-btn primary" onClick={() => startDictationExercise('esp')}>
                      <i className="material-icons">play_arrow</i>
                      Start Dictation
                    </button>
                  </div>
                </div>
              )}

              {activeDictationPath === 'general' && (
                <div className="user-section">
                  <div className="user-section-header">
                    <h2>📖 General Vocabulary Dictation</h2>
                    <p>Practice with everyday English and common phrases</p>
                  </div>
                  
                  <div className="general-tabs">
                    <button className="general-tab active">Everyday English</button>
                    <button className="general-tab">Travel English</button>
                    <button className="general-tab">Professional English</button>
                  </div>

                  <div className="dictation-exercises">
                    <h3>Listening Dictation Exercises</h3>
                    <p>Practice with vocabulary from your selected category</p>
                    <button className="user-course-btn primary" onClick={() => startDictationExercise('general')}>
                      <i className="material-icons">play_arrow</i>
                      Start Dictation
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    switch (activeSection) {
      case 'dashboard':
        return renderDashboardContent();
      case 'learning-paths':
        return renderMyLearningPathsContent();
      case 'learning-path-retention':
        return renderPathRetentionContent();
      case 'learning-path-badges':
        return (
          <UserSectionPathBadges
            summaryData={pathBadgesSummary}
            summaryLoading={pathBadgesSummary == null && !pathBadgesError}
            summaryError={pathBadgesError}
            onRetrySummary={() => setPathBadgesRetryTick((n) => n + 1)}
            exploreCourses={exploreCourses}
            onBackToDashboard={() => setActiveSection('dashboard')}
            onViewSubscription={() => setActiveSection('subscription')}
          />
        );
      case 'courses':
        return renderCoursesContent();
      case 'general-vocabulary':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <div className="user-content-header-top">
                <button className="back-btn" onClick={() => setActiveSection('explore-courses')}>
                  <i className="material-icons">arrow_back</i>
                  <span>{t('user.exploreCourses.backToExplore')}</span>
                </button>
                <h1 className="user-content-title">General Vocabulary</h1>
              </div>
              <p className="user-content-subtitle">Browse reading materials by level, genre, and interest. Your clicks build your vocabulary with spaced repetition.</p>
            </div>

            {/* Browse Library: Filters + Search + Sort */}
            <div className="user-section">
              <div className="filters-row">
                <div className="filters-left">
                  <select value={gvFilterLevel} onChange={e => setGvFilterLevel(e.target.value)}>
                    <option value="all">Level: All</option>
                    {['A1','A2','B1','B2','C1','C2'].map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                  <select value={gvFilterGenre} onChange={e => setGvFilterGenre(e.target.value)}>
                    <option value="all">Genre: All</option>
                    {['daily_life','family','school','nature','work','adventure','communication','technology','travel','culture','food','shopping'].map(g => (
                      <option key={g} value={g}>{g.replace('_',' ')}</option>
                    ))}
                  </select>
                  <select value={gvFilterReadTime} onChange={e => setGvFilterReadTime(e.target.value)}>
                    <option value="all">Reading Time</option>
                    <option value="0-3">0–3 min</option>
                    <option value="4-6">4–6 min</option>
                    <option value=">6">Over 6 min</option>
                  </select>
                  <select value={gvFilterDifficulty} onChange={e => setGvFilterDifficulty(e.target.value)}>
                    <option value="all">Difficulty</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
                <div className="filters-right">
                  <div className="search-box">
                    <input value={gvSearch} onChange={e => setGvSearch(e.target.value)} placeholder="Search by title, author..." />
                    <i className="material-icons">search</i>
                  </div>
                  <select value={gvSortBy} onChange={e => setGvSortBy(e.target.value)}>
                    <option value="recommended">Recommended</option>
                    <option value="popular">Most Popular</option>
                    <option value="new">Newest</option>
                  </select>
                </div>
              </div>

              {/* Books grid */}
              <div className="books-grid">
                {gvLoading ? (
                  <div className="loading">Loading library...</div>
                ) : gvError ? (
                  <div className="empty-state">{gvError}</div>
                ) : (gvArticles || []).length === 0 ? (
                  <div className="empty-state">No materials found. Try different filters.</div>
                ) : (
                  gvArticles
                    .filter(a => gvFilterLevel === 'all' || a.cefr_level === gvFilterLevel)
                    .filter(a => gvFilterGenre === 'all' || a.genre === gvFilterGenre)
                    .filter(a => {
                      if (gvFilterReadTime === '0-3') return (a.estimated_read_time || 0) <= 3;
                      if (gvFilterReadTime === '4-6') return (a.estimated_read_time || 0) >= 4 && (a.estimated_read_time || 0) <= 6;
                      if (gvFilterReadTime === '>6') return (a.estimated_read_time || 0) > 6;
                      return true;
                    })
                    .filter(a => gvSearch.trim() === '' || (a.title || '').toLowerCase().includes(gvSearch.toLowerCase()))
                    .map(item => (
                      <div key={item.id} className="book-card">
                        <div 
                          className="book-cover"
                          style={{
                            backgroundImage: item.image_url ? `url(${getImageUrl(item.image_url) || item.image_url})` : 
                                            item.cover_image ? `url(${item.cover_image})` : 
                                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          }}
                        />
                        <div className="book-meta">
                          <div className="book-header">
                            <span className={`level-badge level-${item.cefr_level}`}>{item.cefr_level}</span>
                            <span className="read-time">{item.estimated_read_time || '—'} min</span>
                          </div>
                          <h4 className="book-title">{item.title}</h4>
                          <p className="book-desc">{cleanHtmlForPreview(item.content || '').slice(0, 120)}{(item.content || '').length > 120 ? '…' : ''}</p>
                        </div>
                        <div className="book-actions">
                          <button className="user-course-btn primary" onClick={async () => {
                            try {
                              const res = await fetch(`${GENERAL_VOCAB_API}/articles/${item.id}`);
                              if (!res.ok) throw new Error('Failed to load article');
                              const art = await res.json();
                              // Use the same article modal system as Educational Levels
                              openArticleModal(art);
                              // Track progress session
                              await fetch(`${GENERAL_VOCAB_API}/reading-progress`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ user_id: DEMO_USER_ID, article_id: art.id, time_spent_minutes: 0, words_clicked: 0, completion_percentage: 0, is_completed: 0 })
                              }).catch(()=>{});
                            } catch (e) {
                              alert('Unable to open reader.');
                            }
                          }}><i className="material-icons">play_arrow</i> Read</button>
                          <button className="user-course-btn" onClick={() => openGvDetailsModal(item)}><i className="material-icons">info</i> Details</button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* My Reads: simple placeholders */}
            <div className="user-section">
              <div className="user-section-header">
                <div className="user-section-title"><i className="material-icons">history</i> My Reads</div>
              </div>
              <div className="books-subgrid">
                <div className="books-pill">Currently Reading: 0</div>
                <div className="books-pill">Finished: 0</div>
                <div className="books-pill">Saved for Later: {gvSaved.length}</div>
              </div>
            </div>


          </div>
        );
      case 'vocabulary':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.vocabulary.title')}</h1>
              <p className="user-content-subtitle">{t('user.vocabulary.subtitle')}</p>
            </div>
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay 
                message={t('user.vocabulary.comingSoon.message')}
                title={t('user.vocabulary.comingSoon.title')}
                onClose={() => setActiveSection('dashboard')} 
              />
              <div className="user-section-header">
                <div className="user-section-title">
                  <i className="material-icons">book</i>
                  {t('user.vocabulary.myVocabulary')}
                </div>
              </div>
              <div className="user-vocabulary-grid">
                <div className="user-vocabulary-card">
                  <div className="user-vocabulary-word">Bonjour</div>
                  <div className="user-vocabulary-meaning">{t('user.vocabulary.words.bonjour.meaning')}</div>
                  <div className="user-vocabulary-example">
                    {t('user.vocabulary.words.bonjour.example')}
                  </div>
                </div>
                <div className="user-vocabulary-card">
                  <div className="user-vocabulary-word">Gracias</div>
                  <div className="user-vocabulary-meaning">{t('user.vocabulary.words.gracias.meaning')}</div>
                  <div className="user-vocabulary-example">
                    {t('user.vocabulary.words.gracias.example')}
                  </div>
                </div>
                <div className="user-vocabulary-card">
                  <div className="user-vocabulary-word">Danke</div>
                  <div className="user-vocabulary-meaning">{t('user.vocabulary.words.danke.meaning')}</div>
                  <div className="user-vocabulary-example">
                    {t('user.vocabulary.words.danke.example')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'practice':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.practice.title')}</h1>
              <p className="user-content-subtitle">{t('user.practice.subtitle')}</p>
            </div>
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay 
                message={t('user.vocabulary.comingSoon.message')}
                title={t('user.vocabulary.comingSoon.title')}
                onClose={() => setActiveSection('dashboard')} 
              />
              <div className="user-section-header">
                <div className="user-section-title">
                  <i className="material-icons">fitness_center</i>
                  {t('user.practice.practiceActivities')}
                </div>
              </div>
              <div className="user-practice-grid">
                <div className="user-practice-card">
                  <div className="user-practice-header">
                    <div className="user-practice-icon">
                      <i className="material-icons">quiz</i>
                    </div>
                    <div className="user-practice-info">
                      <h3>{t('user.practice.grammarQuiz.title')}</h3>
                      <p>{t('user.practice.grammarQuiz.description')}</p>
                    </div>
                  </div>
                  <div className="user-practice-stats">
                    <div className="user-practice-stat">
                      <div className="user-practice-stat-value">85%</div>
                      <div className="user-practice-stat-label">{t('user.practice.accuracy')}</div>
                    </div>
                    <div className="user-practice-stat">
                      <div className="user-practice-stat-value">12</div>
                      <div className="user-practice-stat-label">{t('user.practice.questions')}</div>
                    </div>
                  </div>
                  <button className="user-course-btn primary">
                    <i className="material-icons">play_arrow</i>
                    {t('user.practice.grammarQuiz.startButton')}
                  </button>
                </div>
                <div className="user-practice-card">
                  <div className="user-practice-header">
                    <div className="user-practice-icon">
                      <i className="material-icons">record_voice_over</i>
                    </div>
                    <div className="user-practice-info">
                      <h3>{t('user.practice.speakingPractice.title')}</h3>
                      <p>{t('user.practice.speakingPractice.description')}</p>
                    </div>
                  </div>
                  <div className="user-practice-stats">
                    <div className="user-practice-stat">
                      <div className="user-practice-stat-value">92%</div>
                      <div className="user-practice-stat-label">{t('user.practice.accuracy')}</div>
                    </div>
                    <div className="user-practice-stat">
                      <div className="user-practice-stat-value">8</div>
                      <div className="user-practice-stat-label">{t('user.practice.exercises')}</div>
                    </div>
                  </div>
                  <button className="user-course-btn primary">
                    <i className="material-icons">play_arrow</i>
                    {t('user.practice.speakingPractice.startButton')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'talk':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.freeTalk.title')}</h1>
              <p className="user-section-subtitle">{t('user.freeTalk.subtitle')}</p>
            </div>

            {/* Talk Navigation Tabs */}
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay 
                message={t('user.vocabulary.comingSoon.message')}
                title={t('user.vocabulary.comingSoon.title')}
                onClose={() => setActiveSection('dashboard')} 
                className="higher-position" 
              />
              <div className="talk-tabs">
                <button 
                  className={`talk-tab ${activeTalkTab === 'communication-hub' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('communication-hub')}
                >
                  <i className="material-icons">home</i>
                  {t('user.freeTalk.communicationHub')}
                </button>
                <button 
                  className={`talk-tab ${activeTalkTab === 'create-room' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('create-room')}
                >
                  <i className="material-icons">add_circle</i>
                  {t('user.freeTalk.createRoom')}
                </button>
                <button 
                  className={`talk-tab ${activeTalkTab === 'my-activity' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('my-activity')}
                >
                  <i className="material-icons">history</i>
                  {t('user.freeTalk.myActivity')}
                </button>
                <button 
                  className={`talk-tab ${activeTalkTab === 'profile-settings' ? 'active' : ''}`}
                  onClick={() => setActiveTalkTab('profile-settings')}
                >
                  <i className="material-icons">settings</i>
                  {t('user.freeTalk.profileSettings')}
                </button>
              </div>

              {/* Talk Content */}
              <div className="talk-content">
                {/* Communication Hub Section */}
                {activeTalkTab === 'communication-hub' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">home</i>
                        {t('user.freeTalk.communicationHub')}
                      </div>
                      <p className="user-section-subtitle">{t('user.freeTalk.joinActiveRooms')}</p>
                    </div>

                    {/* Search and Filter */}
                    <div className="search-filter-container">
                      <div className="search-box">
                        <i className="material-icons">search</i>
                        <input 
                          type="text" 
                          placeholder={t('user.freeTalk.searchPlaceholder')} 
                          value={roomSearch}
                          onChange={(e) => setRoomSearch(e.target.value)}
                        />
                      </div>
                      <div className="filter-options">
                        <select 
                          value={languageFilter}
                          onChange={(e) => setLanguageFilter(e.target.value)}
                        >
                          <option value="">{t('user.freeTalk.filters.allLanguages')}</option>
                          <option value="english">English</option>
                          <option value="french">French</option>
                          <option value="arabic">Arabic</option>
                          <option value="spanish">Spanish</option>
                          <option value="german">German</option>
                          <option value="chinese">Chinese</option>
                        </select>
                        <select 
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                        >
                          <option value="">{t('user.freeTalk.filters.allTypes')}</option>
                          <option value="voice">{t('user.freeTalk.filters.voiceOnly')}</option>
                          <option value="text">{t('user.freeTalk.filters.textOnly')}</option>
                          <option value="both">{t('user.freeTalk.filters.voiceText')}</option>
                        </select>
                        <select 
                          value={levelFilterTalk}
                          onChange={(e) => setLevelFilterTalk(e.target.value)}
                        >
                          <option value="">{t('user.freeTalk.filters.allLevels')}</option>
                          <option value="beginner">{t('user.freeTalk.filters.beginner')}</option>
                          <option value="intermediate">{t('user.freeTalk.filters.intermediate')}</option>
                          <option value="advanced">{t('user.freeTalk.filters.advanced')}</option>
                        </select>
                        <select 
                          value={categoryFilterTalk}
                          onChange={(e) => setCategoryFilterTalk(e.target.value)}
                        >
                          <option value="">{t('user.freeTalk.filters.allCategories')}</option>
                          <option value="ielts">IELTS</option>
                          <option value="grammar">Grammar</option>
                          <option value="bac">Bac</option>
                          <option value="fun">Fun Talk</option>
                          <option value="business">Business</option>
                          <option value="general">General</option>
                        </select>
                      </div>
                    </div>

                    {/* Active Rooms Grid */}
                    <div className="rooms-grid">
                      {filteredRooms.length === 0 ? (
                        <div className="no-results">
                          <i className="material-icons">search_off</i>
                          <h3>{t('user.freeTalk.noRoomsFound')}</h3>
                          <p>{t('user.freeTalk.tryAdjusting')}</p>
                        </div>
                      ) : (
                        filteredRooms.map(room => (
                          <div key={room.id} className="room-card">
                            <div className="room-header">
                              <div>
                                <h3 className="room-title">{room.title}</h3>
                                <div className="room-meta">
                                  <span>{room.type === 'voice' ? '🎧' : room.type === 'text' ? '💬' : '🗣️'} {room.type.charAt(0).toUpperCase() + room.type.slice(1)}</span>
                                  <span>🌍 {room.language}</span>
                                  <span>📚 {room.level.charAt(0).toUpperCase() + room.level.slice(1)}</span>
                                </div>
                              </div>
                              {room.isPrivate && <span className="badge">🔒 {t('user.freeTalk.private')}</span>}
                            </div>
                            
                            <div className="room-tags">
                              {room.tags.map(tag => (
                                <span key={tag} className="room-tag">{tag}</span>
                              ))}
                            </div>
                            
                            <div className="room-creator">
                              <div className="creator-avatar">{room.creator.avatar}</div>
                              <span>{t('user.freeTalk.createdBy')} {room.creator.name} {room.creator.country}</span>
                            </div>
                            
                            <div className="room-stats">
                              <div className="room-users">
                                <span>👥 {room.users}/{room.maxUsers} online</span>
                              </div>
                            </div>
                            
                            <div className="room-actions">
                              <button className="user-course-btn primary" onClick={() => joinRoom(room.id)}>
                                {t('user.freeTalk.joinRoom')}
                              </button>
                              <button className="user-course-btn" onClick={() => reportRoom(room.id)}>
                                Report
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                {/* Create Room Section */}
                {activeTalkTab === 'create-room' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">add_circle</i>
                        Create a Room
                      </div>
                      <p className="user-section-subtitle">Start your own practice space and invite others to join</p>
                    </div>

                    <div className="create-room-form">
                      <form onSubmit={handleCreateRoomSubmit}>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="roomTitle" className="required">Room Title *</label>
                            <input 
                              type="text" 
                              id="roomTitle"
                              value={createRoomForm.title}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="e.g., Engineering Vocabulary Practice" 
                              required 
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="roomLanguage" className="required">Language *</label>
                            <select 
                              id="roomLanguage"
                              value={createRoomForm.language}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, language: e.target.value }))}
                              required
                            >
                              <option value="">Select Language</option>
                              <option value="english">English</option>
                              <option value="french">French</option>
                              <option value="arabic">Arabic</option>
                              <option value="spanish">Spanish</option>
                              <option value="german">German</option>
                              <option value="chinese">Chinese</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="roomType" className="required">Room Type *</label>
                            <select 
                              id="roomType"
                              value={createRoomForm.type}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, type: e.target.value }))}
                              required
                            >
                              <option value="">Select Type</option>
                              <option value="voice">Voice Only</option>
                              <option value="text">Text Only</option>
                              <option value="both">Voice & Text</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="roomCategory" className="required">Category *</label>
                            <select 
                              id="roomCategory"
                              value={createRoomForm.category}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, category: e.target.value }))}
                              required
                            >
                              <option value="">Select Category</option>
                              <option value="ielts">IELTS</option>
                              <option value="grammar">Grammar</option>
                              <option value="bac">Bac</option>
                              <option value="fun">Fun Talk</option>
                              <option value="business">Business</option>
                              <option value="general">General</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="roomLevel">Level</label>
                            <select 
                              id="roomLevel"
                              value={createRoomForm.level}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, level: e.target.value }))}
                            >
                              <option value="any">Any Level</option>
                              <option value="beginner">Beginner</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="advanced">Advanced</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="maxUsers">Max Users</label>
                            <input 
                              type="number" 
                              id="maxUsers"
                              value={createRoomForm.maxUsers}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, maxUsers: parseInt(e.target.value) }))}
                              min="2" 
                              max="20" 
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="roomTags">Tags</label>
                          <input 
                            type="text" 
                            id="roomTags"
                            value={createRoomForm.tags}
                            onChange={(e) => setCreateRoomForm(prev => ({ ...prev, tags: e.target.value }))}
                            placeholder="e.g., #Bac #Grammar #IELTS (separate with spaces)" 
                          />
                          <div className="form-help">Enter tags starting with # and separate with spaces</div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="roomDescription">Description</label>
                          <textarea 
                            id="roomDescription"
                            value={createRoomForm.description}
                            onChange={(e) => setCreateRoomForm(prev => ({ ...prev, description: e.target.value }))}
                            rows="3" 
                            placeholder="Describe what this room is about..."
                          ></textarea>
                        </div>

                        <div className="form-group">
                          <label className="checkbox-label">
                            <input 
                              type="checkbox" 
                              id="isPrivateRoom"
                              checked={createRoomForm.isPrivate}
                              onChange={(e) => setCreateRoomForm(prev => ({ ...prev, isPrivate: e.target.checked }))}
                            />
                            Make this room private (requires invite code)
                          </label>
                        </div>

                        <div className="form-actions">
                          <button type="submit" className="form-btn primary">
                            <i className="material-icons">add_circle</i>
                            Create Room
                          </button>
                          <button type="button" className="form-btn secondary" onClick={() => setActiveTalkTab('communication-hub')}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* My Activity Section */}
                {activeTalkTab === 'my-activity' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">history</i>
                        My Activity
                      </div>
                      <p className="user-section-subtitle">Track your participation and manage your rooms</p>
                    </div>

                    {/* Activity Tabs */}
                    <div className="activity-tabs">
                      <button className="activity-tab active">
                        <i className="material-icons">room</i>
                        My Rooms
                        <span className="tab-count">{myRooms.length}</span>
                      </button>
                      <button className="activity-tab">
                        <i className="material-icons">schedule</i>
                        Recently Joined
                        <span className="tab-count">{recentRooms.length}</span>
                      </button>
                      <button className="activity-tab">
                        <i className="material-icons">forum</i>
                        My Posts
                        <span className="tab-count">{myPosts.length}</span>
                      </button>
                      <button className="activity-tab">
                        <i className="material-icons">report</i>
                        Reported Users
                        <span className="tab-count">{reportedUsers.length}</span>
                      </button>
                    </div>

                    {/* My Rooms */}
                    <div className="my-rooms-list">
                      {myRooms.map(room => (
                        <div key={room.id} className="my-room-item">
                          <div className="room-info">
                            <h3>{room.title}</h3>
                            <div className="room-meta">
                              <span>🌍 {room.language}</span>
                              <span>{room.type === 'voice' ? '🎧' : room.type === 'text' ? '💬' : '🗣️'} {room.type}</span>
                              <span>📚 {room.level}</span>
                              <span>👥 {room.users}/{room.maxUsers}</span>
                            </div>
                          </div>
                          <div className="room-status">
                            <span className={`status-badge ${room.isActive ? 'active' : 'inactive'}`}>
                              {room.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="room-actions">
                            <button className="user-course-btn primary">
                              <i className="material-icons">play_arrow</i>
                              Join
                            </button>
                            <button className="user-course-btn" onClick={() => editRoom(room.id)}>
                              <i className="material-icons">edit</i>
                              Edit
                            </button>
                            <button className="user-course-btn danger" onClick={() => deleteRoom(room.id)}>
                              <i className="material-icons">delete</i>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Profile Settings Section */}
                {activeTalkTab === 'profile-settings' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">settings</i>
                        Profile Settings
                      </div>
                      <p className="user-section-subtitle">Customize your profile and preferences</p>
                    </div>

                    <div className="profile-settings-form">
                      <form onSubmit={(e) => {
                        e.preventDefault();
                        showNotification('Settings saved successfully!', 'success');
                      }}>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="username">Username *</label>
                            <input type="text" id="username" defaultValue="JohnDoe" required />
                          </div>
                          <div className="form-group">
                            <label htmlFor="country">Country</label>
                            <select id="country">
                              <option value="">Select Country</option>
                              <option value="algeria" selected>🇩🇿 Algeria</option>
                              <option value="usa">🇺🇸 United States</option>
                              <option value="uk">🇬🇧 United Kingdom</option>
                              <option value="france">🇫🇷 France</option>
                              <option value="spain">🇪🇸 Spain</option>
                              <option value="germany">🇩🇪 Germany</option>
                              <option value="china">🇨🇳 China</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="nativeLanguage">Native Language</label>
                            <select id="nativeLanguage">
                              <option value="">Select Language</option>
                              <option value="arabic" selected>Arabic</option>
                              <option value="english">English</option>
                              <option value="french">French</option>
                              <option value="spanish">Spanish</option>
                              <option value="german">German</option>
                              <option value="chinese">Chinese</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="learningLanguage">Learning Language</label>
                            <select id="learningLanguage">
                              <option value="">Select Language</option>
                              <option value="english" selected>English</option>
                              <option value="french">French</option>
                              <option value="arabic">Arabic</option>
                              <option value="spanish">Spanish</option>
                              <option value="german">German</option>
                              <option value="chinese">Chinese</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="languageLevel">Language Level</label>
                          <select id="languageLevel">
                            <option value="">Select Level</option>
                            <option value="beginner">Beginner</option>
                            <option value="intermediate" selected>Intermediate</option>
                            <option value="advanced">Advanced</option>
                          </select>
                        </div>

                        <button type="submit" className="btn-primary">Save Settings</button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'explore-courses': {
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.exploreCourses.title')}</h1>
              <p className="user-content-subtitle">{t('user.exploreCourses.subtitle')}</p>
            </div>

            {selectedExploreLanguage == null ? (
              <div className="user-content-header language-section-container">
                <p className="language-title">Choose a language</p>
                <div className="language-grid">
                  {EXPLORE_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      className="language-card"
                      onClick={() => setSelectedExploreLanguage(lang.code)}
                    >
                      <img
                        src={`${FLAG_IMG_BASE}/${lang.flagCountryCode || lang.code}.png`}
                        alt=""
                        className="language-card-flag-img"
                      />
                      <span className="language-card-label">{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="explore-modules-header">
                  <button
                    type="button"
                    className="back-btn explore-back-to-languages"
                    onClick={() => setSelectedExploreLanguage(null)}
                  >
                    <i className="material-icons">arrow_back</i>
                    <span>Change language</span>
                    {EXPLORE_LANGUAGES.find(l => l.code === selectedExploreLanguage) && (
                      <img
                        src={`${FLAG_IMG_BASE}/${EXPLORE_LANGUAGES.find(l => l.code === selectedExploreLanguage).flagCountryCode || selectedExploreLanguage}.png`}
                        alt=""
                        className="explore-back-flag-img"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </div>

                <div className="user-section">
                  <div className="explore-tabs">
                    {visibleExploreCourses.map(course => (
                      <button
                        key={course.id}
                        className={`explore-tab ${activeExploreTab === course.id ? 'active' : ''}`}
                        onClick={() => setActiveExploreTab(course.id)}
                      >
                        <span className="explore-tab-icon">{course.icon}</span>
                        <span className="explore-tab-text">{course.title}</span>
                      </button>
                    ))}
                  </div>

                  <div className="explore-content">
                    {visibleExploreCourses.map(course => (
                      <div
                        key={course.id}
                        className={`explore-category ${activeExploreTab === course.id ? 'active' : ''}`}
                      >
                        <div className="user-section" style={{ position: 'relative' }}>
                          {!unlockExploreCourses && (course.id === 'general-vocabulary' ||
                            course.id === 'academic-vocabulary-esp' ||
                            course.id === 'dictation' ||
                            course.id === 'reading' ||
                            course.id === 'virtual-classroom') && activeExploreTab === course.id && (
                            <ComingSoonOverlay onClose={() => setActiveExploreTab('educational-levels-vocabulary')} />
                          )}
                          <div className="user-section-header">
                            <div className="user-section-title">
                              <span className="explore-title-icon">{course.icon}</span>
                              {course.title}
                              {course.badge && (
                                <span className={`explore-card-badge ${course.badge.type === 'new' ? 'explore-card-badge--new' : 'explore-card-badge--progress'}`}>
                                  {course.badge.type === 'new' ? 'New' : `${course.badge.value}%`}
                                </span>
                              )}
                            </div>
                            <p className="user-section-subtitle">{course.description}</p>
                          </div>

                          <div className="explore-card-single">
                            <div className="explore-card">
                              <div className="explore-card-header">
                                <div className="explore-card-icon">{course.icon}</div>
                                <div className="explore-card-title-section">
                                  <h3 className="explore-card-title">{course.title}</h3>
                                  {course.badge && (
                                    <span className={`explore-card-badge-inline ${course.badge.type === 'new' ? 'explore-card-badge--new' : 'explore-card-badge--progress'}`}>
                                      {course.badge.type === 'new' ? 'New' : `${course.badge.value}%`}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="explore-card-description">{course.description}</p>
                              <div className="explore-card-features">
                                <h4>{t('user.exploreCourses.keyFeatures')}</h4>
                                <ul>
                                  {Array.isArray(course.features)
                                    ? course.features.map((feature, featureIndex) => (
                                        <li key={featureIndex}>
                                          {typeof feature === 'string' ? (
                                            feature
                                          ) : (
                                            <div className="feature-item">
                                              <div className="feature-title">{feature.title}</div>
                                              <div className="feature-description">{feature.description}</div>
                                            </div>
                                          )}
                                        </li>
                                      ))
                                    : null}
                                </ul>
                              </div>
                              <div className="explore-card-actions">
                                <button
                                  className="user-course-btn primary"
                                  onClick={
                                    course.id === 'educational-levels-vocabulary' ? openEducationalLevels :
                                    course.id === 'international-exam-vocabulary' ? openExamLevels :
                                    course.id === 'academic-vocabulary-esp' ? openESPVocabulary :
                                    course.id === 'general-vocabulary' ? () => setActiveSection('general-vocabulary') :
                                    course.id === 'reading' ? openReadingPaths :
                                    course.id === 'dictation' ? openDictationPaths :
                                    course.id === 'vocabulary-placement-test' ? () => setActiveSection('vocabulary-placement-test') :
                                    undefined
                                  }
                                >
                                  <i className="material-icons">launch</i>
                                  {t('user.exploreCourses.startLearning')}
                                </button>
                                <button className="user-course-btn">
                                  <i className="material-icons">info</i>
                                  {t('user.exploreCourses.learnMore')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      }
      case 'educational-levels': {
        if (yearLevelsFromApi && yearLevelsFromApi.length > 0) {
          const currentYearLevel = yearLevelsFromApi.find((yl) => yl.id === selectedScopedYearLevelId);
          const langNorm = selectedLearningLanguage ? String(selectedLearningLanguage).trim().toLowerCase() : '';
          const isGermanThirdYearHigh =
            langNorm === 'german' &&
            currentYearLevel &&
            currentYearLevel.school_level === 'High School' &&
            currentYearLevel.year_name === 'Third Year';
          const isFrenchThirdYearHigh =
            langNorm === 'french' &&
            currentYearLevel &&
            currentYearLevel.school_level === 'High School' &&
            currentYearLevel.year_name === 'Third Year';
          const isItalianThirdYearHigh =
            langNorm === 'italian' &&
            currentYearLevel &&
            currentYearLevel.school_level === 'High School' &&
            currentYearLevel.year_name === 'Third Year';
          const isSpanishThirdYearHigh =
            langNorm === 'spanish' &&
            currentYearLevel &&
            currentYearLevel.school_level === 'High School' &&
            currentYearLevel.year_name === 'Third Year';
          const isFrenchFourthYearMiddle =
            langNorm === 'french' &&
            currentYearLevel &&
            (currentYearLevel.school_level === 'Middle School' || currentYearLevel.school_level === 'middle-school') &&
            currentYearLevel.year_name === 'Fourth Year';

          const germanUnitTitlesByNumber = {
            1: 'Die Umweltprobleme',
            2: 'Die Massenmedien und Kommunikationsmittel',
            3: 'Jugendprobleme',
            4: 'Die deutschsprachigen Länder',
            5: 'Künstlerleben',
            6: 'Der technische Fortschritt'
          };
          const frenchUnitTitlesByNumber = {
            1: "Le texte d'histoire",
            2: "Le débat des idées",
            3: "L'appel"
          };
          const italianUnitTitlesByNumber = {
            1: 'La famiglia',
            2: "L'ambiente",
            3: 'La cucina',
            4: 'La moda',
            5: 'La tecnologia',
            6: 'La salute',
            7: 'Lo sport'
          };
          const spanishUnitTitlesByNumber = {
            1: 'El medio ambiente',
            2: 'La educación',
            3: 'La ciencia y la tecnología',
            4: 'La solidaridad',
            5: 'La salud'
          };
          const frenchFourthYearMiddleUnitTitlesByNumber = {
            1: 'Bienvenue dans ma région !',
            2: 'Gloire à nos ancêtres !',
            3: 'Oui à la culture !',
            4: 'Vivons en harmonie !',
            5: 'Non à la violence !',
            6: 'Protégeons la nature !',
            7: 'Agissons en écoresponsables !'
          };
          const unitsToShow = isFrenchThirdYearHigh ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 3) : (isItalianThirdYearHigh ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 7) : (isSpanishThirdYearHigh ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 5) : (isFrenchFourthYearMiddle ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 7) : units)));

          return (
            <>
            <div className="user-main-content" id="educational-levels-explore-section">
              <div className="user-content-header">
                <div className="user-content-header-top">
                  <button className="back-btn" onClick={closeEducationalLevels}>
                    <i className="material-icons">arrow_back</i>
                    <span>{t('user.exploreCourses.backToExplore')}</span>
                  </button>
                  <h1 className="user-content-title">{t('user.educationalVocabulary.pageTitle')}</h1>
                </div>
                <p className="user-content-subtitle">{t('user.educationalVocabulary.planSubtitle')}</p>
              </div>
              <div className="user-section">
                <div className="year-tabs">
                  {yearLevelsFromApi.map((yl) => (
                    <button
                      key={yl.id}
                      className={`year-tab ${selectedScopedYearLevelId === yl.id ? 'active' : ''}`}
                      onClick={() => setSelectedScopedYearLevelId(yl.id)}
                    >
                      {trSchoolName(yl.school_level)} – {trYearName(yl.year_name)}
                    </button>
                  ))}
                </div>
                {(unitsLoading || educationalScopedLoading) ? (
                  <div className="user-courses-loading"><p>{t('user.myCourses.loading') || 'Loading...'}</p></div>
                ) : units.length > 0 ? (
                  selectedUnitId == null ? (
                    <>
                      <div className="units-grid-header">
                        <p className="user-section-subtitle">{t('user.educationalVocabulary.selectUnit')}</p>
                      </div>
                      <div className="units-grid">
                        {unitsToShow.map((unit) => {
                          const displayTitle =
                            isGermanThirdYearHigh && germanUnitTitlesByNumber[unit.unit_number]
                              ? germanUnitTitlesByNumber[unit.unit_number]
                              : (isFrenchThirdYearHigh && frenchUnitTitlesByNumber[unit.unit_number])
                                ? frenchUnitTitlesByNumber[unit.unit_number]
                                : (isItalianThirdYearHigh && italianUnitTitlesByNumber[unit.unit_number])
                                  ? italianUnitTitlesByNumber[unit.unit_number]
                                  : (isSpanishThirdYearHigh && spanishUnitTitlesByNumber[unit.unit_number])
                                    ? spanishUnitTitlesByNumber[unit.unit_number]
                                    : (isFrenchFourthYearMiddle && frenchFourthYearMiddleUnitTitlesByNumber[unit.unit_number])
                                      ? frenchFourthYearMiddleUnitTitlesByNumber[unit.unit_number]
                                      : unit.title;

                          return (
                            <div
                              key={unit.id}
                              className="unit-card"
                              onClick={() => {
                                setSelectedUnitId(unit.id);
                                const slug = scopedSchoolSlugRef.current;
                                const yearName = scopedYearNameRef.current;
                                const lang = (selectedLearningLanguage || '').toLowerCase();
                                const unitTitleForApi = (lang === 'german' || lang === 'french' || lang === 'italian' || lang === 'spanish') ? displayTitle : null;
                                if (slug && yearName) loadEducationalContent(slug, yearName, unit.id, unitTitleForApi);
                              }}
                            >
                              {unit.image_url ? (
                                <img src={getImageUrl(unit.image_url) || unit.image_url} alt={displayTitle} className="unit-card-image" />
                              ) : (() => {
                                const banner = getUnitCardBanner(unit.id, unitCardBanners, selectedLearningLanguage);
                                return banner?.image_url ? (
                                  <img src={getImageUrl(banner.image_url) || banner.image_url} alt={displayTitle} className="unit-card-image" />
                                ) : (
                                  <div className="unit-card-placeholder" />
                                );
                              })()}
                              <div className="unit-card-body">
                                <span className="unit-card-number">{t('user.educationalVocabulary.unitLabel', { number: unit.unit_number })}</span>
                                <h3 className="unit-card-title">{displayTitle}</h3>
                                {unit.streams && unit.streams.length > 0 && (
                                  <div className="unit-card-streams">
                                    {unit.streams.slice(0, 4).map((st) => (
                                      <span key={st.id} className="unit-stream-tag">{st.name_en || st.code}</span>
                                    ))}
                                    {unit.streams.length > 4 && <span className="unit-stream-tag">+{unit.streams.length - 4}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="units-grid-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <button
                          className="back-btn"
                          onClick={() => {
                            setSelectedUnitId(null);
                            setArticles([]);
                          }}
                        >
                          <i className="material-icons">arrow_back</i>
                          <span>{t('user.educationalVocabulary.backToUnits')}</span>
                        </button>
                        <span className="units-current-title">
                          {(() => {
                            const u = units.find((u) => u.id === selectedUnitId);
                            if (!u) return 'Unit';
                            if (isGermanThirdYearHigh && germanUnitTitlesByNumber[u.unit_number]) {
                              return germanUnitTitlesByNumber[u.unit_number];
                            }
                            if (isFrenchThirdYearHigh && frenchUnitTitlesByNumber[u.unit_number]) {
                              return frenchUnitTitlesByNumber[u.unit_number];
                            }
                            return u.title;
                          })()}
                        </span>
                      </div>
                      {exploreExamSequentialNotice && (
                        <div className="reading-exam-sequential-notice" role="status" style={{ marginBottom: 16 }}>
                          <i className="material-icons reading-exam-sequential-notice__lock" aria-hidden="true">lock</i>
                          <span>{exploreExamSequentialNotice}</span>
                        </div>
                      )}
                      {articles.length > 0 ? (
                        <div className="educational-content-grid">
                          {articles.map((article, index) => {
                            const masteryLocked = article.educational_sequential_access === 'mastery_locked';
                            return (
                            <div
                              key={article.id != null ? article.id : index}
                              className={`educational-content-card article-card${masteryLocked ? ' article-card--mastery-locked' : ''}`}
                              title={masteryLocked ? t('user.educationalSequential.masteryLockTooltip') : undefined}
                              onClick={() => {
                                if (masteryLocked) showExamMasteryLockModal('educational');
                                else openArticleModal(article);
                              }}
                            >
                              <div className="article-card-cover" style={{ position: 'relative', width: '100%', height: '50%' }}>
                                {masteryLocked && (
                                  <span
                                    className="article-card-lock-badge article-card-lock-badge--mastery material-icons"
                                    title={t('user.educationalSequential.masteryLockTooltip')}
                                    aria-hidden="true"
                                  >
                                    lock
                                  </span>
                                )}
                                {article.image_url ? (
                                  <img src={getImageUrl(article.image_url) || article.image_url} alt={article.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px 12px 0 0' }} />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px 12px 0 0' }} />
                                )}
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', padding: '0 8px 4px 8px', zIndex: 2 }}>
                                  <span style={{ background: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 6px', borderRadius: '8px', fontSize: '10px' }}>{article.unit || 'Article'}</span>
                                  <span style={{ background: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 6px', borderRadius: '8px', fontSize: '10px' }}>{article.estimated_read_time ? t('user.examVocabulary.minRead', { minutes: article.estimated_read_time }) : t('user.examVocabulary.readingArticle')}</span>
                                </div>
                              </div>
                              <div className="educational-content-header">
                                <h3 className="educational-content-title">{article.title}</h3>
                                <p className="educational-content-description">{cleanHtmlForPreview(article.content || '', 120)}</p>
                              </div>
                              <div className="educational-content-actions">
                                <button
                                  className="user-course-btn primary"
                                  disabled={masteryLocked}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (masteryLocked) showExamMasteryLockModal('educational');
                                    else openArticleModal(article);
                                  }}
                                >
                                  <i className="material-icons">visibility</i>
                                  {t('user.examVocabulary.readArticle')}
                                </button>
                                <button className="user-course-btn" onClick={(e) => { e.stopPropagation(); openDetailsModal(article); }}>
                                  <i className="material-icons">info</i>
                                  {t('user.examVocabulary.details')}
                                </button>
                              </div>
                              <div className="article-metadata">
                                <div className="metadata-item"><span className="material-icons">school</span><span>{trSchoolName(article.school || 'High School')}</span></div>
                                <div className="metadata-item"><span className="material-icons">calendar_today</span><span>{trYearName(article.year || 'Third Year')}</span></div>
                                <div className="metadata-item"><span className="material-icons">schedule</span><span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : ''}</span></div>
                              </div>
                            </div>
                          ); })}
                        </div>
                      ) : (
                        <div className="user-courses-loading"><p>{t('user.educationalVocabulary.loadingArticles')}</p></div>
                      )}
                    </>
                  )
                ) : educationalContentScoped.length > 0 ? (
                  <div className="educational-content-grid">
                    {educationalContentScoped.map((item) => {
                      const masteryLocked = item.educational_sequential_access === 'mastery_locked';
                      return (
                      <div
                        key={item.id}
                        className={`educational-content-card article-card${masteryLocked ? ' article-card--mastery-locked' : ''}`}
                        title={masteryLocked ? t('user.educationalSequential.masteryLockTooltip') : undefined}
                        onClick={() => {
                          if (masteryLocked) showExamMasteryLockModal('educational');
                          else openArticleModal(item);
                        }}
                      >
                        <div className="article-card-cover" style={{ position: 'relative', width: '100%', height: '50%' }}>
                          {masteryLocked && (
                            <span
                              className="article-card-lock-badge article-card-lock-badge--mastery material-icons"
                              title={t('user.educationalSequential.masteryLockTooltip')}
                              aria-hidden="true"
                            >
                              lock
                            </span>
                          )}
                          {item.image_url ? (
                            <img src={getImageUrl(item.image_url) || item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px 12px 0 0' }} onError={(e) => { if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'; e.target.style.display = 'none'; }} />
                          ) : null}
                          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '12px 12px 0 0', display: item.image_url ? 'none' : 'block' }} />
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', padding: '0 8px 4px 8px', zIndex: 2 }}>
                            <span style={{ background: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 6px', borderRadius: '8px', fontSize: '10px' }}>{item.unit || item.difficulty || 'Intermediate'}</span>
                            <span style={{ background: 'rgba(0,0,0,0.7)', color: 'white', padding: '2px 6px', borderRadius: '8px', fontSize: '10px' }}>{item.estimated_read_time ? t('user.examVocabulary.minRead', { minutes: item.estimated_read_time }) : t('user.examVocabulary.readingArticle')}</span>
                          </div>
                        </div>
                        <div className="educational-content-header">
                          <h3 className="educational-content-title">{item.title}</h3>
                          <p className="educational-content-description">{cleanHtmlForPreview(item.content || '', 120)}</p>
                        </div>
                        <div className="educational-content-actions">
                          <button
                            className="user-course-btn primary"
                            disabled={masteryLocked}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (masteryLocked) showExamMasteryLockModal('educational');
                              else openArticleModal(item);
                            }}
                          >
                            <i className="material-icons">visibility</i>
                            {t('user.examVocabulary.readArticle')}
                          </button>
                          <button className="user-course-btn" onClick={(e) => { e.stopPropagation(); openDetailsModal(item); }}>
                            <i className="material-icons">info</i>
                            {t('user.examVocabulary.details')}
                          </button>
                        </div>
                        <div className="article-metadata">
                          <div className="metadata-item"><span className="material-icons">school</span><span>{trSchoolName(item.school || 'High School')}</span></div>
                          <div className="metadata-item"><span className="material-icons">calendar_today</span><span>{trYearName(item.year || 'First Year')}</span></div>
                          <div className="metadata-item"><span className="material-icons">schedule</span><span>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span></div>
                        </div>
                      </div>
                    ); })}
                  </div>
                ) : (
                  <div className="user-courses-empty">
                    <p>{t('user.educationalVocabulary.noContentYet')}</p>
                  </div>
                )}
              </div>
            </div>
            <ArticleDetailsModal
              article={detailsArticle}
              isOpen={showDetailsModal}
              onClose={closeDetailsModal}
              onStartReading={(article) => { closeDetailsModal(); openArticleModal(article); }}
              isLocked={detailsArticleIsLocked}
              onUnlockClick={() => { closeDetailsModal(); setShowUnlockModal(true); }}
              userId={currentUser?.id}
            />
          </>
          );
        }
        // Explore path: no yearLevelsFromApi → show hierarchical High/Middle/Primary + years (like International Exam)
        return (
          <>
          <div className="user-main-content">
            <div className="user-content-header">
              <div className="user-content-header-top">
                <button className="back-btn" onClick={closeEducationalLevels}>
                  <i className="material-icons">arrow_back</i>
                  <span>{t('user.exploreCourses.backToExplore')}</span>
                </button>
                <h1 className="user-content-title">{t('user.educationalVocabulary.pageTitle')}</h1>
              </div>
              <p className="user-content-subtitle">{t('user.educationalVocabulary.pageSubtitle')}</p>
            </div>

            {/* School Level Tabs */}
            <div className="user-section">
              <div className="educational-tabs">
                {schoolLevels.map(level => (
                  <button
                    key={level.id}
                    className={`educational-tab ${activeSchoolLevel === level.id ? 'active' : ''}`}
                    onClick={() => handleSchoolLevelChange(level.id)}
                  >
                    <i className="material-icons">school</i>
                    {trSchoolName(level)}
                  </button>
                ))}
              </div>

              {/* Year Level Sub-tabs */}
              <div className="year-tabs">
                {schoolLevels.find(level => level.id === activeSchoolLevel)?.years.map((year, index) => (
                  <button
                    key={index}
                    className={`year-tab ${activeYearLevel === `${activeSchoolLevel}-${index + 1}` ? 'active' : ''}`}
                    onClick={() => handleYearLevelChange(`${activeSchoolLevel}-${index + 1}`, year)}
                  >
                    {trYearName(year)}
                  </button>
                ))}
              </div>

              {/* Educational Content */}
              <div className="educational-content">
                <div className="user-section">
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">library_books</i>
                      {trSchoolName(schoolLevels.find(level => level.id === activeSchoolLevel))} - {trYearName(schoolLevels.find(level => level.id === activeSchoolLevel)?.years.find((_, index) => `${activeSchoolLevel}-${index + 1}` === activeYearLevel))}
                    </div>
                    <p className="user-section-subtitle">{t('user.educationalVocabulary.browseYear')}</p>
                  </div>

                  {/* Unit Cards: when backend has units for this school+year, show units first */}
                  {unitsLoading ? (
                    <div className="user-courses-loading"><p>{t('user.myCourses.loading') || 'Loading...'}</p></div>
                  ) : units.length > 0 ? (
                    selectedUnitId == null ? (
                      <>
                        <div className="units-grid-header">
                          <p className="user-section-subtitle">{t('user.educationalVocabulary.selectUnit')}</p>
                        </div>
                        <div className="units-grid">
                          {(() => {
                            const langNorm = selectedLearningLanguage ? String(selectedLearningLanguage).trim().toLowerCase() : '';
                            const currentYearName = schoolLevels.find(level => level.id === activeSchoolLevel)?.years.find((_, idx) => `${activeSchoolLevel}-${idx + 1}` === activeYearLevel);
                            const isGermanThirdYearHigh = langNorm === 'german' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                            const isFrenchThirdYearHigh = langNorm === 'french' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                            const isItalianThirdYearHigh = langNorm === 'italian' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                            const isSpanishThirdYearHigh = langNorm === 'spanish' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                            const isFrenchFourthYearMiddle = langNorm === 'french' && activeSchoolLevel === 'middle-school' && currentYearName && String(currentYearName).toLowerCase().includes('fourth');
                            const germanUnitTitlesByNumber = {
                              1: 'Die Umweltprobleme',
                              2: 'Die Massenmedien und Kommunikationsmittel',
                              3: 'Jugendprobleme',
                              4: 'Die deutschsprachigen Länder',
                              5: 'Künstlerleben',
                              6: 'Der technische Fortschritt'
                            };
                            const frenchUnitTitlesByNumber = {
                              1: "Le texte d'histoire",
                              2: "Le débat des idées",
                              3: "L'appel"
                            };
                            const italianUnitTitlesByNumber = {
                              1: 'La famiglia',
                              2: "L'ambiente",
                              3: 'La cucina',
                              4: 'La moda',
                              5: 'La tecnologia',
                              6: 'La salute',
                              7: 'Lo sport'
                            };
                            const spanishUnitTitlesByNumber = {
                              1: 'El medio ambiente',
                              2: 'La educación',
                              3: 'La ciencia y la tecnología',
                              4: 'La solidaridad',
                              5: 'La salud'
                            };
                            const frenchFourthYearMiddleUnitTitlesByNumber = {
                              1: 'Bienvenue dans ma région !',
                              2: 'Gloire à nos ancêtres !',
                              3: 'Oui à la culture !',
                              4: 'Vivons en harmonie !',
                              5: 'Non à la violence !',
                              6: 'Protégeons la nature !',
                              7: 'Agissons en écoresponsables !'
                            };
                            const unitsToShowExplore = isFrenchThirdYearHigh ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 3) : (isItalianThirdYearHigh ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 7) : (isSpanishThirdYearHigh ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 5) : (isFrenchFourthYearMiddle ? units.filter((u) => u.unit_number >= 1 && u.unit_number <= 7) : units)));
                            return unitsToShowExplore.map((unit) => {
                              const displayTitle = isGermanThirdYearHigh && germanUnitTitlesByNumber[unit.unit_number] ? germanUnitTitlesByNumber[unit.unit_number] : (isFrenchThirdYearHigh && frenchUnitTitlesByNumber[unit.unit_number]) ? frenchUnitTitlesByNumber[unit.unit_number] : (isItalianThirdYearHigh && italianUnitTitlesByNumber[unit.unit_number]) ? italianUnitTitlesByNumber[unit.unit_number] : (isSpanishThirdYearHigh && spanishUnitTitlesByNumber[unit.unit_number]) ? spanishUnitTitlesByNumber[unit.unit_number] : (isFrenchFourthYearMiddle && frenchFourthYearMiddleUnitTitlesByNumber[unit.unit_number]) ? frenchFourthYearMiddleUnitTitlesByNumber[unit.unit_number] : unit.title;
                              return (
                            <div
                              key={unit.id}
                              className="unit-card"
                              onClick={() => {
                                setSelectedUnitId(unit.id);
                                const yearVal = schoolLevels.find(level => level.id === activeSchoolLevel)?.years.find((_, index) => `${activeSchoolLevel}-${index + 1}` === activeYearLevel);
                                const yearName = (yearVal && typeof yearVal === 'object' && yearVal.key != null) ? yearVal.key : (yearVal || null);
                                const lang = (selectedLearningLanguage || '').toLowerCase();
                                const unitTitleForApi = (lang === 'german' || lang === 'french' || lang === 'italian' || lang === 'spanish') ? displayTitle : null;
                                if (yearName) loadEducationalContent(activeSchoolLevel, yearName, unit.id, unitTitleForApi);
                              }}
                            >
                              {unit.image_url ? (
                                <img src={getImageUrl(unit.image_url) || unit.image_url} alt={displayTitle} className="unit-card-image" />
                              ) : (() => {
                                const banner = getUnitCardBanner(unit.id, unitCardBanners, selectedLearningLanguage);
                                return banner?.image_url ? (
                                  <img src={getImageUrl(banner.image_url) || banner.image_url} alt={displayTitle} className="unit-card-image" />
                                ) : (
                                  <div className="unit-card-placeholder" />
                                );
                              })()}
                              <div className="unit-card-body">
                                <span className="unit-card-number">{t('user.educationalVocabulary.unitLabel', { number: unit.unit_number })}</span>
                                <h3 className="unit-card-title">{displayTitle}</h3>
                                {unit.streams && unit.streams.length > 0 && (
                                  <div className="unit-card-streams">
                                    {unit.streams.slice(0, 3).map((s) => (
                                      <span key={s.id} className="unit-stream-badge">{s.name_en || s.code}</span>
                                    ))}
                                    {unit.streams.length > 3 && <span className="unit-stream-badge">+{unit.streams.length - 3}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          ); });
                          })()}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="units-back-row">
                          <button
                            type="button"
                            className="back-btn units-back-btn"
                            onClick={() => {
                              setSelectedUnitId(null);
                              setArticles([]);
                            }}
                          >
                            <i className="material-icons">arrow_back</i>
                            <span>{t('user.educationalVocabulary.backToUnits')}</span>
                          </button>
                          <span className="units-current-title">
                            {(() => {
                              const u = units.find((x) => x.id === selectedUnitId);
                              const langNorm = selectedLearningLanguage ? String(selectedLearningLanguage).trim().toLowerCase() : '';
                              const currentYearName = schoolLevels.find(level => level.id === activeSchoolLevel)?.years.find((_, idx) => `${activeSchoolLevel}-${idx + 1}` === activeYearLevel);
                              const isGermanThirdYearHigh = langNorm === 'german' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                              const isFrenchThirdYearHigh = langNorm === 'french' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                              const isItalianThirdYearHigh = langNorm === 'italian' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                              const isSpanishThirdYearHigh = langNorm === 'spanish' && activeSchoolLevel === 'high-school' && currentYearName && String(currentYearName).toLowerCase().includes('third');
                              const isFrenchFourthYearMiddle = langNorm === 'french' && activeSchoolLevel === 'middle-school' && currentYearName && String(currentYearName).toLowerCase().includes('fourth');
                              const germanUnitTitlesByNumber = { 1: 'Die Umweltprobleme', 2: 'Die Massenmedien und Kommunikationsmittel', 3: 'Jugendprobleme', 4: 'Die deutschsprachigen Länder', 5: 'Künstlerleben', 6: 'Der technische Fortschritt' };
                              const frenchUnitTitlesByNumber = { 1: "Le texte d'histoire", 2: "Le débat des idées", 3: "L'appel" };
                              const italianUnitTitlesByNumber = { 1: 'La famiglia', 2: "L'ambiente", 3: 'La cucina', 4: 'La moda', 5: 'La tecnologia', 6: 'La salute', 7: 'Lo sport' };
                              const spanishUnitTitlesByNumber = { 1: 'El medio ambiente', 2: 'La educación', 3: 'La ciencia y la tecnología', 4: 'La solidaridad', 5: 'La salud' };
                              const frenchFourthYearMiddleUnitTitlesByNumber = { 1: 'Bienvenue dans ma région !', 2: 'Gloire à nos ancêtres !', 3: 'Oui à la culture !', 4: 'Vivons en harmonie !', 5: 'Non à la violence !', 6: 'Protégeons la nature !', 7: 'Agissons en écoresponsables !' };
                              if (!u) return 'Unit';
                              if (isGermanThirdYearHigh && germanUnitTitlesByNumber[u.unit_number]) return germanUnitTitlesByNumber[u.unit_number];
                              if (isFrenchThirdYearHigh && frenchUnitTitlesByNumber[u.unit_number]) return frenchUnitTitlesByNumber[u.unit_number];
                              if (isItalianThirdYearHigh && italianUnitTitlesByNumber[u.unit_number]) return italianUnitTitlesByNumber[u.unit_number];
                              if (isSpanishThirdYearHigh && spanishUnitTitlesByNumber[u.unit_number]) return spanishUnitTitlesByNumber[u.unit_number];
                              if (isFrenchFourthYearMiddle && frenchFourthYearMiddleUnitTitlesByNumber[u.unit_number]) return frenchFourthYearMiddleUnitTitlesByNumber[u.unit_number];
                              return u.title || 'Unit';
                            })()}
                          </span>
                        </div>
                        {/* Search and article grid for this unit (below) */}
                      </>
                    )
                  ) : null}

                  {/* Search and Filter Section (hidden when showing unit cards only) */}
                  {(units.length === 0 || selectedUnitId != null) && (
                  <>
                  <div className="vocabulary-search-section">
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder={t('user.educationalVocabulary.searchPlaceholder')}
                        value={vocabularySearchTerm}
                        onChange={(e) => setVocabularySearchTerm(e.target.value)}
                        className="vocabulary-search-input"
                      />
                      <i className="material-icons search-icon">search</i>
                    </div>
                    <button
                      className="refresh-content-btn"
                      onClick={() => {
                        const selectedSchool = schoolLevels.find(level => level.id === activeSchoolLevel);
                        if (selectedSchool && activeYearLevel) {
                          const yearIndex = parseInt(activeYearLevel.split('-').pop()) - 1;
                          const yearObj = selectedSchool.years[yearIndex];
                          if (yearObj) {
                            const yearName = typeof yearObj === 'string' ? yearObj : yearObj.name;
                            loadEducationalContent(activeSchoolLevel, yearName);
                          }
                        }
                      }}
                      title={t('user.educationalVocabulary.refresh')}
                    >
                      <i className="material-icons">refresh</i>
                      {t('user.educationalVocabulary.refresh')}
                    </button>
                    {vocabularySearchTerm && (
                      <div className="search-results-info">
                        {t('user.educationalVocabulary.foundArticles', { count: filteredArticles.length, term: vocabularySearchTerm })}
                      </div>
                    )}
                  </div>

                  {/* Display Articles from Admin Panel (Explore: one free per level, rest locked; sequential mastery lock when entitled) */}
                  {exploreExamSequentialNotice && (
                    <div className="reading-exam-sequential-notice" role="status" style={{ marginBottom: 16 }}>
                      <i className="material-icons reading-exam-sequential-notice__lock" aria-hidden="true">lock</i>
                      <span>{exploreExamSequentialNotice}</span>
                    </div>
                  )}
                  {(vocabularySearchTerm ? filteredArticles : articles).length > 0 ? (
                    <div className="educational-content-grid">
                      {(vocabularySearchTerm ? filteredArticles : articles).map((article, index) => {
                        const subscriptionLocked = !educationalContentFromSubscription && article.isFreePreview === false;
                        const masteryLocked = educationalContentFromSubscription && article.educational_sequential_access === 'mastery_locked';
                        return (
                        <div
                          key={article.id != null ? article.id : index}
                          className={`educational-content-card article-card${masteryLocked ? ' article-card--mastery-locked' : ''}`}
                          title={masteryLocked ? t('user.educationalSequential.masteryLockTooltip') : undefined}
                          onClick={() => {
                            if (subscriptionLocked) setShowUnlockModal(true);
                            else if (masteryLocked) showExamMasteryLockModal('educational');
                            else openArticleModal(article);
                          }}
                        >
                          {/* Article Image with Overlay Metadata */}
                          <div className="article-card-cover" style={{ position: 'relative', width: '100%', height: '50%' }}>
                            {subscriptionLocked && (
                              <span className="article-card-lock-badge material-icons" title="Unlock with subscription">lock</span>
                            )}
                            {masteryLocked && (
                              <span
                                className="article-card-lock-badge article-card-lock-badge--mastery material-icons"
                                title={t('user.educationalSequential.masteryLockTooltip')}
                                aria-hidden="true"
                              >
                                lock
                              </span>
                            )}
                            {article.image_url ? (
                              <img
                                src={getImageUrl(article.image_url) || article.image_url}
                                alt={article.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center',
                                  display: 'block',
                                  borderRadius: '12px 12px 0 0'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '12px 12px 0 0',
                                display: article.image_url ? 'none' : 'block'
                              }}
                            />
                            <div style={{
                              position: 'absolute',
                              bottom: '0px',
                              left: '0px',
                              right: '0px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              zIndex: 2,
                              padding: '0 8px 4px 8px'
                            }}>
                              <span style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                {article.unit || article.difficulty || 'Intermediate'}
                              </span>
                              <span style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                {article.estimated_read_time ? t('user.examVocabulary.minRead', { minutes: article.estimated_read_time }) : t('user.examVocabulary.readingArticle')}
                              </span>
                            </div>
                          </div>

                          {/* Article Content */}
                          <div className="educational-content-header">
                            <h3 className="educational-content-title">{article.title}</h3>
                            <p className="educational-content-description">
                              {cleanHtmlForPreview(article.content, 120)}
                            </p>
                          </div>
                          <div className="educational-content-actions">
                            <button
                              type="button"
                              className="user-course-btn primary"
                              disabled={masteryLocked}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (subscriptionLocked) setShowUnlockModal(true);
                                else if (masteryLocked) showExamMasteryLockModal('educational');
                                else openArticleModal(article);
                              }}
                            >
                              <i className="material-icons">visibility</i>
                              {t('user.examVocabulary.readArticle')}
                            </button>
                            <button
                              type="button"
                              className="user-course-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (subscriptionLocked) setShowUnlockModal(true);
                                else openDetailsModal(article, false);
                              }}
                            >
                              <i className="material-icons">info</i>
                              {t('user.examVocabulary.details')}
                            </button>
                          </div>
                          <div className="article-metadata">
                            <div className="metadata-left">
                              <div className="metadata-item">
                                <span className="material-icons">school</span>
                                <span>{trSchoolName(article.school || 'High School')}</span>
                              </div>
                              <div className="metadata-item">
                                <span className="material-icons">calendar_today</span>
                                <span>{trYearName(article.year || 'First Year')}</span>
                              </div>
                            </div>
                            <div className="metadata-item">
                              <span className="material-icons">schedule</span>
                              <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : '15/07/2025'}</span>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* No Articles Found Message */
                    <div className="no-articles-found">
                      <div className="no-articles-icon">
                        <i className="material-icons">library_books</i>
                      </div>
                      <h3 className="no-articles-title">{t('user.educationalVocabulary.noArticlesTitle')}</h3>
                      <p className="no-articles-message">
                        {t('user.educationalVocabulary.noArticlesYet', {
                          school: trSchoolName(schoolLevels.find(level => level.id === activeSchoolLevel)),
                          year: trYearName(schoolLevels.find(level => level.id === activeSchoolLevel)?.years.find((_, index) => `${activeSchoolLevel}-${index + 1}` === activeYearLevel))
                        })}
                      </p>
                      <p className="no-articles-subtitle">
                        {t('user.educationalVocabulary.noArticlesSubtitle')}
                      </p>
                      <button className="back-to-courses-btn" onClick={closeEducationalLevels}>
                        <i className="material-icons">arrow_back</i>
                        {t('user.educationalVocabulary.backToExplore')}
                      </button>
                    </div>
                  )}
                  </>
                  )}
                </div>
            </div>
          </div>
          </div>
          <ArticleDetailsModal
            article={detailsArticle}
            isOpen={showDetailsModal}
            onClose={closeDetailsModal}
            onStartReading={(article) => { closeDetailsModal(); openArticleModal(article); }}
            isLocked={detailsArticleIsLocked}
            onUnlockClick={() => { closeDetailsModal(); setShowUnlockModal(true); }}
            userId={currentUser?.id}
          />
        </>
        );
      }
      case 'exam-levels':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <div className="user-content-header-top">
                <button className="back-btn" onClick={closeExamLevels}>
                  <i className="material-icons">arrow_back</i>
                  <span>{t('user.exploreCourses.backToExplore')}</span>
                </button>
                <h1 className="user-content-title">{t('user.exploreCourses.internationalExam.title')}</h1>
              </div>
              <p className="user-content-subtitle">{t('user.examVocabulary.pageSubtitle')}</p>
            </div>

            {/* Exam Level Tabs */}
            <div className="user-section">
              <div className="educational-tabs">
                {examLevels.map(level => (
                  <button 
                    key={level.id}
                    className={`educational-tab ${activeExamLevel === level.id ? 'active' : ''}`}
                    onClick={() => handleExamLevelChange(level.id)}
                  >
                    <i className="material-icons">school</i>
                    {trExamLevelName(level)}
                  </button>
                ))}
              </div>

              {/* Exam Year Level Sub-tabs */}
              <div className="year-tabs">
                {examLevels.find(level => level.id === activeExamLevel)?.years.map((year, index) => (
                  <button 
                    key={index}
                    className={`year-tab ${activeExamYearLevel === `${activeExamLevel}-${index + 1}` ? 'active' : ''}`}
                    onClick={() => handleExamYearLevelChange(`${activeExamLevel}-${index + 1}`, year)}
                  >
                    {trExamYear(year)}
                  </button>
                ))}
              </div>

              {/* Exam Content */}
              <div className="educational-content">
                <div className="user-section">
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">library_books</i>
                      {(() => {
                        const L = examLevels.find((l) => l.id === activeExamLevel);
                        if (!L) return null;
                        const yRaw = L.years.find((_, index) => `${activeExamLevel}-${index + 1}` === activeExamYearLevel);
                        return (
                          <>
                            {trExamLevelName(L)} - {trExamYear(yRaw)}
                          </>
                        );
                      })()}
                    </div>
                    <p className="user-section-subtitle">{t('user.examVocabulary.browseForExamType')}</p>
                  </div>

                  {exploreExamSequentialNotice && (
                    <div className="reading-exam-sequential-notice" role="status" style={{ marginBottom: 16 }}>
                      <i className="material-icons reading-exam-sequential-notice__lock" aria-hidden="true">lock</i>
                      <span>{exploreExamSequentialNotice}</span>
                    </div>
                  )}

                  {/* Display Articles from Admin Panel — last added is free when !examContentEntitled; mastery lock when entitled + sequential */}
                  {examCatalogView({ status: examCatalogStatus, articleCount: examArticles.length }) === 'loading' ? (
                    <div className="no-articles-found" role="status">
                      <h3 className="no-articles-title">{t('user.examVocabulary.loading')}</h3>
                    </div>
                  ) : examCatalogView({ status: examCatalogStatus, articleCount: examArticles.length }) === 'error' ? (
                    <div className="no-articles-found" role="alert">
                      <h3 className="no-articles-title">{t('user.examVocabulary.loadErrorTitle')}</h3>
                      <p className="no-articles-message">{t('user.examVocabulary.loadError')}</p>
                      <button
                        type="button"
                        className="back-to-courses-btn"
                        onClick={() => {
                          const selection = yearNameForExamTab(examLevels, activeExamLevel, activeExamYearLevel);
                          loadExamContent(selection.examLevelId, selection.yearName);
                        }}
                      >
                        {t('user.examVocabulary.retry')}
                      </button>
                    </div>
                  ) : examArticles.length > 0 ? (
                    <div className="educational-content-grid">
                      {examArticles.map((article, index) => {
                        const subscriptionLocked = !examContentEntitled && article.isFreePreview !== true;
                        const masteryLocked = examContentEntitled && article.exam_sequential_access === 'mastery_locked';
                        return (
                        <div
                          key={article.id != null ? article.id : index}
                          className={`educational-content-card article-card${masteryLocked ? ' article-card--mastery-locked' : ''}`}
                          title={masteryLocked ? t('user.readingExamSequential.masteryLockTooltip') : undefined}
                          onClick={() => {
                            if (subscriptionLocked) setShowUnlockModal(true);
                            else if (masteryLocked) showExamMasteryLockModal('explore');
                            else openArticleModal(article);
                          }}
                        >
                          {/* Article Image with Overlay — same as Explore */}
                          <div className="article-card-cover" style={{ position: 'relative', width: '100%', height: '50%' }}>
                            {subscriptionLocked && (
                              <span className="article-card-lock-badge material-icons" title={t('user.examVocabulary.unlockWithSubscription')}>lock</span>
                            )}
                            {masteryLocked && (
                              <span
                                className="article-card-lock-badge article-card-lock-badge--mastery material-icons"
                                title={t('user.readingExamSequential.masteryLockTooltip')}
                                aria-hidden="true"
                              >
                                lock
                              </span>
                            )}
                            {article.image_url ? (
                              <img
                                src={getImageUrl(article.image_url) || article.image_url}
                                alt={article.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center',
                                  display: 'block',
                                  borderRadius: '12px 12px 0 0'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                }}
                              />
                            ) : null}
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: '12px 12px 0 0',
                                display: article.image_url ? 'none' : 'block'
                              }}
                            />
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              zIndex: 2,
                              padding: '0 8px 4px 8px'
                            }}>
                              <span style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                {article.difficulty || (article.category ? trExamYear(article.category) : '') || t('user.examVocabulary.intermediate')}
                              </span>
                              <span style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                {article.estimated_read_time
                                  ? t('user.examVocabulary.minRead', { minutes: article.estimated_read_time })
                                  : t('user.examVocabulary.readingArticle')}
                              </span>
                            </div>
                          </div>

                          {/* Article Content */}
                          <div className="educational-content-header">
                            <h3 className="educational-content-title">{article.title}</h3>
                            <p className="educational-content-description">
                              {cleanHtmlForPreview(article.content, 120)}
                            </p>
                          </div>
                          <div className="educational-content-actions">
                            <button
                              type="button"
                              className="user-course-btn primary"
                              disabled={masteryLocked}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (subscriptionLocked) setShowUnlockModal(true);
                                else if (masteryLocked) showExamMasteryLockModal('explore');
                                else openArticleModal(article);
                              }}
                            >
                              <i className="material-icons">visibility</i>
                              {t('user.examVocabulary.readArticle')}
                            </button>
                            <button
                              type="button"
                              className="user-course-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (subscriptionLocked) {
                                  setShowUnlockModal(true);
                                  return;
                                }
                                openDetailsModal(article, false);
                              }}
                            >
                              <i className="material-icons">info</i>
                              {t('user.examVocabulary.details')}
                            </button>
                          </div>
                          <div className="article-metadata">
                            <div className="metadata-left">
                              <div className="metadata-item">
                                <span className="material-icons">assignment</span>
                                <span>
                                  {(() => {
                                    const L = examLevels.find((l) => l.id === activeExamLevel);
                                    const yFallback = L?.years.find((_, index) => `${activeExamLevel}-${index + 1}` === activeExamYearLevel);
                                    const cat = article.category || yFallback;
                                    return `${trExamLevelName(L)} – ${cat ? trExamYear(cat) : ''}`;
                                  })()}
                                </span>
                              </div>
                              {article.topic && (
                                <div className="metadata-item">
                                  <span className="material-icons">label</span>
                                  <span>{article.topic}</span>
                                </div>
                              )}
                            </div>
                            <div className="metadata-item">
                              <span className="material-icons">schedule</span>
                              <span>{article.created_at ? new Date(article.created_at).toLocaleDateString() : '15/07/2025'}</span>
                            </div>
                          </div>
                        </div>
                      ); })}
                    </div>
                  ) : (
                    <div className="no-articles-found">
                      <div className="no-articles-icon">
                        <i className="material-icons">library_books</i>
                      </div>
                      <h3 className="no-articles-title">{t('user.examVocabulary.noArticlesTitle')}</h3>
                      <p className="no-articles-message">
                        {(() => {
                          const L = examLevels.find((l) => l.id === activeExamLevel);
                          const yRaw = L?.years.find((_, index) => `${activeExamLevel}-${index + 1}` === activeExamYearLevel) ?? '';
                          return t('user.examVocabulary.noArticlesYet', {
                            exam: trExamLevelName(L),
                            section: trExamYear(yRaw),
                          });
                        })()}
                      </p>
                      <p className="no-articles-subtitle">
                        {t('user.examVocabulary.noArticlesSubtitle')}
                      </p>
                      <button className="back-to-courses-btn" onClick={closeExamLevels}>
                        <i className="material-icons">arrow_back</i>
                        {t('user.exploreCourses.backToExplore')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'esp-vocabulary':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <div className="user-content-header-top">
                <button className="back-btn" onClick={closeESPVocabulary}>
                  <i className="material-icons">arrow_back</i>
                  <span>{t('user.exploreCourses.backToExplore')}</span>
                </button>
                <h1 className="user-content-title">Academic Vocabulary (ESP)</h1>
              </div>
              <p className="user-content-subtitle">Select a faculty to access specialized academic vocabulary</p>
            </div>

            {/* Faculty Tabs */}
            <div className="user-section">
              <div className="esp-faculty-tabs">
                {Object.entries(facultyData).map(([facultyId, faculty]) => (
                  <button 
                    key={facultyId}
                    className={`esp-faculty-tab ${activeFaculty === facultyId ? 'active' : ''}`}
                    onClick={() => handleFacultyChange(facultyId)}
                  >
                    <i className="material-icons">{faculty.icon}</i>
                    {faculty.name}
                  </button>
                ))}
              </div>

              {/* Specialty Cards */}
              {activeFaculty && !activeSpecialty && (
                <div className="user-section">
                  <div className="user-section-header">
                    <div className="user-section-title">
                      <i className="material-icons">{facultyData[activeFaculty].icon}</i>
                      {facultyData[activeFaculty].name} Specialties
                    </div>
                    <p className="user-section-subtitle">Select a specialty to access its vocabulary content</p>
                  </div>

                  <div className="esp-specialties-grid">
                    {facultyData[activeFaculty].specialties.map(specialty => (
                      <div 
                        key={specialty}
                        className="esp-specialty-card"
                        onClick={() => handleSpecialtyChange(specialty)}
                      >
                        <div className="esp-specialty-icon">
                          <i className="material-icons">school</i>
                        </div>
                        <div className="esp-specialty-content">
                          <h3 className="esp-specialty-title">{specialty}</h3>
                          <p className="esp-specialty-description">Access vocabulary and content for {specialty}</p>
                        </div>
                        <div className="esp-specialty-arrow">
                          <i className="material-icons">arrow_forward</i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Academic Level Cards */}
              {activeFaculty && activeSpecialty && !activeAcademicLevel && (
                <div className="user-section">
                  <div className="user-section-header">
                    <div className="user-section-header-top">
                      <button className="back-btn" onClick={() => setActiveSpecialty('')}>
                        <i className="material-icons">arrow_back</i>
                        <span>Back to Specialties</span>
                      </button>
                      <div className="user-section-title">
                        <i className="material-icons">{facultyData[activeFaculty].icon}</i>
                        {activeSpecialty} Academic Levels
                      </div>
                    </div>
                    <p className="user-section-subtitle">Select an academic level to access {activeSpecialty} content</p>
                  </div>

                  <div className="esp-specialties-grid">
                    {academicLevels.map(level => (
                      <div 
                        key={level.id}
                        className="esp-specialty-card"
                        onClick={() => handleAcademicLevelChange(level.name)}
                      >
                        <div className="esp-specialty-icon">
                          <i className="material-icons">{level.icon}</i>
                        </div>
                        <div className="esp-specialty-content">
                          <h3 className="esp-specialty-title">{level.name}</h3>
                          <p className="esp-specialty-description">{level.description}</p>
                        </div>
                        <div className="esp-specialty-arrow">
                          <i className="material-icons">arrow_forward</i>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ESP Content */}
              {activeSpecialty && activeAcademicLevel && (
                <div className="user-section">
                  <div className="user-section-header">
                    <div className="user-section-header-top">
                      <button className="back-btn" onClick={() => setActiveAcademicLevel('')}>
                        <i className="material-icons">arrow_back</i>
                        <span>Back to Academic Levels</span>
                      </button>
                      <div className="user-section-title">
                        <i className="material-icons">{facultyData[activeFaculty].icon}</i>
                        {activeSpecialty} - {activeAcademicLevel} Content
                      </div>
                    </div>
                    <p className="user-section-subtitle">Browse vocabulary and content for {activeSpecialty} at {activeAcademicLevel} level</p>
                  </div>

                  {espContent.length > 0 ? (
                    <div className="educational-content-grid">
                      {espContent.map(content => (
                        <div key={content.id} className="educational-content-card article-card" onClick={() => openReadingFromList(content)}>
                          {/* Article Image - covers half the card */}
                          <div 
                            className="educational-content-image"
                            style={{
                              backgroundImage: content.image_url ? `url(${getImageUrl(content.image_url) || content.image_url})` : 
                                              content.cover_image ? `url(${content.cover_image})` : 
                                              'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            }}
                          />
                          
                          {/* Article Content */}
                          <div className="educational-content-header">
                            <h3 className="educational-content-title">{content.title}</h3>
                            <p className="educational-content-description">
                              {cleanHtmlForPreview(content.content, 120)}
                            </p>
                          </div>
                          <div className="educational-content-meta">
                            <span className="educational-content-difficulty">
                              {content.difficulty}
                            </span>
                            <span className="educational-content-duration">
                              {content.estimated_read_time ? `${content.estimated_read_time} min read` : 'Reading Article'}
                            </span>
                          </div>
                          <div className="educational-content-actions">
                            <button className="user-course-btn primary" onClick={(e) => { e.stopPropagation(); openReadingFromList(content); }}>
                              <i className="material-icons">visibility</i>
                              Read Article
                            </button>
                            <button 
                              className="user-course-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetailsModal(content);
                              }}
                            >
                              <i className="material-icons">info</i>
                              Details
                            </button>
                          </div>
                          <div className="article-metadata">
                            <div className="metadata-left">
                              <div className="metadata-item">
                                <span className="material-icons">school</span>
                                <span>{activeSpecialty}</span>
                              </div>
                              <div className="metadata-item">
                                <span className="material-icons">calendar_today</span>
                                <span>{activeAcademicLevel}</span>
                            </div>
                          </div>
                          <div className="metadata-item">
                            <span className="material-icons">schedule</span>
                            <span>{content.created_at ? new Date(content.created_at).toLocaleDateString() : '15/07/2025'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        );
      case 'shop':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">Educational Shop</h1>
              <p className="user-content-subtitle">Discover premium courses, books, and learning materials</p>
            </div>
            <div style={{ position: 'relative', minHeight: '100vh' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              {/* Shop Controls */}
              <div className="user-section">
              <div className="shop-controls">
                <div className="shop-search">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <button onClick={handleSearch}>
                    <i className="material-icons">search</i>
                  </button>
                </div>
                
                <div className="shop-filters">
                  <select 
                    value={priceFilter} 
                    onChange={(e) => {
                      setPriceFilter(e.target.value);
                      handleFilterChange();
                    }}
                  >
                    <option value="">All Prices</option>
                    <option value="0-50">Under $50</option>
                    <option value="50-100">$50 - $100</option>
                    <option value="100-200">$100 - $200</option>
                    <option value="200+">$200+</option>
                  </select>
                  
                  <select 
                    value={sortBy} 
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      handleSortChange();
                    }}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Highest Rated</option>
                    <option value="newest">Newest First</option>
                  </select>
                </div>

                <div className="shop-cart-info">
                  <button className="cart-btn" onClick={() => setActiveShopTab('cart')}>
                    <i className="material-icons">shopping_cart</i>
                    <span className="cart-count">{getCartCount()}</span>
                  </button>
                  <button className="wishlist-btn" onClick={() => setActiveShopTab('wishlist')}>
                    <i className="material-icons">favorite</i>
                    <span className="wishlist-count">{wishlist.length}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Shop Tabs */}
            <div className="user-section">
              <div className="shop-tabs">
                <button 
                  className={`shop-tab ${activeShopTab === 'courses' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('courses')}
                >
                  <i className="material-icons">school</i>
                  Online Courses
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'books' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('books')}
                >
                  <i className="material-icons">book</i>
                  Books & Materials
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'tools' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('tools')}
                >
                  <i className="material-icons">build</i>
                  Learning Tools
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'software' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('software')}
                >
                  <i className="material-icons">computer</i>
                  Software & Apps
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'memberships' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('memberships')}
                >
                  <i className="material-icons">card_membership</i>
                  Memberships
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'certificates' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('certificates')}
                >
                  <i className="material-icons">verified</i>
                  Certificates
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'tutoring' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('tutoring')}
                >
                  <i className="material-icons">person_add</i>
                  Tutoring Services
                </button>
                <button 
                  className={`shop-tab ${activeShopTab === 'resources' ? 'active' : ''}`}
                  onClick={() => setActiveShopTab('resources')}
                >
                  <i className="material-icons">folder</i>
                  Study Resources
                </button>
              </div>
              {/* Shop Content */}
              <div className="shop-content">
                {activeShopTab === 'courses' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">school</i>
                        Online Courses
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">language</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Premium Spanish Course</h3>
                          <p className="user-course-description">
                            Advanced Spanish course with native speakers, cultural insights, and personalized feedback.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$99</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$149</span>
                            </div>
                            <span className="user-course-status active">33% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(1)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(1)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">business</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Business English Mastery</h3>
                          <p className="user-course-description">
                            Professional English for the workplace. Improve your business communication skills.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$79</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$119</span>
                            </div>
                            <span className="user-course-status active">34% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(2)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(2)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'books' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">book</i>
                        Books & Materials
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">book</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Grammar Mastery Book</h3>
                          <p className="user-course-description">
                            Comprehensive grammar guide with exercises, examples, and practice tests.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$29</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$39</span>
                            </div>
                            <span className="user-course-status active">25% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(3)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(3)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">menu_book</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Vocabulary Builder</h3>
                          <p className="user-course-description">
                            Essential vocabulary book with context, examples, and memory techniques.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$19</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$25</span>
                            </div>
                            <span className="user-course-status active">24% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(4)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(4)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'tools' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">build</i>
                        Learning Tools
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">headphones</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Audio Learning Pack</h3>
                          <p className="user-course-description">
                            High-quality audio lessons for listening comprehension and pronunciation practice.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$49</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$69</span>
                            </div>
                            <span className="user-course-status active">29% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(5)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(5)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">quiz</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Practice Test Kit</h3>
                          <p className="user-course-description">
                            Comprehensive practice tests with detailed explanations and progress tracking.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$39</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$55</span>
                            </div>
                            <span className="user-course-status active">29% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(6)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(6)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'memberships' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">card_membership</i>
                        Memberships
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', padding: '20px', background: 'var(--hover)', borderRadius: '12px', border: '2px solid var(--accent)' }}>
                        <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '20px' }}>
                          <i className="material-icons" style={{ color: 'white', fontSize: '28px' }}>star</i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '4px', fontSize: '18px' }}>Premium Membership</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Get unlimited access to all courses and premium features</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--accent)' }}>$199/year</span>
                            <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$299/year</span>
                            <span style={{ background: 'var(--success)', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>SAVE $100</span>
                          </div>
                        </div>
                        <button className="user-course-btn primary">
                          <i className="material-icons">shopping_cart</i>
                          Subscribe
                        </button>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', padding: '20px', background: 'var(--hover)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '20px' }}>
                          <i className="material-icons" style={{ color: 'white', fontSize: '28px' }}>school</i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: 'var(--text)', marginBottom: '4px', fontSize: '18px' }}>Student Membership</div>
                          <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Access to basic courses and study materials</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--accent)' }}>$99/year</span>
                            <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$149/year</span>
                            <span style={{ background: 'var(--success)', color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>SAVE $50</span>
                          </div>
                        </div>
                        <button className="user-course-btn primary">
                          <i className="material-icons">shopping_cart</i>
                          Subscribe
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'certificates' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">verified</i>
                        Certificates
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">verified</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">TOEFL Preparation Certificate</h3>
                          <p className="user-course-description">
                            Complete TOEFL preparation course with official certificate upon completion.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$199</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$299</span>
                            </div>
                            <span className="user-course-status active">33% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(7)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(7)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'tutoring' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">person_add</i>
                        Tutoring Services
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">person</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">One-on-One Spanish Tutoring</h3>
                          <p className="user-course-description">
                            Personalized Spanish tutoring sessions with native speakers.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$45/hour</span>
                            </div>
                            <span className="user-course-status active">Available</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(8)}>
                              <i className="material-icons">schedule</i>
                              Book Session
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(8)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'resources' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">folder</i>
                        Study Resources
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">description</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Study Guide Pack</h3>
                          <p className="user-course-description">
                            Comprehensive study guides for various subjects and exams.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$15</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$25</span>
                            </div>
                            <span className="user-course-status active">40% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(9)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(9)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeShopTab === 'software' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">computer</i>
                        Software & Apps
                      </div>
                    </div>
                    <div className="user-courses-grid">
                      <div className="user-course-card">
                        <div className="user-course-image">
                          <div className="placeholder">
                            <i className="material-icons">phone_android</i>
                          </div>
                        </div>
                        <div className="user-course-content">
                          <h3 className="user-course-title">Language Learning App</h3>
                          <p className="user-course-description">
                            Mobile app for learning languages with interactive exercises and progress tracking.
                          </p>
                          <div className="user-course-meta">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>$9.99</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', textDecoration: 'line-through' }}>$19.99</span>
                            </div>
                            <span className="user-course-status active">50% OFF</span>
                          </div>
                          <div className="user-course-actions">
                            <button className="user-course-btn primary" onClick={() => addToCart(10)}>
                              <i className="material-icons">shopping_cart</i>
                              Add to Cart
                            </button>
                            <button className="user-course-btn" onClick={() => toggleWishlistItem(10)}>
                              <i className="material-icons">favorite</i>
                              Wishlist
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Cart Section */}
                {activeShopTab === 'cart' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">shopping_cart</i>
                        Shopping Cart
                      </div>
                      <div className="cart-total">
                        Total: ${getCartTotal().toFixed(2)}
                      </div>
                    </div>
                    
                    {cart.length === 0 ? (
                      <div className="empty-cart">
                        <i className="material-icons">shopping_cart</i>
                        <h3>Your cart is empty</h3>
                        <p>Add some products to get started!</p>
                        <button className="user-course-btn primary" onClick={() => setActiveShopTab('courses')}>
                          <i className="material-icons">shopping_bag</i>
                          Browse Products
                        </button>
                      </div>
                    ) : (
                      <div className="cart-items">
                        {cart.map((item) => (
                          <div key={item.product_id} className="cart-item">
                            <div className="cart-item-image">
                              <img src={getImageUrl(item.image_url) || '/placeholder.jpg'} alt={item.title} />
                            </div>
                            <div className="cart-item-info">
                              <h4 className="cart-item-title">{item.title}</h4>
                              <div className="cart-item-price">${item.price.toFixed(2)}</div>
                              <div className="cart-item-quantity">
                                <button 
                                  className="quantity-btn" 
                                  onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)}
                                  disabled={item.quantity <= 1}
                                >
                                  -
                                </button>
                                <span>{item.quantity}</span>
                                <button 
                                  className="quantity-btn" 
                                  onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            <button 
                              className="remove-item" 
                              onClick={() => removeFromCart(item.product_id)}
                            >
                              <i className="material-icons">close</i>
                            </button>
                          </div>
                        ))}
                        
                        <div className="cart-actions">
                          <button className="user-course-btn primary">
                            <i className="material-icons">payment</i>
                            Proceed to Checkout
                          </button>
                          <button className="user-course-btn" onClick={() => setActiveShopTab('courses')}>
                            <i className="material-icons">add_shopping_cart</i>
                            Continue Shopping
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Wishlist Section */}
                {activeShopTab === 'wishlist' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">favorite</i>
                        My Wishlist
                      </div>
                    </div>
                    
                    {wishlist.length === 0 ? (
                      <div className="empty-wishlist">
                        <i className="material-icons">favorite_border</i>
                        <h3>Your wishlist is empty</h3>
                        <p>Save products you like for later!</p>
                        <button className="user-course-btn primary" onClick={() => setActiveShopTab('courses')}>
                          <i className="material-icons">shopping_bag</i>
                          Browse Products
                        </button>
                      </div>
                    ) : (
                      <div className="wishlist-items">
                        {wishlist.map((item) => (
                          <div key={item.product_id} className="wishlist-item">
                            <div className="wishlist-item-image">
                              <img src={getImageUrl(item.image_url) || '/placeholder.jpg'} alt={item.title} />
                            </div>
                            <div className="wishlist-item-info">
                              <h4 className="wishlist-item-title">{item.title}</h4>
                              <div className="wishlist-item-price">${item.price.toFixed(2)}</div>
                              <div className="wishlist-item-rating">
                                {generateStars(item.rating)}
                                <span>({item.review_count})</span>
                              </div>
                            </div>
                            <div className="wishlist-item-actions">
                              <button 
                                className="add-to-cart-btn" 
                                onClick={() => addToCart(item.product_id)}
                              >
                                <i className="material-icons">add_shopping_cart</i>
                              </button>
                              <button 
                                className="remove-wishlist-btn" 
                                onClick={() => toggleWishlistItem(item.product_id)}
                              >
                                <i className="material-icons">close</i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        );
      case 'live-learning':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">Live Learning</h1>
              <p className="user-content-subtitle">Book lessons with native speakers and track your progress</p>
            </div>

            {/* Live Learning Navigation Tabs */}
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              <div className="live-learning-tabs">
                <button 
                  className={`live-tab ${activeLiveTab === 'book-lesson' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('book-lesson')}
                >
                  <i className="material-icons">event</i>
                  Book a Lesson
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'my-bookings' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('my-bookings')}
                >
                  <i className="material-icons">schedule</i>
                  My Bookings
                  <span className="badge">3</span>
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'my-teachers' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('my-teachers')}
                >
                  <i className="material-icons">school</i>
                  My Teachers
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'progress' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('progress')}
                >
                  <i className="material-icons">trending_up</i>
                  Progress
                </button>
                <button 
                  className={`live-tab ${activeLiveTab === 'rate-lesson' ? 'active' : ''}`}
                  onClick={() => setActiveLiveTab('rate-lesson')}
                >
                  <i className="material-icons">rate_review</i>
                  Rate a Lesson
                </button>
              </div>

              {/* Live Learning Content */}
              <div className="live-learning-content">
                {/* Book a Lesson Section */}
                {activeLiveTab === 'book-lesson' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">event</i>
                        Book a Lesson
                      </div>
                      <p className="user-section-subtitle">Find the perfect teacher for your language learning journey</p>
                    </div>

                    {/* Search and Filter */}
                    <div className="live-search-filter">
                      <div className="search-box">
                        <i className="material-icons">search</i>
                        <input 
                          type="text" 
                          placeholder="Search teachers by name or specialty..."
                          value={teacherSearch}
                          onChange={(e) => setTeacherSearch(e.target.value)}
                        />
                      </div>
                      <div className="filter-options">
                        <select 
                          value={specialtyFilter} 
                          onChange={(e) => setSpecialtyFilter(e.target.value)}
                        >
                          <option value="">All Specialties</option>
                          <option value="english">English</option>
                          <option value="spanish">Spanish</option>
                          <option value="french">French</option>
                          <option value="german">German</option>
                          <option value="chinese">Chinese</option>
                          <option value="ielts">IELTS</option>
                          <option value="toefl">TOEFL</option>
                        </select>
                        <select 
                          value={ratingFilter} 
                          onChange={(e) => setRatingFilter(e.target.value)}
                        >
                          <option value="">All Ratings</option>
                          <option value="5">5 Stars</option>
                          <option value="4">4+ Stars</option>
                          <option value="3">3+ Stars</option>
                        </select>
                        <select 
                          value={availabilityFilter} 
                          onChange={(e) => setAvailabilityFilter(e.target.value)}
                        >
                          <option value="">All Availability</option>
                          <option value="today">Available Today</option>
                          <option value="tomorrow">Available Tomorrow</option>
                          <option value="week">This Week</option>
                        </select>
                      </div>
                    </div>

                    {/* Teachers Grid */}
                    <div className="teachers-grid">
                      {filteredTeachers.length === 0 ? (
                        <div className="no-results">
                          <i className="material-icons">search_off</i>
                          <h3>No teachers found</h3>
                          <p>Try adjusting your search criteria</p>
                        </div>
                      ) : (
                        filteredTeachers.map(teacher => (
                          <div key={teacher.id} className="teacher-card">
                            <div className="teacher-image">
                              <img src={teacher.image} alt={teacher.name} />
                              <div className={`teacher-status ${teacher.availability.includes('Today') ? 'online' : 'offline'}`}>
                                {teacher.availability.includes('Today') ? 'Online' : 'Offline'}
                              </div>
                            </div>
                            <div className="teacher-content">
                              <h3 className="teacher-title">{teacher.name}</h3>
                              <p className="teacher-specialty">{teacher.specialty}</p>
                              <div className="teacher-rating">
                                <span className="stars">{generateStars(teacher.rating)}</span>
                                <span className="rating-text">{teacher.rating}/5.0 ({teacher.totalStudents} reviews)</span>
                              </div>
                              <p className="teacher-description">{teacher.bio}</p>
                              <div className="teacher-meta">
                                <div className="teacher-price-info">
                                  <span className="teacher-price">${teacher.hourlyRate}/hour</span>
                                  <span className="teacher-availability">{teacher.availability}</span>
                                </div>
                                <span className={`teacher-status-badge ${teacher.availability.includes('Today') ? 'online' : 'offline'}`}>
                                  {teacher.availability.includes('Today') ? 'Online Now' : 'Offline'}
                                </span>
                              </div>
                              <div className="teacher-actions">
                                <button className="user-course-btn primary" onClick={() => openBookingModal(teacher.id)}>
                                  <i className="material-icons">event</i>
                                  Book Lesson
                                </button>
                                <button className="user-course-btn" onClick={() => viewTeacherProfile(teacher.id)}>
                                  <i className="material-icons">info</i>
                                  View Profile
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* My Bookings Section */}
                {activeLiveTab === 'my-bookings' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">schedule</i>
                        My Bookings
                      </div>
                      <p>Manage your upcoming and past lessons</p>
                    </div>

                    {/* Booking Status Tabs */}
                    <div className="booking-tabs">
                      <button 
                        className={`booking-tab ${activeBookingTab === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setActiveBookingTab('upcoming')}
                      >
                        Upcoming
                        <span className="tab-count">{bookings.upcoming.length}</span>
                      </button>
                      <button 
                        className={`booking-tab ${activeBookingTab === 'completed' ? 'active' : ''}`}
                        onClick={() => setActiveBookingTab('completed')}
                      >
                        Completed
                        <span className="tab-count">{bookings.completed.length}</span>
                      </button>
                      <button 
                        className={`booking-tab ${activeBookingTab === 'cancelled' ? 'active' : ''}`}
                        onClick={() => setActiveBookingTab('cancelled')}
                      >
                        Cancelled
                        <span className="tab-count">{bookings.cancelled.length}</span>
                      </button>
                    </div>

                    {/* Upcoming Bookings */}
                    {activeBookingTab === 'upcoming' && (
                      <div className="bookings-container">
                        {bookings.upcoming.length === 0 ? (
                          <div className="no-bookings">
                            <i className="material-icons">schedule</i>
                            <h3>No upcoming bookings</h3>
                            <p>Book your first lesson to get started!</p>
                          </div>
                        ) : (
                          bookings.upcoming.map(booking => (
                            <div key={booking.id} className="booking-card">
                              <div className="booking-teacher">
                                <img src={booking.teacherImage} alt={booking.teacherName} />
                                <div className="booking-teacher-info">
                                  <h4>{booking.teacherName}</h4>
                                  <p>{booking.subject}</p>
                                </div>
                              </div>
                              <div className="booking-details">
                                <div className="booking-time">
                                  <i className="material-icons">schedule</i>
                                  <span>{formatDate(booking.date)} at {booking.time} ({booking.duration} min)</span>
                                </div>
                                <div className="booking-status scheduled">Scheduled</div>
                              </div>
                              <div className="booking-actions">
                                <button className="user-course-btn primary" onClick={() => joinLesson(booking.id)}>
                                  <i className="material-icons">play_arrow</i>
                                  Join Session
                                </button>
                                <button className="user-course-btn" onClick={() => rescheduleBooking(booking.id)}>
                                  <i className="material-icons">reschedule</i>
                                  Reschedule
                                </button>
                                <button className="user-course-btn danger" onClick={() => cancelBooking(booking.id)}>
                                  <i className="material-icons">cancel</i>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Completed Bookings */}
                    {activeBookingTab === 'completed' && (
                      <div className="bookings-container">
                        {bookings.completed.length === 0 ? (
                          <div className="no-bookings">
                            <i className="material-icons">check_circle</i>
                            <h3>No completed lessons</h3>
                            <p>Complete your first lesson to see it here!</p>
                          </div>
                        ) : (
                          bookings.completed.map(booking => (
                            <div key={booking.id} className="booking-card completed">
                              <div className="booking-teacher">
                                <img src={booking.teacherImage} alt={booking.teacherName} />
                                <div className="booking-teacher-info">
                                  <h4>{booking.teacherName}</h4>
                                  <p>{booking.subject}</p>
                                </div>
                              </div>
                              <div className="booking-details">
                                <div className="booking-time">
                                  <i className="material-icons">schedule</i>
                                  <span>{formatDate(booking.date)} at {booking.time} ({booking.duration} min)</span>
                                </div>
                                <div className="booking-status completed">Completed</div>
                              </div>
                              <div className="booking-actions">
                                <button className="user-course-btn primary" onClick={() => rateLesson(booking.id)}>
                                  <i className="material-icons">rate_review</i>
                                  Rate Lesson
                                </button>
                                <button className="user-course-btn">
                                  <i className="material-icons">event</i>
                                  Book Again
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Cancelled Bookings */}
                    {activeBookingTab === 'cancelled' && (
                      <div className="bookings-container">
                        {bookings.cancelled.length === 0 ? (
                          <div className="no-bookings">
                            <i className="material-icons">cancel</i>
                            <h3>No cancelled bookings</h3>
                            <p>Great! You haven't cancelled any lessons.</p>
                          </div>
                        ) : (
                          bookings.cancelled.map(booking => (
                            <div key={booking.id} className="booking-card cancelled">
                              <div className="booking-teacher">
                                <img src={booking.teacherImage} alt={booking.teacherName} />
                                <div className="booking-teacher-info">
                                  <h4>{booking.teacherName}</h4>
                                  <p>{booking.subject}</p>
                                </div>
                              </div>
                              <div className="booking-details">
                                <div className="booking-time">
                                  <i className="material-icons">schedule</i>
                                  <span>{formatDate(booking.date)} at {booking.time} ({booking.duration} min)</span>
                                </div>
                                <div className="booking-status cancelled">Cancelled</div>
                              </div>
                              <div className="booking-actions">
                                <button className="user-course-btn">
                                  <i className="material-icons">event</i>
                                  Book Again
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                {/* My Teachers Section */}
                {activeLiveTab === 'my-teachers' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">school</i>
                        My Teachers
                      </div>
                      <p className="user-section-subtitle">Your favorite teachers and learning history</p>
                    </div>

                    <div className="teachers-grid">
                      {myTeachers.length === 0 ? (
                        <div className="no-teachers">
                          <i className="material-icons">school</i>
                          <h3>No teachers yet</h3>
                          <p>Book your first lesson to start building your teacher network!</p>
                        </div>
                      ) : (
                        myTeachers.map(teacher => (
                          <div key={teacher.id} className="teacher-card">
                            <div className="teacher-image">
                              <img src={teacher.image} alt={teacher.name} />
                              <div className="teacher-status online">Online</div>
                            </div>
                            <div className="teacher-content">
                              <h3 className="teacher-title">{teacher.name}</h3>
                              <p className="teacher-specialty">{teacher.specialty} • {teacher.lessonsCompleted} lessons completed</p>
                              <div className="teacher-rating">
                                <span className="stars">{generateStars(teacher.rating)}</span>
                                <span className="rating-text">{teacher.rating} rating</span>
                              </div>
                              <p className="teacher-description">
                                Your trusted language partner. Great for consistent learning and progress tracking.
                              </p>
                              <div className="teacher-meta">
                                <div className="teacher-price-info">
                                  <span className="teacher-price">$25/hour</span>
                                  <span className="teacher-availability">Last lesson: {formatDate(teacher.lastLesson)}</span>
                                </div>
                                <span className="teacher-status-badge online">Online Now</span>
                              </div>
                              <div className="teacher-actions">
                                <button className="user-course-btn primary" onClick={() => openBookingModal(teacher.id)}>
                                  <i className="material-icons">event</i>
                                  Book Again
                                </button>
                                <button className="user-course-btn" onClick={() => viewTeacherProfile(teacher.id)}>
                                  <i className="material-icons">message</i>
                                  Message
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Progress Section */}
                {activeLiveTab === 'progress' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">trending_up</i>
                        Learning Progress
                      </div>
                      <p className="user-section-subtitle">Track your language learning journey and achievements</p>
                    </div>

                    {/* Progress Overview Cards */}
                    <div className="progress-stats">
                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">schedule</i>
                        </div>
                        <div className="progress-info">
                          <h3>Total Lessons</h3>
                          <div className="progress-number">47</div>
                          <div className="progress-period">This year</div>
                        </div>
                      </div>

                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">timer</i>
                        </div>
                        <div className="progress-info">
                          <h3>Study Hours</h3>
                          <div className="progress-number">89</div>
                          <div className="progress-period">Total hours</div>
                        </div>
                      </div>

                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">star</i>
                        </div>
                        <div className="progress-info">
                          <h3>Average Rating</h3>
                          <div className="progress-number">4.8</div>
                          <div className="progress-period">From teachers</div>
                        </div>
                      </div>

                      <div className="progress-card">
                        <div className="progress-icon">
                          <i className="material-icons">language</i>
                        </div>
                        <div className="progress-info">
                          <h3>Languages</h3>
                          <div className="progress-number">3</div>
                          <div className="progress-period">Spanish, English, French</div>
                        </div>
                      </div>
                    </div>

                    {/* Learning Streak */}
                    <div className="streak-container">
                      <h3>Current Learning Streak</h3>
                      <div className="streak-info">
                        <div className="streak-number">12</div>
                        <div className="streak-text">
                          <p>Days</p>
                          <span>Keep it up! You're doing great.</span>
                        </div>
                      </div>
                    </div>

                    {/* Language Progress */}
                    <div className="user-section">
                      <div className="user-section-header">
                        <div className="user-section-title">
                          <i className="material-icons">language</i>
                          Language Progress
                        </div>
                      </div>
                      
                      <div className="language-progress-grid">
                        <div className="language-progress-card">
                          <div className="language-header">
                            <h4>Spanish</h4>
                            <span className="level-badge intermediate">Intermediate</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '75%'}}></div>
                          </div>
                          <p>15 lessons completed • 75% to Advanced</p>
                        </div>

                        <div className="language-progress-card">
                          <div className="language-header">
                            <h4>English</h4>
                            <span className="level-badge advanced">Advanced</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '90%'}}></div>
                          </div>
                          <p>8 lessons completed • 90% to Fluent</p>
                        </div>

                        <div className="language-progress-card">
                          <div className="language-header">
                            <h4>French</h4>
                            <span className="level-badge beginner">Beginner</span>
                          </div>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{width: '30%'}}></div>
                          </div>
                          <p>3 lessons completed • 30% to Intermediate</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rate a Lesson Section */}
                {activeLiveTab === 'rate-lesson' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">rate_review</i>
                        Rate a Lesson
                      </div>
                      <p className="user-section-subtitle">Share your feedback with teachers to help them improve</p>
                    </div>

                    <div className="rating-container">
                      {ratings.length === 0 ? (
                        <div className="no-ratings">
                          <i className="material-icons">rate_review</i>
                          <h3>No lessons to rate</h3>
                          <p>Complete a lesson to rate your teacher!</p>
                        </div>
                      ) : (
                        ratings.map(rating => (
                          <div key={rating.id} className="rating-card">
                            <div className="rating-teacher">
                              <img src="/placeholder-avatar.jpg" alt={rating.teacherName} />
                              <div className="rating-teacher-info">
                                <h4>{rating.teacherName}</h4>
                                <p>{rating.subject} • {formatDate(rating.date)}</p>
                              </div>
                            </div>
                            {rating.canRate ? (
                              <button className="user-course-btn primary" onClick={() => openRatingModal(rating.id)}>
                                <i className="material-icons">rate_review</i>
                                Rate Lesson
                              </button>
                            ) : (
                              <span className="rated-badge">Already Rated</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'course-library':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.courseLibrary.title')}</h1>
              <p className="user-content-subtitle">{t('user.courseLibrary.subtitle')}</p>
            </div>

            {/* Hero Section */}
            <div className="user-section">
              <div className="hero-content">
                <h2>{t('user.courseLibrary.hero.title')}</h2>
                <p>{t('user.courseLibrary.hero.description')}</p>
                <div className="hero-stats">
                  <div className="stat">
                    <span className="stat-number">1,000+</span>
                    <span className="stat-label">{t('user.courseLibrary.hero.courses')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">500+</span>
                    <span className="stat-label">{t('user.courseLibrary.hero.teachers')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">50K+</span>
                    <span className="stat-label">{t('user.courseLibrary.hero.students')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Library Navigation Tabs */}
            <div className="user-section">
              <div className="course-library-tabs">
                <button 
                  className={`course-tab ${activeCourseLibraryTab === 'courses' ? 'active' : ''}`}
                  onClick={() => setActiveCourseLibraryTab('courses')}
                >
                  <i className="material-icons">library_books</i>
                  {t('user.courseLibrary.tabs.courses')}
                </button>
                <button 
                  className={`course-tab ${activeCourseLibraryTab === 'support-classes' ? 'active' : ''}`}
                  onClick={() => setActiveCourseLibraryTab('support-classes')}
                >
                  <i className="material-icons">support_agent</i>
                  {t('user.courseLibrary.tabs.supportClasses')}
                </button>
                <button 
                  className={`course-tab ${activeCourseLibraryTab === 'my-learning' ? 'active' : ''}`}
                  onClick={() => setActiveCourseLibraryTab('my-learning')}
                >
                  <i className="material-icons">school</i>
                  {t('user.courseLibrary.tabs.myLearning')}
                </button>
                <button 
                  className={`course-tab ${activeCourseLibraryTab === 'wishlist' ? 'active' : ''}`}
                  onClick={() => setActiveCourseLibraryTab('wishlist')}
                >
                  <i className="material-icons">favorite</i>
                  {t('user.courseLibrary.tabs.wishlist')}
                </button>
                <button 
                  className={`course-tab ${activeCourseLibraryTab === 'certificates' ? 'active' : ''}`}
                  onClick={() => setActiveCourseLibraryTab('certificates')}
                >
                  <i className="material-icons">verified</i>
                  {t('user.courseLibrary.tabs.certificates')}
                </button>
              </div>

              {/* Course Library Content */}
              <div className="course-library-content">
                {/* All Courses Section */}
                {activeCourseLibraryTab === 'courses' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">library_books</i>
                        {t('user.courseLibrary.allCourses')}
                      </div>
                      <div className="view-toggle">
                        <button 
                          className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} 
                          onClick={() => setViewMode('grid')}
                        >
                          <i className="material-icons">grid_view</i>
                        </button>
                        <button 
                          className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} 
                          onClick={() => setViewMode('list')}
                        >
                          <i className="material-icons">view_list</i>
                        </button>
                      </div>
                    </div>

                    {/* Filters Section */}
                    <div className="filters-section">
                      <div className="filters-container">
                        <div className="filter-group">
                          <label htmlFor="categoryFilter">{t('user.courseLibrary.filters.category')}</label>
                          <select 
                            id="categoryFilter"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                          >
                            <option value="">{t('user.courseLibrary.filters.allCategories')}</option>
                            <option value="programming">Programming</option>
                            <option value="design">Design</option>
                            <option value="business">Business</option>
                            <option value="marketing">Marketing</option>
                            <option value="languages">Languages</option>
                            <option value="music">Music</option>
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="levelFilter">{t('user.courseLibrary.filters.level')}</label>
                          <select 
                            id="levelFilter"
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                          >
                            <option value="">{t('user.courseLibrary.filters.allLevels')}</option>
                            <option value="beginner">{t('user.freeTalk.filters.beginner')}</option>
                            <option value="intermediate">{t('user.freeTalk.filters.intermediate')}</option>
                            <option value="advanced">{t('user.freeTalk.filters.advanced')}</option>
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="priceFilterCourse">{t('user.courseLibrary.filters.price')}</label>
                          <select 
                            id="priceFilterCourse"
                            value={priceFilterCourse}
                            onChange={(e) => setPriceFilterCourse(e.target.value)}
                          >
                            <option value="">{t('user.courseLibrary.filters.allPrices')}</option>
                            <option value="free">{t('user.courseLibrary.filters.free')}</option>
                            <option value="paid">{t('user.courseLibrary.filters.paid')}</option>
                            <option value="under50">{t('user.courseLibrary.filters.under50')}</option>
                            <option value="under100">{t('user.courseLibrary.filters.under100')}</option>
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="ratingFilterCourse">{t('user.courseLibrary.filters.rating')}</label>
                          <select 
                            id="ratingFilterCourse"
                            value={ratingFilterCourse}
                            onChange={(e) => setRatingFilterCourse(e.target.value)}
                          >
                            <option value="">{t('user.courseLibrary.filters.allRatings')}</option>
                            <option value="4.5">4.5+ {t('user.courseLibrary.filters.stars')}</option>
                            <option value="4.0">4.0+ {t('user.courseLibrary.filters.stars')}</option>
                            <option value="3.5">3.5+ {t('user.courseLibrary.filters.stars')}</option>
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="sortFilter">{t('user.courseLibrary.filters.sortBy')}</label>
                          <select 
                            id="sortFilter"
                            value={sortFilter}
                            onChange={(e) => setSortFilter(e.target.value)}
                          >
                            <option value="popular">{t('user.courseLibrary.filters.mostPopular')}</option>
                            <option value="newest">{t('user.courseLibrary.filters.newest')}</option>
                            <option value="rating">{t('user.courseLibrary.filters.highestRated')}</option>
                            <option value="price-low">{t('user.courseLibrary.filters.priceLowToHigh')}</option>
                            <option value="price-high">{t('user.courseLibrary.filters.priceHighToLow')}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Search Box */}
                    <div className="search-container">
                      <i className="material-icons">search</i>
                      <input 
                        type="text" 
                        placeholder={t('user.courseLibrary.searchPlaceholder')} 
                        value={courseSearch}
                        onChange={(e) => setCourseSearch(e.target.value)}
                      />
                    </div>

                    {/* Courses Grid */}
                    <div className={`courses-container ${viewMode === 'list' ? 'list-view' : ''}`}>
                      {filteredCourses.length === 0 ? (
                        <div className="no-results">
                          <i className="material-icons">search_off</i>
                          <h3>{t('user.courseLibrary.noCoursesFound')}</h3>
                          <p>{t('user.courseLibrary.tryAdjusting')}</p>
                        </div>
                      ) : (
                        filteredCourses.map(course => (
                          <div key={course.id} className="course-card">
                            <div className="course-image">
                              <img src={getImageUrl(course.image || course.cover_image_url || course.thumbnail_url) || (course.title ? `https://via.placeholder.com/300x200/667eea/ffffff?text=${encodeURIComponent(course.title.charAt(0))}` : 'https://via.placeholder.com/300x200/667eea/ffffff')} alt={course.title || 'Course'} />
                              {course.featured && (
                                <div className="featured-badge">{t('user.courseLibrary.featured')}</div>
                              )}
                            </div>
                            <div className="course-content">
                              <h3 className="course-title">{course.title}</h3>
                              <p className="course-instructor">{t('user.courseLibrary.by')} {course.instructor}</p>
                              <div className="course-rating">
                                {generateStars(course.rating)}
                                <span className="rating-text">{course.rating} ({course.reviewCount} {t('user.courseLibrary.reviews')})</span>
                              </div>
                              <div className="course-meta">
                                <span className="course-duration">{course.duration}</span>
                                <span className="course-students">{course.students.toLocaleString()} {t('user.courseLibrary.students')}</span>
                              </div>
                              <div className="course-actions">
                                <button 
                                  className="user-course-btn primary" 
                                  onClick={() => openCourseDetailModal(course.id)}
                                >
                                  <i className="material-icons">visibility</i>
                                  {t('user.courseLibrary.viewDetails')}
                                </button>
                                <button 
                                  className="user-course-btn" 
                                  onClick={() => addToCourseWishlist(course.id)}
                                >
                                  <i className="material-icons">favorite_border</i>
                                  {t('user.courseLibrary.wishlist')}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Support Classes Section */}
                {activeCourseLibraryTab === 'support-classes' && (
                  <>
                    {showWorkshopView ? (
                      /* Workshops View */
                      <div className="user-section">
                        {selectedWorkshop ? (
                          /* Workshop Detail View with Channel */
                          <>
                            <button 
                              className="back-button"
                              onClick={() => {
                                setSelectedWorkshop(null);
                                setShowWorkshopView(false);
                                setWorkshopChannelPosts([]);
                                setWorkshopSessions([]);
                                setIsEnrolledInWorkshop(false);
                              }}
                              title="Back to Workshops"
                            >
                              <i className="material-icons">arrow_back</i>
                            </button>
                            
                            <div className="user-section-header">
                              <div className="user-section-title">
                                <i className="material-icons">live_tv</i>
                                {selectedWorkshop.title}
                              </div>
                              <p className="user-section-subtitle">
                                {selectedWorkshop.description || 'Interactive live sessions with expert guidance'}
                              </p>
                            </div>

                            {/* Workshop Tabs */}
                            <div className="workshop-tabs">
                              <button 
                                className={`workshop-tab ${activeWorkshopTab === 'overview' ? 'active' : ''}`}
                                onClick={() => setActiveWorkshopTab('overview')}
                              >
                                <i className="material-icons">info</i>
                                Overview
                              </button>
                              <button 
                                className={`workshop-tab ${activeWorkshopTab === 'sessions' ? 'active' : ''}`}
                                onClick={() => setActiveWorkshopTab('sessions')}
                                disabled={!isEnrolledInWorkshop}
                              >
                                <i className="material-icons">event</i>
                                Sessions
                              </button>
                              <button 
                                className={`workshop-tab ${activeWorkshopTab === 'channel' ? 'active' : ''}`}
                                onClick={() => setActiveWorkshopTab('channel')}
                                disabled={!isEnrolledInWorkshop}
                              >
                                <i className="material-icons">forum</i>
                                Channel
                              </button>
                              <button 
                                className={`workshop-tab ${activeWorkshopTab === 'materials' ? 'active' : ''}`}
                                onClick={() => setActiveWorkshopTab('materials')}
                                disabled={!isEnrolledInWorkshop}
                              >
                                <i className="material-icons">folder</i>
                                Materials
                              </button>
                            </div>

                            {/* Workshop Content */}
                            <div className="workshop-content">
                              {activeWorkshopTab === 'overview' && (
                                <div className="workshop-overview">
                                  <div className="workshop-info-card">
                                    <h3>Workshop Information</h3>
                                    <div className="info-grid">
                                      <div className="info-item">
                                        <i className="material-icons">person</i>
                                        <div>
                                          <strong>Teacher:</strong>
                                          <span>{selectedWorkshop.teacher_name || 'Expert Teacher'}</span>
                                        </div>
                                      </div>
                                      {selectedWorkshop.unit_name && (
                                        <div className="info-item">
                                          <i className="material-icons">book</i>
                                          <div>
                                            <strong>Unit:</strong>
                                            <span>{selectedWorkshop.unit_name}</span>
                                          </div>
                                        </div>
                                      )}
                                      {selectedWorkshop.subject && (
                                        <div className="info-item">
                                          <i className="material-icons">subject</i>
                                          <div>
                                            <strong>Subject:</strong>
                                            <span>{selectedWorkshop.subject}</span>
                                          </div>
                                        </div>
                                      )}
                                      {selectedWorkshop.start_date && (
                                        <div className="info-item">
                                          <i className="material-icons">calendar_today</i>
                                          <div>
                                            <strong>Start Date:</strong>
                                            <span>{new Date(selectedWorkshop.start_date).toLocaleDateString()}</span>
                                          </div>
                                        </div>
                                      )}
                                      <div className="info-item">
                                        <i className="material-icons">people</i>
                                        <div>
                                          <strong>Enrolled:</strong>
                                          <span>{selectedWorkshop.enrolled_count || 0} / {selectedWorkshop.max_students || 50}</span>
                                        </div>
                                      </div>
                                      <div className="info-item">
                                        <i className="material-icons">attach_money</i>
                                        <div>
                                          <strong>Price:</strong>
                                          <span>${selectedWorkshop.price || 0}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {!isEnrolledInWorkshop ? (
                                    <div className="enrollment-card">
                                      <h3>Enroll in this Workshop</h3>
                                      <p>Join this interactive live workshop to access all sessions, materials, and the private channel.</p>
                                      <button 
                                        className="user-course-btn primary"
                                        onClick={() => enrollInWorkshop(selectedWorkshop.id)}
                                        style={{ marginTop: '20px' }}
                                      >
                                        <i className="material-icons">shopping_cart</i>
                                        Enroll Now - ${selectedWorkshop.price || 0}
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="enrollment-success-card">
                                      <i className="material-icons" style={{ fontSize: '48px', color: '#4CAF50' }}>check_circle</i>
                                      <h3>You're Enrolled!</h3>
                                      <p>You now have access to all workshop sessions, materials, and the private channel.</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {activeWorkshopTab === 'sessions' && isEnrolledInWorkshop && (
                                <div className="workshop-sessions">
                                  <h3>Workshop Sessions</h3>
                                  {loadingWorkshop ? (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                      <i className="material-icons" style={{ fontSize: '48px', color: 'var(--accent)' }}>hourglass_empty</i>
                                      <p>Loading sessions...</p>
                                    </div>
                                  ) : workshopSessions.length === 0 ? (
                                    <div className="no-results">
                                      <i className="material-icons">event</i>
                                      <h3>No sessions scheduled yet</h3>
                                      <p>The teacher will add sessions soon.</p>
                                    </div>
                                  ) : (
                                    <div className="sessions-list">
                                      {workshopSessions.map(session => (
                                        <div key={session.id} className="session-card">
                                          <div className="session-number">Session {session.session_number}</div>
                                          <h4>{session.title || `Session ${session.session_number}`}</h4>
                                          {session.description && <p>{session.description}</p>}
                                          <div className="session-meta">
                                            <span>
                                              <i className="material-icons">calendar_today</i>
                                              {new Date(session.scheduled_date).toLocaleString()}
                                            </span>
                                            <span>
                                              <i className="material-icons">schedule</i>
                                              {session.duration_minutes || 60} minutes
                                            </span>
                                            {session.meeting_link && (
                                              <a href={session.meeting_link} target="_blank" rel="noopener noreferrer" className="session-link">
                                                <i className="material-icons">videocam</i>
                                                Join Live Session
                                              </a>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {activeWorkshopTab === 'channel' && isEnrolledInWorkshop && (
                                <div className="workshop-channel">
                                  <h3>Workshop Channel</h3>
                                  {loadingWorkshop ? (
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                      <i className="material-icons" style={{ fontSize: '48px', color: 'var(--accent)' }}>hourglass_empty</i>
                                      <p>Loading channel...</p>
                                    </div>
                                  ) : workshopChannelPosts.length === 0 ? (
                                    <div className="no-results">
                                      <i className="material-icons">forum</i>
                                      <h3>No posts yet</h3>
                                      <p>The teacher will post announcements and materials here.</p>
                                    </div>
                                  ) : (
                                    <div className="channel-posts">
                                      {workshopChannelPosts.map(post => (
                                        <div key={post.id} className={`channel-post ${post.is_pinned ? 'pinned' : ''}`}>
                                          {post.is_pinned && (
                                            <div className="pinned-badge">
                                              <i className="material-icons">push_pin</i>
                                              Pinned
                                            </div>
                                          )}
                                          <div className="post-header">
                                            <div className="post-author">
                                              <img 
                                                src={getSafeAvatarUrl(post.author_avatar, post.author_name?.charAt(0) || 'T', API_BASE_URL, 40)} 
                                                alt={post.author_name}
                                                className="author-avatar"
                                              />
                                              <div>
                                                <strong>{post.author_name}</strong>
                                                <span className="post-type">{post.post_type}</span>
                                              </div>
                                            </div>
                                            <span className="post-date">
                                              {new Date(post.created_at).toLocaleDateString()}
                                            </span>
                                          </div>
                                          {post.title && <h4>{post.title}</h4>}
                                          {post.content && <p>{post.content}</p>}
                                          {post.file_url && (
                                            <div className="post-file">
                                              <i className="material-icons">attach_file</i>
                                              <a href={`${API_BASE_URL}${post.file_url}`} target="_blank" rel="noopener noreferrer">
                                                {post.file_name || 'Download File'}
                                              </a>
                                              {post.file_size && <span>({(post.file_size / 1024).toFixed(1)} KB)</span>}
                                            </div>
                                          )}
                                          {post.link_url && (
                                            <div className="post-link">
                                              <i className="material-icons">link</i>
                                              <a href={post.link_url} target="_blank" rel="noopener noreferrer">
                                                {post.link_url}
                                              </a>
                                            </div>
                                          )}
                                          {post.comments && post.comments.length > 0 && (
                                            <div className="post-comments">
                                              <h5>Comments ({post.comments.length})</h5>
                                              {post.comments.map(comment => (
                                                <div key={comment.id} className="comment">
                                                  <img 
                                                    src={getSafeAvatarUrl(comment.commenter_avatar, comment.commenter_name?.charAt(0) || 'U', API_BASE_URL, 32)} 
                                                    alt={comment.commenter_name}
                                                    className="commenter-avatar"
                                                  />
                                                  <div className="comment-content">
                                                    <strong>{comment.commenter_name}</strong>
                                                    <p>{comment.comment}</p>
                                                    <span className="comment-date">
                                                      {new Date(comment.created_at).toLocaleDateString()}
                                                    </span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {activeWorkshopTab === 'materials' && isEnrolledInWorkshop && (
                                <div className="workshop-materials">
                                  <h3>Workshop Materials</h3>
                                  {workshopChannelPosts.filter(p => p.post_type === 'material').length === 0 ? (
                                    <div className="no-results">
                                      <i className="material-icons">folder</i>
                                      <h3>No materials yet</h3>
                                      <p>The teacher will upload materials here.</p>
                                    </div>
                                  ) : (
                                    <div className="materials-list">
                                      {workshopChannelPosts
                                        .filter(p => p.post_type === 'material')
                                        .map(post => (
                                          <div key={post.id} className="material-item">
                                            <i className="material-icons">description</i>
                                            <div className="material-info">
                                              <h4>{post.title || post.file_name || 'Material'}</h4>
                                              {post.content && <p>{post.content}</p>}
                                              {post.file_size && <span>{(post.file_size / 1024).toFixed(1)} KB</span>}
                                            </div>
                                            <a 
                                              href={`${API_BASE_URL}${post.file_url}`} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="download-btn"
                                            >
                                              <i className="material-icons">download</i>
                                              Download
                                            </a>
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          /* Workshops List View */
                          <>
                            <button 
                              className="back-button"
                              onClick={() => {
                                setShowWorkshopView(false);
                                setRecordedCourses([]);
                              }}
                              title="Back to Support Classes"
                            >
                              <i className="material-icons">arrow_back</i>
                            </button>
                            
                            <div className="user-section-header">
                              <div className="user-section-title">
                                <i className="material-icons">live_tv</i>
                                Workshops
                              </div>
                              <p className="user-section-subtitle">
                                Interactive live sessions with expert guidance
                              </p>
                            </div>

                            {/* Workshop Features Info */}
                            <div className="support-class-features-info">
                              <div className="feature-item">
                                <i className="material-icons">groups</i>
                                <span>Live Sessions</span>
                              </div>
                              <div className="feature-item">
                                <i className="material-icons">psychology</i>
                                <span>Expert Guidance</span>
                              </div>
                              <div className="feature-item">
                                <i className="material-icons">question_answer</i>
                                <span>Real-time Q&A</span>
                              </div>
                            </div>

                            {/* Workshops Grid */}
                            {loadingWorkshop ? (
                              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                                <i className="material-icons" style={{ fontSize: '48px', color: 'var(--accent)', marginBottom: '16px' }}>hourglass_empty</i>
                                <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>Loading workshops...</p>
                              </div>
                            ) : recordedCourses.length === 0 ? (
                              <div className="no-results">
                                <i className="material-icons">live_tv</i>
                                <h3>No workshops available</h3>
                                <p>No upcoming workshops found at the moment.</p>
                              </div>
                            ) : (
                              <div className="courses-container">
                                {recordedCourses.map(workshop => (
                                  <div key={workshop.id} className="course-card" onClick={() => openWorkshopDetail(workshop.id)}>
                                    <div className="course-image">
                                      <img src={`https://via.placeholder.com/300x200/FF9800/ffffff?text=${encodeURIComponent(workshop.title?.charAt(0) || 'W')}`} alt={workshop.title || 'Workshop'} />
                                      <div className="featured-badge" style={{ background: '#FF9800' }}>Workshop</div>
                                    </div>
                                    <div className="course-content">
                                      <h3 className="course-title">{workshop.title}</h3>
                                      <p className="course-instructor">{t('user.courseLibrary.by')} {workshop.teacher_name || 'Expert Teacher'}</p>
                                      <div className="course-meta">
                                        {workshop.start_date && (
                                          <span className="course-duration">
                                            <i className="material-icons">event</i>
                                            {new Date(workshop.start_date).toLocaleDateString()}
                                          </span>
                                        )}
                                        <span className="course-students">
                                          {workshop.enrolled_count || 0} enrolled
                                        </span>
                                      </div>
                                      <div className="course-actions">
                                        <button 
                                          className="user-course-btn primary" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openWorkshopDetail(workshop.id);
                                          }}
                                        >
                                          <i className="material-icons">visibility</i>
                                          View Details
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : showNormalClassesView ? (
                      /* Normal Classes View */
                      <div className="user-section">
                        {selectedNormalClass ? (
                          /* Normal Class Detail View */
                          <>
                            <button 
                              className="back-button"
                              onClick={() => {
                                setSelectedNormalClass(null);
                                setShowNormalClassesView(false);
                              }}
                              title="Back to Normal Classes"
                            >
                              <i className="material-icons">arrow_back</i>
                            </button>
                            
                            <div className="user-section-header">
                              <div className="user-section-title">
                                <i className="material-icons">class</i>
                                {selectedNormalClass.title}
                              </div>
                              <p className="user-section-subtitle">
                                {selectedNormalClass.description || 'Structured curriculum with regular assessments'}
                              </p>
                            </div>

                            <div className="normal-class-details">
                              <div className="class-info-card">
                                <h3>Class Information</h3>
                                <div className="info-grid">
                                  <div className="info-item">
                                    <i className="material-icons">person</i>
                                    <div>
                                      <strong>Teacher:</strong>
                                      <span>{selectedNormalClass.teacher_name || 'Expert Teacher'}</span>
                                    </div>
                                  </div>
                                  <div className="info-item">
                                    <i className="material-icons">subject</i>
                                    <div>
                                      <strong>Subject:</strong>
                                      <span>{selectedNormalClass.subject} - {selectedNormalClass.year}</span>
                                    </div>
                                  </div>
                                  <div className="info-item">
                                    <i className="material-icons">people</i>
                                    <div>
                                      <strong>Enrolled:</strong>
                                      <span>{selectedNormalClass.current_enrollments || 0} / {selectedNormalClass.enrollment_target || 30}</span>
                                    </div>
                                  </div>
                                  <div className="info-item">
                                    <i className="material-icons">attach_money</i>
                                    <div>
                                      <strong>Price:</strong>
                                      <span>${selectedNormalClass.price || 0} per month</span>
                                    </div>
                                  </div>
                                  <div className="info-item">
                                    <i className="material-icons">schedule</i>
                                    <div>
                                      <strong>Schedule:</strong>
                                      <span>One session per week (12 weeks)</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {selectedNormalClass.materials && selectedNormalClass.materials.length > 0 && (
                                <div className="class-materials">
                                  <h3>Course Materials</h3>
                                  <div className="materials-list">
                                    {selectedNormalClass.materials.map(material => (
                                      <div key={material.id} className="material-item">
                                        <i className="material-icons">description</i>
                                        <div className="material-info">
                                          <h4>{material.title}</h4>
                                          {material.description && <p>{material.description}</p>}
                                          {material.file_size && <span>{material.file_size}</span>}
                                        </div>
                                        {material.file_url && (
                                          <a 
                                            href={`${API_BASE_URL}${material.file_url}`} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="download-btn"
                                          >
                                            <i className="material-icons">download</i>
                                            Download
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          /* Normal Classes List View */
                          <>
                            <button 
                              className="back-button"
                              onClick={() => {
                                setShowNormalClassesView(false);
                                setNormalClasses([]);
                              }}
                              title="Back to Support Classes"
                            >
                              <i className="material-icons">arrow_back</i>
                            </button>
                            
                            <div className="user-section-header">
                              <div className="user-section-title">
                                <i className="material-icons">class</i>
                                Normal Classes
                              </div>
                              <p className="user-section-subtitle">
                                Structured curriculum with regular assessments
                              </p>
                            </div>

                            {/* Normal Class Features Info */}
                            <div className="support-class-features-info">
                              <div className="feature-item">
                                <i className="material-icons">school</i>
                                <span>Structured Curriculum</span>
                              </div>
                              <div className="feature-item">
                                <i className="material-icons">assessment</i>
                                <span>Regular Assessments</span>
                              </div>
                              <div className="feature-item">
                                <i className="material-icons">trending_up</i>
                                <span>Progress Tracking</span>
                              </div>
                            </div>

                            {/* Normal Classes Grid */}
                            {loadingNormalClasses ? (
                              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                                <i className="material-icons" style={{ fontSize: '48px', color: 'var(--accent)', marginBottom: '16px' }}>hourglass_empty</i>
                                <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>Loading normal classes...</p>
                              </div>
                            ) : normalClasses.length === 0 ? (
                              <div className="no-results">
                                <i className="material-icons">class</i>
                                <h3>No normal classes available</h3>
                                <p>No active normal classes found at the moment.</p>
                              </div>
                            ) : (
                              <div className="courses-container">
                                {normalClasses.map(normalClass => (
                                  <div key={normalClass.id} className="course-card" onClick={() => openNormalClassDetail(normalClass.id)}>
                                    <div className="course-image">
                                      <img src={`https://via.placeholder.com/300x200/4CAF50/ffffff?text=${encodeURIComponent(normalClass.title?.charAt(0) || 'N')}`} alt={normalClass.title || 'Normal Class'} />
                                      <div className="featured-badge" style={{ background: '#4CAF50' }}>Normal Class</div>
                                    </div>
                                    <div className="course-content">
                                      <h3 className="course-title">{normalClass.title}</h3>
                                      <p className="course-instructor">{t('user.courseLibrary.by')} {normalClass.teacher_name || 'Expert Teacher'}</p>
                                      <div className="course-meta">
                                        <span className="course-duration">
                                          <i className="material-icons">schedule</i>
                                          Weekly Sessions
                                        </span>
                                        <span className="course-students">
                                          {normalClass.current_enrollments || 0} enrolled
                                        </span>
                                      </div>
                                      <div className="course-actions">
                                        <button 
                                          className="user-course-btn primary" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openNormalClassDetail(normalClass.id);
                                          }}
                                        >
                                          <i className="material-icons">visibility</i>
                                          View Details
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : showRecordedCoursesView ? (
                      /* Recorded Courses View - After Selection */
                      <div className="user-section">
                        <button 
                          className="back-button"
                          onClick={handleBackToSupportClasses}
                          title="Back to Support Classes"
                        >
                          <i className="material-icons">arrow_back</i>
                        </button>
                        <div className="user-section-header">
                          <div className="user-section-title">
                            <i className="material-icons">play_circle</i>
                            Recorded Courses{selectedSubject && selectedYear ? ` - ${selectedSubject} (${selectedYear})` : ''}
                          </div>
                          <p className="user-section-subtitle">
                            Learn at your own pace with pre-recorded lessons
                          </p>
                        </div>

                        {/* Course Features Info */}
                        <div className="support-class-features-info">
                          <div className="feature-item">
                            <i className="material-icons">schedule</i>
                            <span>24/7 Access</span>
                          </div>
                          <div className="feature-item">
                            <i className="material-icons">video_library</i>
                            <span>Pre-recorded Lessons</span>
                          </div>
                          <div className="feature-item">
                            <i className="material-icons">self_improvement</i>
                            <span>Self-Paced Learning</span>
                          </div>
                        </div>

                        {/* Courses Grid */}
                        {loadingRecordedCourses ? (
                          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <i className="material-icons" style={{ fontSize: '48px', color: 'var(--accent)', marginBottom: '16px' }}>hourglass_empty</i>
                            <p style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>Loading courses...</p>
                          </div>
                        ) : recordedCourses.length === 0 ? (
                          <div className="no-results">
                            <i className="material-icons">video_library</i>
                            <h3>No courses available</h3>
                            <p>No recorded courses found for {selectedSubject} - {selectedYear}</p>
                          </div>
                        ) : (
                          <div className="courses-container">
                            {recordedCourses.map(course => (
                              <div key={course.id} className="course-card">
                                <div className="course-image">
                                  <img src={course.image || `https://via.placeholder.com/300x200/667eea/ffffff?text=${encodeURIComponent(course.title.charAt(0))}`} alt={course.title || 'Course'} />
                                  {course.featured && (
                                    <div className="featured-badge">{t('user.courseLibrary.featured')}</div>
                                  )}
                                </div>
                                <div className="course-content">
                                  <h3 className="course-title">{course.title}</h3>
                                  <p className="course-instructor">{t('user.courseLibrary.by')} {course.instructor}</p>
                                  <div className="course-rating">
                                    {generateStars(course.rating)}
                                    <span className="rating-text">{course.rating} ({course.reviewCount} {t('user.courseLibrary.reviews')})</span>
                                  </div>
                                  <div className="course-meta">
                                    <span className="course-duration">{course.duration}</span>
                                    <span className="course-students">{course.students.toLocaleString()} {t('user.courseLibrary.students')}</span>
                                  </div>
                                  <div className="course-actions">
                                    <button 
                                      className="user-course-btn primary" 
                                      onClick={() => openCourseDetailModal(course.id)}
                                    >
                                      <i className="material-icons">play_circle</i>
                                      Start Learning
                                    </button>
                                    <button 
                                      className="user-course-btn" 
                                      onClick={() => addToCourseWishlist(course.id)}
                                    >
                                      <i className="material-icons">favorite_border</i>
                                      {t('user.courseLibrary.wishlist')}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Support Classes Selection View */
                      <div className="user-section">
                        <div className="user-section-header">
                          <div className="user-section-title">
                            <i className="material-icons">support_agent</i>
                            {t('user.courseLibrary.tabs.supportClasses')}
                          </div>
                          <p className="user-section-subtitle">Choose your learning path: Recorded Courses, Workshops, or Normal Classes</p>
                        </div>

                    <div className="support-classes-container">
                      {/* Recorded Courses Card */}
                      <div className="support-class-card recorded-courses">
                        <div className="support-class-thumbnail">
                          <img src={courseTypeImages.recorded} alt="Recorded Courses" />
                          <div className="support-class-badge">Self-Paced</div>
                        </div>
                        <div className="support-class-content">
                          <h3 className="support-class-title">Recorded Courses</h3>
                          <p className="support-class-subtitle">Learn at your own pace with pre-recorded lessons</p>
                          <div className="support-class-features">
                            <div className="feature-item">
                              <i className="material-icons">schedule</i>
                              <span>24/7 Access</span>
                            </div>
                            <div className="feature-item">
                              <i className="material-icons">video_library</i>
                              <span>Pre-recorded Lessons</span>
                            </div>
                            <div className="feature-item">
                              <i className="material-icons">self_improvement</i>
                              <span>Self-Paced Learning</span>
                            </div>
                          </div>
                          <button 
                            className="support-class-btn primary blue"
                            onClick={() => openSupportClassModal('recorded')}
                          >
                            <i className="material-icons">play_circle</i>
                            Full Learning Mode
                          </button>
                        </div>
                      </div>

                      {/* Workshops Card */}
                      <div className="support-class-card workshops">
                        <div className="support-class-thumbnail">
                          <img src={courseTypeImages.workshops} alt="Workshops" />
                          <div className="support-class-badge">Live Sessions</div>
                        </div>
                        <div className="support-class-content">
                          <h3 className="support-class-title">Workshops</h3>
                          <p className="support-class-subtitle">Interactive live sessions with expert guidance</p>
                          <div className="support-class-features">
                            <div className="feature-item">
                              <i className="material-icons">groups</i>
                              <span>Live Sessions</span>
                            </div>
                            <div className="feature-item">
                              <i className="material-icons">psychology</i>
                              <span>Expert Guidance</span>
                            </div>
                            <div className="feature-item">
                              <i className="material-icons">question_answer</i>
                              <span>Real-time Q&A</span>
                            </div>
                          </div>
                          <button 
                            className="support-class-btn primary orange"
                            onClick={() => openSupportClassModal('workshops')}
                          >
                            <i className="material-icons">live_tv</i>
                            Full Learning Mode
                          </button>
                        </div>
                      </div>

                      {/* Normal Classes Card */}
                      <div className="support-class-card normal-classes">
                        <div className="support-class-thumbnail">
                          <img src={courseTypeImages.normal} alt="Normal Classes" />
                          <div className="support-class-badge">Structured</div>
                        </div>
                        <div className="support-class-content">
                          <h3 className="support-class-title">Normal Classes</h3>
                          <p className="support-class-subtitle">Structured curriculum with regular assessments</p>
                          <div className="support-class-features">
                            <div className="feature-item">
                              <i className="material-icons">school</i>
                              <span>Structured Curriculum</span>
                            </div>
                            <div className="feature-item">
                              <i className="material-icons">assessment</i>
                              <span>Regular Assessments</span>
                            </div>
                            <div className="feature-item">
                              <i className="material-icons">trending_up</i>
                              <span>Progress Tracking</span>
                            </div>
                          </div>
                          <button 
                            className="support-class-btn primary green"
                            onClick={() => openSupportClassModal('normal')}
                          >
                            <i className="material-icons">class</i>
                            Full Learning Mode
                          </button>
                        </div>
                      </div>
                    </div>
                      </div>
                    )}
                  </>
                )}

                {/* My Learning Section */}
                {activeCourseLibraryTab === 'my-learning' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">school</i>
                        My Learning
                      </div>
                      <p className="user-section-subtitle">Continue your enrolled courses</p>
                    </div>

                    <div className="my-learning-container">
                      {myLearning.length === 0 ? (
                        <div className="no-courses">
                          <i className="material-icons">school</i>
                          <h3>No enrolled courses</h3>
                          <p>Enroll in a course to start learning!</p>
                        </div>
                      ) : (
                        myLearning.map(course => (
                          <div key={course.id} className="learning-course-card">
                            <div className="course-progress">
                              <div className="progress-circle">
                                <div className="progress-fill" style={{ transform: `rotate(${course.progress * 3.6}deg)` }}></div>
                                <span className="progress-text">{course.progress}%</span>
                              </div>
                            </div>
                            <div className="course-info">
                              <h3 className="course-title">{course.title}</h3>
                              <p className="course-instructor">by {course.instructor}</p>
                              <div className="course-stats">
                                <span>{course.completedLessons}/{course.totalLessons} lessons completed</span>
                                <span>Last accessed: {formatDate(course.lastAccessed)}</span>
                              </div>
                              <div className="next-lesson">
                                <strong>Next lesson:</strong> {course.nextLesson}
                              </div>
                            </div>
                            <div className="course-actions">
                              <button className="user-course-btn primary">
                                <i className="material-icons">play_arrow</i>
                                Continue
                              </button>
                              <button className="user-course-btn">
                                <i className="material-icons">info</i>
                                Details
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Wishlist Section */}
                {activeCourseLibraryTab === 'wishlist' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">favorite</i>
                        {t('user.courseLibrary.myWishlist')}
                      </div>
                      <p className="user-section-subtitle">{t('user.courseLibrary.savedForLater')}</p>
                    </div>

                    <div className="wishlist-container">
                      {courseWishlist.length === 0 ? (
                        <div className="no-wishlist">
                          <i className="material-icons">favorite_border</i>
                          <h3>{t('user.courseLibrary.noWishlistItems')}</h3>
                          <p>{t('user.courseLibrary.addToWishlist')}</p>
                        </div>
                      ) : (
                        courseWishlist.map(course => (
                          <div key={course.id} className="wishlist-course-card">
                            <div className="course-info">
                              <h3 className="course-title">{course.title}</h3>
                              <p className="course-instructor">{t('user.courseLibrary.by')} {course.instructor}</p>
                              <div className="course-rating">
                                {generateStars(course.rating)}
                                <span className="rating-text">{course.rating} ({course.reviewCount} {t('user.courseLibrary.reviews')})</span>
                              </div>
                            </div>
                            <div className="course-price">
                              <span className="price">${course.price}</span>
                              {course.originalPrice > course.price && (
                                <span className="original-price">${course.originalPrice}</span>
                              )}
                            </div>
                            <div className="course-actions">
                              <button 
                                className="user-course-btn primary" 
                                onClick={() => enrollCourse(course.id)}
                              >
                                <i className="material-icons">shopping_cart</i>
                                {t('user.courseLibrary.enrollNow')}
                              </button>
                              <button 
                                className="user-course-btn danger" 
                                onClick={() => removeFromCourseWishlist(course.id)}
                              >
                                <i className="material-icons">delete</i>
                                {t('user.courseLibrary.remove')}
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Certificates Section */}
                {activeCourseLibraryTab === 'certificates' && (
                  <div className="user-section">
                    <div className="user-section-header">
                      <div className="user-section-title">
                        <i className="material-icons">verified</i>
                        My Certificates
                      </div>
                      <p className="user-section-subtitle">Your earned certificates and achievements</p>
                    </div>

                    <div className="certificates-container">
                      {certificates.length === 0 ? (
                        <div className="no-certificates">
                          <i className="material-icons">verified</i>
                          <h3>No certificates yet</h3>
                          <p>Complete courses to earn certificates!</p>
                        </div>
                      ) : (
                        certificates.map(certificate => (
                          <div key={certificate.id} className="certificate-card">
                            <div className="certificate-icon">
                              <i className="material-icons">verified</i>
                            </div>
                            <div className="certificate-info">
                              <h3 className="certificate-title">{certificate.title}</h3>
                              <p className="certificate-instructor">by {certificate.instructor}</p>
                              <div className="certificate-meta">
                                <span className="certificate-grade">Grade: {certificate.grade}</span>
                                <span className="certificate-hours">{certificate.hours} hours</span>
                                <span className="certificate-date">Completed: {formatDate(certificate.completedDate)}</span>
                              </div>
                            </div>
                            <div className="certificate-actions">
                              <button className="user-course-btn primary">
                                <i className="material-icons">download</i>
                                Download
                              </button>
                              <button className="user-course-btn">
                                <i className="material-icons">share</i>
                                Share
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 'subscription':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.subscription.title')}</h1>
              <p className="user-content-subtitle">{t('user.subscription.subtitle')}</p>
            </div>
            <UserSectionSubscription />
          </div>
        );
      case 'profile':
        return (
          <div className="user-main-content" key={`profile-wrapper-${sectionKey}`}>
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.profileSettings.title')}</h1>
              <p className="user-content-subtitle">{t('user.profileSettings.subtitle')}</p>
            </div>
            <ProfileSettings key={`profile-${sectionKey}`} />
          </div>
        );
      case 'profile-settings':
        return (
          <div className="user-main-content" key={`profile-settings-wrapper-${sectionKey}`}>
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.profileSettings.title')}</h1>
              <p className="user-content-subtitle">{t('user.profileSettings.subtitle')}</p>
            </div>
            <ProfileSettings key={`profile-settings-${sectionKey}`} />
          </div>
        );
      case 'account-settings':
        return (
          <div className="user-main-content" key={`account-settings-wrapper-${sectionKey}`}>
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.accountSettings.title')}</h1>
              <p className="user-content-subtitle">{t('user.accountSettings.subtitle')}</p>
            </div>
            <AccountSettings key={`account-settings-${sectionKey}`} />
          </div>
        );
      case 'support':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">{t('user.support.title')}</h1>
              <p className="user-content-subtitle">{t('user.support.subtitle')}</p>
            </div>
            <UserSectionSupport />
          </div>
        );
      case 'vocabulary-placement-test':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <div className="user-content-header-top">
                <button className="back-btn" onClick={() => setActiveSection('explore')}>
                  <i className="material-icons">arrow_back</i>
                  <span>{t('user.exploreCourses.backToExplore')}</span>
                </button>
                <h1 className="user-content-title">📊 Vocabulary Placement Test</h1>
              </div>
              <p className="user-content-subtitle">Choose a path to assess your vocabulary and get a personalized plan</p>
            </div>

            {!activePlacementPath ? (
              <div className="user-section">
                <div className="dictation-paths-grid">
                  <div className="dictation-path-card" onClick={() => setActivePlacementPath('educational-levels')}>
                    <div className="path-icon">📚</div>
                    <h3>Educational Levels Vocabulary Test</h3>
                    <p>Primary → Middle → High School (by year)</p>
                  </div>

                  <div className="dictation-path-card" onClick={() => setActivePlacementPath('general')}>
                    <div className="path-icon">📖</div>
                    <h3>General English Vocabulary Test</h3>
                    <p>Everyday + general academic words (CEFR A1–C2)</p>
                  </div>

                  <div className="dictation-path-card" onClick={() => setActivePlacementPath('international-exam')}>
                    <div className="path-icon">🌍</div>
                    <h3>International Exams Vocabulary Test</h3>
                    <p>IELTS, TOEFL, Cambridge words and collocations</p>
                  </div>

                  <div className="dictation-path-card" onClick={() => setActivePlacementPath('esp')}>
                    <div className="path-icon">🎓</div>
                    <h3>ESP Vocabulary Test</h3>
                    <p>Medicine, Engineering, Law, Business…</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="user-section">
                {activePlacementPath === 'educational-levels' && (
                  <>
                    <div className="user-section-header">
                      <div className="user-section-header-top">
                        <button className="back-btn" onClick={() => setActivePlacementPath(null)}>
                          <i className="material-icons">arrow_back</i>
                          <span>Back to Placement Paths</span>
                        </button>
                        <div className="user-section-title">
                          <i className="material-icons">school</i>
                          Educational Levels – Choose a mode
                        </div>
                      </div>
                      <p className="user-section-subtitle">Check a specific year or find your overall school-year level</p>
                    </div>

                    <div className="dictation-paths-grid">
                      <div className="dictation-path-card" onClick={() => setActivePlacementMode('check-year')}>
                        <div className="path-icon">🧪</div>
                        <h3>Check My Year</h3>
                        <p>Pick a year and test only that year's vocabulary</p>
                      </div>
                      <div className="dictation-path-card" onClick={() => setActivePlacementMode('find-level')}>
                        <div className="path-icon">🎯</div>
                        <h3>Find My Level</h3>
                        <p>Adaptive test across years to place your level</p>
                      </div>
                    </div>

                    {activePlacementMode === 'check-year' && (
                      <div className="user-section" style={{ marginTop: 16 }}>
                        <div className="user-section-header">
                          <div className="user-section-title">
                            <i className="material-icons">calendar_today</i>
                            Select a year to test
                          </div>
                        </div>
                        <div className="educational-tabs">
                          {schoolLevels.map(level => (
                            <button
                              key={level.id}
                              className={`educational-tab ${readingActiveSchoolLevel === level.id ? 'active' : ''}`}
                              onClick={() => setReadingActiveSchoolLevel(level.id)}
                            >
                              <i className="material-icons">school</i>
                              {level.name}
                            </button>
                          ))}
                        </div>
                        <div className="year-tabs">
                          {schoolLevels.find(level => level.id === readingActiveSchoolLevel)?.years.map((year, index) => (
                            <button
                              key={index}
                              className={`year-tab ${readingActiveYearLevel === `${readingActiveSchoolLevel}-${index + 1}` ? 'active' : ''}`}
                              onClick={() => setReadingActiveYearLevel(`${readingActiveSchoolLevel}-${index + 1}`)}
                            >
                              {year}
                            </button>
                          ))}
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <button className="user-course-btn primary">
                            <i className="material-icons">play_arrow</i>
                            Start Year Test
                          </button>
                        </div>
                      </div>
                    )}

                    {activePlacementMode === 'find-level' && (
                      <div className="user-section" style={{ marginTop: 16 }}>
                        <div className="user-section-header">
                          <div className="user-section-title">
                            <i className="material-icons">auto_graph</i>
                            Adaptive Placement (Find My Level)
                          </div>
                        </div>
                        <p className="user-section-subtitle">The test will adjust difficulty and stop when it has enough data to place your level</p>
                        <button className="user-course-btn primary">
                          <i className="material-icons">play_arrow</i>
                          Start Adaptive Test
                        </button>
                      </div>
                    )}
                  </>
                )}

                {activePlacementPath === 'general' && (
                  <>
                    <div className="user-section-header">
                      <div className="user-section-header-top">
                        <button className="back-btn" onClick={() => setActivePlacementPath(null)}>
                          <i className="material-icons">arrow_back</i>
                          <span>Back to Placement Paths</span>
                        </button>
                        <div className="user-section-title">
                          <i className="material-icons">language</i>
                          General English Vocabulary Test
                        </div>
                      </div>
                      <p className="user-section-subtitle">Everyday + general academic words; mapped to CEFR A1–C2</p>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <ComingSoonOverlay onClose={() => setActivePlacementPath(null)} />
                      <button className="user-course-btn primary">
                        <i className="material-icons">play_arrow</i>
                        Start Test
                      </button>
                    </div>
                  </>
                )}

                {activePlacementPath === 'international-exam' && (
                  <>
                    <div className="user-section-header">
                      <div className="user-section-header-top">
                        <button className="back-btn" onClick={() => setActivePlacementPath(null)}>
                          <i className="material-icons">arrow_back</i>
                          <span>Back to Placement Paths</span>
                        </button>
                        <div className="user-section-title">
                          <i className="material-icons">public</i>
                          International Exams Vocabulary Test
                        </div>
                      </div>
                      <p className="user-section-subtitle">IELTS, TOEFL, Cambridge exam vocabulary readiness</p>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <ComingSoonOverlay onClose={() => setActivePlacementPath(null)} />
                      <button className="user-course-btn primary">
                        <i className="material-icons">play_arrow</i>
                        Start Test
                      </button>
                    </div>
                  </>
                )}

                {activePlacementPath === 'esp' && (
                  <>
                    <div className="user-section-header">
                      <div className="user-section-header-top">
                        <button className="back-btn" onClick={() => setActivePlacementPath(null)}>
                          <i className="material-icons">arrow_back</i>
                          <span>Back to Placement Paths</span>
                        </button>
                        <div className="user-section-title">
                          <i className="material-icons">science</i>
                          ESP Vocabulary Test
                        </div>
                      </div>
                      <p className="user-section-subtitle">Field-specific vocabulary (Medicine, Engineering, Law, Business…)</p>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <ComingSoonOverlay onClose={() => setActivePlacementPath(null)} />
                      <button className="user-course-btn primary">
                        <i className="material-icons">play_arrow</i>
                        Start Test
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      case 'assignments':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">Assignments</h1>
              <p className="user-content-subtitle">View and submit your assignments</p>
            </div>
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="higher-position" />
              <div className="user-section-header">
                <div className="user-section-title">
                  <i className="material-icons">assignment</i>
                  My Assignments
                </div>
              </div>
            </div>
          </div>
        );
      case 'virtual-classroom':
        return (
          <div className="user-main-content">
            <div className="user-content-header">
              <h1 className="user-content-title">Virtual Classroom</h1>
              <p className="user-content-subtitle">Join live sessions and forums</p>
            </div>
            <div className="user-section" style={{ position: 'relative' }}>
              <ComingSoonOverlay onClose={() => setActiveSection('dashboard')} className="lower-position" />
              <div className="user-section-header">
                <div className="user-section-title">
                  <i className="material-icons">video_camera_front</i>
                  Virtual Classroom
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return renderDashboardContent();
    }
  };

  // Add search functionality for vocabulary content
  const [vocabularySearchTerm, setVocabularySearchTerm] = useState('');
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Filter articles based on search term
  useEffect(() => {
    if (articles.length > 0) {
      const filtered = articles.filter(article => 
        article.title.toLowerCase().includes(vocabularySearchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(vocabularySearchTerm.toLowerCase())
      );
      setFilteredArticles(filtered);
    } else {
      setFilteredArticles([]);
    }
  }, [articles, vocabularySearchTerm]);

  // Fetch dictation articles when dictation path or level/year changes
  useEffect(() => {
    if (dictationPathsOpen && activeDictationPath === 'educational-levels') {
      fetchDictationArticles(activeDictationSchoolLevel, activeDictationYear);
    }
  }, [dictationPathsOpen, activeDictationPath, activeDictationSchoolLevel, activeDictationYear]);
  const handleTopBarSearch = (query) => {
    // Handle global search from TopBar for user dashboard
    console.log('User dashboard global search:', query);
    // You can implement search functionality here
    // For example: filter content, navigate to search results, etc.
  };

  const handleTopBarNotificationClick = () => {
    // Handle notification click from TopBar
    setActiveSection('notifications');
  };

  const handleTopBarTabChange = (section) => {
    // Handle tab/section change from TopBar
    // Force re-render by incrementing key when clicking the same section
    if (activeSection === section) {
      setSectionKey(prev => prev + 1);
    }
    setActiveSection(section);
  };

  // Wrapper function for Sidebar to handle section changes with key increment
  const handleSidebarSectionChange = (section) => {
    // Force re-render by incrementing key when clicking the same section
    if (activeSection === section) {
      setSectionKey(prev => prev + 1);
    }
    setActiveSection(section);
  };

  // If hideSidebar is true, only render the content (for embedding in Parent/Teacher Dashboard)
  // Keep the same overlays as the full dashboard so Start Learning can open articles and dictation.

  // Normal rendering with optional sidebar and TopBar
  return (
    <div className={`user-dashboard${hideSidebar ? ' user-dashboard-embedded' : ''}`}>
      {!hideSidebar && <TawkTo />}
      {/* Mobile overlay/backdrop */}
      {!hideSidebar && sidebarOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      {!hideSidebar && (
        <UserSidebar 
          activeSection={activeSection} 
          setActiveSection={(section) => {
            handleSidebarSectionChange(section);
            // Close sidebar on mobile after selecting a section
            if (window.innerWidth <= 768) {
              setSidebarOpen(false);
            }
          }}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      )}
      {!hideSidebar && (
        <TopBar 
          onSearch={handleTopBarSearch} 
          onNotificationClick={handleTopBarNotificationClick}
          onTabChange={handleTopBarTabChange}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
          isPremium={isPremium}
          subscriptionEndsAt={subscriptionEndsAt}
          onUpgradeClick={() => setActiveSection('subscription')}
        />
      )}
      <div className="user-main-content-wrapper">
        <ErrorBoundary name="user-dashboard-section" resetKey={activeSection}>
          <SectionContent render={renderSectionContent} />
        </ErrorBoundary>
      </div>
      
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <span className="notification-text">{notification.message}</span>
          <button 
            className="notification-close" 
            onClick={() => setNotification({ show: false, message: '', type: 'success' })}
          >
            <i className="material-icons">close</i>
          </button>
        </div>
      )}

      {/* Booking Modal */}
      {bookingModal.show && (
        <div className="modal-overlay" onClick={closeBookingModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeBookingModal}>&times;</span>
            <h2>Book a Lesson</h2>
            <div className="teacher-info">
              <img src={bookingModal.teacher?.image || "/placeholder-avatar.jpg"} alt={bookingModal.teacher?.name || "Teacher"} />
              <div>
                <h3>{bookingModal.teacher?.name || "Teacher"}</h3>
                <p>{bookingModal.teacher?.specialty || "Language Teacher"}</p>
                <p>Rate: ${bookingModal.teacher?.hourlyRate || currentTeacherRate}/hour</p>
              </div>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleBookingSubmit({
                date: e.target.lessonDate.value,
                time: e.target.lessonTime.value,
                duration: e.target.lessonDuration.value,
                subject: e.target.lessonSubject.value,
                notes: e.target.lessonNotes.value
              });
            }}>
              <div className="form-group">
                <label htmlFor="lessonDate">Date:</label>
                <input type="date" id="lessonDate" required />
              </div>
              <div className="form-group">
                <label htmlFor="lessonTime">Time:</label>
                <input type="time" id="lessonTime" required />
              </div>
              <div className="form-group">
                <label htmlFor="lessonDuration">Duration:</label>
                <select id="lessonDuration" required>
                  <option value="30">30 minutes</option>
                  <option value="60" selected>1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="lessonSubject">Subject:</label>
                <input type="text" id="lessonSubject" placeholder="e.g., IELTS Speaking, Business English" required />
              </div>
              <div className="form-group">
                <label htmlFor="lessonNotes">Notes (Optional):</label>
                <textarea id="lessonNotes" rows="3" placeholder="Any specific topics or areas you'd like to focus on..."></textarea>
              </div>
              <button type="submit" className="user-course-btn primary">Confirm Booking</button>
            </form>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {ratingModal.show && (
        <div className="modal-overlay" onClick={closeRatingModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeRatingModal}>&times;</span>
            <h2>Rate Your Lesson</h2>
            <div className="lesson-info">
              <img src={ratingModal.teacher?.image || "/placeholder-avatar.jpg"} alt={ratingModal.teacher?.name || "Teacher"} />
              <div>
                <h3>{ratingModal.teacher?.name || "Teacher"}</h3>
                <p>{ratingModal.teacher?.specialty || "Language Teacher"} - Recent Lesson</p>
              </div>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleRatingSubmit({
                overallRating: 5, // This would be dynamic based on star clicks
                comment: e.target.ratingComment.value,
                teachingQuality: 5,
                communication: 5,
                punctuality: 5,
                lessonId: ratingModal.teacher?.id
              });
            }}>
              <div className="rating-stars">
                <label>Overall Rating:</label>
                <div className="stars-container">
                  <span className="star" data-rating="1">★</span>
                  <span className="star" data-rating="2">★</span>
                  <span className="star" data-rating="3">★</span>
                  <span className="star" data-rating="4">★</span>
                  <span className="star" data-rating="5">★</span>
                </div>
                <span className="rating-text">Click to rate</span>
              </div>
              <div className="form-group">
                <label htmlFor="ratingComment">Your Review:</label>
                <textarea id="ratingComment" rows="4" placeholder="Share your experience with this teacher..."></textarea>
              </div>
              <button type="submit" className="user-course-btn primary">Submit Rating</button>
            </form>
          </div>
        </div>
      )}

      {/* Course Detail Modal */}
      {courseDetailModal.show && (
        <div className="modal-overlay" onClick={closeCourseDetailModal}>
          <div className="modal-content course-detail-modal" onClick={(e) => e.stopPropagation()}>
            <span className="modal-close" onClick={closeCourseDetailModal}>&times;</span>
            
            <div className="course-preview">
              <div className="course-image">
                <img src={courseDetailModal.course?.image} alt={courseDetailModal.course?.title} />
                <div className="play-button">
                  <i className="material-icons">play_arrow</i>
                </div>
              </div>
              <div className="course-info">
                <div className="course-meta">
                  <span className="course-level">{courseDetailModal.course?.level}</span>
                  <span className="course-duration">{courseDetailModal.course?.duration}</span>
                  <span className="course-students">{courseDetailModal.course?.students.toLocaleString()} students</span>
                </div>
                <div className="course-rating">
                  <div className="stars">
                    {generateStars(courseDetailModal.course?.rating || 0)}
                  </div>
                  <span className="rating-text">{courseDetailModal.course?.rating} ({courseDetailModal.course?.reviewCount} reviews)</span>
                </div>
                <div className="course-price">
                  <span className="price">${courseDetailModal.course?.price}</span>
                  {courseDetailModal.course?.originalPrice && courseDetailModal.course?.originalPrice > courseDetailModal.course?.price && (
                    <span className="original-price">${courseDetailModal.course?.originalPrice}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="course-description">
              <h3>About this course</h3>
              <p>{courseDetailModal.course?.description}</p>
            </div>

            {/* Preview Videos Section */}
            {courseDetailModal.videos && courseDetailModal.videos.filter(v => v.is_preview).length > 0 && (
              <div className="course-preview-videos">
                <h3>Preview (Free)</h3>
                <div className="preview-videos-list">
                  {courseDetailModal.videos
                    .filter(v => v.is_preview)
                    .map((video, index) => (
                      <div key={video.id || index} className="preview-video-item">
                        <div className="video-thumbnail">
                          <i className="material-icons">play_circle</i>
                          <span className="preview-badge">FREE PREVIEW</span>
                        </div>
                        <div className="video-info">
                          <h4>{video.video_title || `Preview ${index + 1}`}</h4>
                          {video.duration > 0 && (
                            <span className="video-duration">{Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</span>
                          )}
                        </div>
                        <button 
                          className="play-preview-btn"
                          onClick={() => {
                            if (!video.video_url) {
                              console.error('Video URL is missing:', video);
                              showNotification('Video URL not available', 'error');
                              return;
                            }
                            const videoUrl = video.video_url.startsWith('http') 
                              ? video.video_url 
                              : `${API_BASE_URL}${video.video_url}`;
                            console.log('Opening video URL:', videoUrl);
                            // Try to open in new tab, if that fails, try to play inline
                            const newWindow = window.open(videoUrl, '_blank');
                            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                              // Popup blocked or failed, try direct link
                              window.location.href = videoUrl;
                            }
                          }}
                        >
                          <i className="material-icons">play_arrow</i>
                          Watch Preview
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Course Videos Section */}
            {courseDetailModal.videos && courseDetailModal.videos.length > 0 && (
              <div className="course-videos-section">
                <h3>Course Content</h3>
                <div className="course-videos-list">
                  {courseDetailModal.videos.map((video, index) => {
                    const isLocked = !video.is_preview && !courseDetailModal.isEnrolled;
                    return (
                      <div key={video.id || index} className={`course-video-item ${isLocked ? 'locked' : ''}`}>
                        <div className="video-thumbnail">
                          {isLocked ? (
                            <>
                              <i className="material-icons">lock</i>
                              <span className="lock-badge">LOCKED</span>
                            </>
                          ) : (
                            <i className="material-icons">play_circle</i>
                          )}
                        </div>
                        <div className="video-info">
                          <h4>
                            {video.is_preview && <span className="preview-label">Preview: </span>}
                            {video.video_title || `Lesson ${index + 1}`}
                          </h4>
                          {video.duration > 0 && (
                            <span className="video-duration">{Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</span>
                          )}
                        </div>
                        {isLocked ? (
                          <div className="locked-message">
                            <i className="material-icons">lock</i>
                            <span>Enroll to access</span>
                          </div>
                        ) : (
                          <button 
                            className="play-video-btn"
                            onClick={() => {
                              if (!video.video_url) {
                                console.error('Video URL is missing:', video);
                                showNotification('Video URL not available', 'error');
                                return;
                              }
                              const videoUrl = video.video_url.startsWith('http') 
                                ? video.video_url 
                                : `${API_BASE_URL}${video.video_url}`;
                              console.log('Opening video URL:', videoUrl);
                              // Try to open in new tab, if that fails, try to play inline
                              const newWindow = window.open(videoUrl, '_blank');
                              if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                                // Popup blocked or failed, try direct link
                                window.location.href = videoUrl;
                              }
                            }}
                          >
                            <i className="material-icons">play_arrow</i>
                            Watch
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="course-curriculum">
              <h3>What you'll learn</h3>
              <ul>
                {courseDetailModal.course?.learningObjectives?.map((objective, index) => (
                  <li key={index}>{objective}</li>
                ))}
              </ul>
            </div>

            <div className="course-instructor">
              <h3>Instructor</h3>
              <div className="instructor-info">
                <img src={courseDetailModal.course?.instructorImage} alt={courseDetailModal.course?.instructor} />
                <div className="instructor-details">
                  <h4>{courseDetailModal.course?.instructor}</h4>
                  <p>Expert Instructor</p>
                  <p>Professional instructor with years of experience in teaching and industry practice.</p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="user-course-btn" onClick={() => addToCourseWishlist(courseDetailModal.course?.id)}>
                <i className="material-icons">favorite_border</i>
                Add to Wishlist
              </button>
              <button className="user-course-btn primary" onClick={() => enrollCourse(courseDetailModal.course?.id)}>
                <i className="material-icons">shopping_cart</i>
                Enroll Now
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Room Interface Modal */}
      {roomModal.show && (
        <div className="modal-overlay" onClick={leaveRoom}>
          <div className="modal-content room-interface" onClick={(e) => e.stopPropagation()}>
            <div className="room-header">
              <div className="room-info">
                <h2>{roomModal.room?.title}</h2>
                <div className="room-meta">
                  <span className="room-type">
                    {roomModal.room?.type === 'voice' ? '🎧' : roomModal.room?.type === 'text' ? '💬' : '🗣️'} {roomModal.room?.type}
                  </span>
                  <span className="room-language">🌍 {roomModal.room?.language}</span>
                  <span className="room-level">📚 {roomModal.room?.level}</span>
                </div>
              </div>
              <div className="room-actions">
                <button className="btn-secondary" onClick={leaveRoom}>
                  <i className="material-icons">exit_to_app</i>
                  Leave Room
                </button>
                <span className="close" onClick={leaveRoom}>&times;</span>
              </div>
            </div>

            <div className="room-content">
              {/* Voice Panel */}
              <div className="voice-panel">
                <div className="voice-controls">
                  <button className={`voice-btn ${isMicOn ? 'active' : ''}`} onClick={toggleMic}>
                    <i className="material-icons">{isMicOn ? 'mic' : 'mic_off'}</i>
                    <span className="btn-text">Mic {isMicOn ? 'ON' : 'OFF'}</span>
                  </button>
                  <button className={`voice-btn ${isSpeakerOn ? 'active' : ''}`} onClick={toggleSpeaker}>
                    <i className="material-icons">{isSpeakerOn ? 'volume_up' : 'volume_off'}</i>
                    <span className="btn-text">Speaker {isSpeakerOn ? 'ON' : 'OFF'}</span>
                  </button>
                  <button className="voice-btn" onClick={shareScreen}>
                    <i className="material-icons">screen_share</i>
                    <span className="btn-text">Share Screen</span>
                  </button>
                </div>
              </div>

              {/* Chat Area */}
              <div className="chat-area">
                <div className="chat-messages">
                  {chatMessages.map(message => (
                    <div key={message.id} className={`chat-message ${message.isOwn ? 'own' : ''}`}>
                      <div className="message-header">
                        <span className="message-author">{message.author}</span>
                        <span className="message-time">{message.time}</span>
                      </div>
                      <div className="message-content">{message.content}</div>
                    </div>
                  ))}
                </div>
                <div className="chat-input">
                  <input 
                    type="text" 
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={handleMessageKeyPress}
                  />
                  <button onClick={sendMessage}>
                    <i className="material-icons">send</i>
                  </button>
                </div>
              </div>

              {/* Participants Panel */}
              <div className="participants-panel">
                <h3>Participants ({participants.length})</h3>
                <div className="participants-list">
                  {participants.map(participant => (
                    <div key={participant.id} className="participant-item">
                      <div className="participant-avatar">{participant.avatar}</div>
                      <div className="participant-info">
                        <span className="participant-name">{participant.name}</span>
                        <span className="participant-country">{participant.country}</span>
                      </div>
                      <div className="participant-status">
                        {participant.isMuted && <i className="material-icons">mic_off</i>}
                        {participant.isOwner && <span className="owner-badge">Owner</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report User Modal */}
      {reportModal.show && (
        <div className="modal-overlay" onClick={closeReportModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <span className="close" onClick={closeReportModal}>&times;</span>
            <h2>Report {reportModal.target?.type === 'user' ? 'User' : 'Room'}</h2>
            <form onSubmit={handleReportSubmit}>
              <div className="form-group">
                <label htmlFor="reportReason" className="required">Reason for Report *</label>
                <select 
                  id="reportReason"
                  value={reportForm.reason}
                  onChange={(e) => setReportForm(prev => ({ ...prev, reason: e.target.value }))}
                  required
                >
                  <option value="">Select a reason</option>
                  <option value="inappropriate">Inappropriate behavior</option>
                  <option value="spam">Spam or advertising</option>
                  <option value="harassment">Harassment or bullying</option>
                  <option value="offensive">Offensive language</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="reportDescription">Description</label>
                <textarea 
                  id="reportDescription"
                  value={reportForm.description}
                  onChange={(e) => setReportForm(prev => ({ ...prev, description: e.target.value }))}
                  rows="4" 
                  placeholder="Please provide details about the incident..."
                ></textarea>
              </div>
              <div className="form-actions">
                <button type="submit" className="form-btn primary">
                  <i className="material-icons">report</i>
                  Submit Report
                </button>
                <button type="button" className="form-btn secondary" onClick={closeReportModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Article Modal */}
      {showArticleModal && selectedArticle && (
        <div
          className="modal-overlay article-read-overlay"
          onPointerDown={onArticlePointerDown}
          onPointerMove={onArticlePointerMove}
          onTouchStart={onArticlePointerDown}
          onTouchMove={onArticlePointerMove}
          onClick={onArticleOverlayClick}
        >
          <div className="modal-content article-read-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header article-title-header">
              <h2 className="article-read-title">{renderTranslatableText(selectedArticle.title, selectedArticle)}</h2>
              <button className="close-modal" onClick={closeArticleModal}>
                <i className="material-icons">close</i>
              </button>
            </div>
            

            
            <div 
              className="modal-body article-read-body"
              ref={(el) => {
                articleModalBodyRef.current = el;
                if (el) {
                  setTimeout(() => highlightWords(el), 100);
                }
              }}
            >
              <ArticleQuizReader
                article={selectedArticle}
                html={selectedArticleHtml || 'No content available'}
                quizLabel={t('user.dashboard.startVocabularyQuiz') || 'Start Quiz'}
                quizDisabled={articleQuizStarting}
                onStartQuiz={startArticleVocabularyQuiz}
              />
            </div>
          </div>
        </div>
      )}

      {/* Exam sequential mastery lock — full layout when user taps a locked article */}
      {examMasteryLockModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="exam-mastery-lock-title"
          onClick={() => setExamMasteryLockModalOpen(false)}
        >
          <div
            className="modal-content exam-mastery-lock-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '440px' }}
          >
            <div className="modal-header">
              <h2 id="exam-mastery-lock-title">
                {t(
                  activeSection === 'educational-levels'
                    ? 'user.educationalSequential.masteryLockModalTitle'
                    : 'user.readingExamSequential.masteryLockModalTitle'
                )}
              </h2>
              <button
                type="button"
                className="close-modal"
                onClick={() => setExamMasteryLockModalOpen(false)}
                aria-label="Close"
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body exam-mastery-lock-modal__body">
              <div className="exam-mastery-lock-modal__icon-wrap" aria-hidden="true">
                <i className="material-icons">lock</i>
              </div>
              <p className="exam-mastery-lock-modal__message">
                {t(
                  activeSection === 'educational-levels'
                    ? 'user.educationalSequential.masteryLockToast'
                    : 'user.readingExamSequential.masteryLockToast'
                )}
              </p>
              <button type="button" className="user-course-btn primary" onClick={() => setExamMasteryLockModalOpen(false)}>
                {t('user.dashboard.retentionQuizClose') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retention Quiz Modal — exam articles open it from Start Vocabulary Quiz; educational articles still open it after close */}
      {showRetentionQuizModal && retentionQuizArticle && (
        <div
          className="modal-overlay"
          onPointerDown={onArticlePointerDown}
          onPointerMove={onArticlePointerMove}
          onTouchStart={onArticlePointerDown}
          onTouchMove={onArticlePointerMove}
          onClick={(e) => {
            if (retentionQuizSubmitting) return;
            if (Date.now() < quizOverlayReadyAtRef.current) return;
            if (e.target !== e.currentTarget) return;
            const gesture = articleGestureRef.current;
            if (gesture.moved || !gesture.downOnOverlay) return;
            closeRetentionQuizModal();
          }}
        >
          <div className="modal-content retention-quiz-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h2>{t('user.dashboard.retentionQuizTitle') || 'Vocabulary quiz'}</h2>
              <button type="button" className="close-modal" onClick={closeRetentionQuizModal} disabled={retentionQuizSubmitting} aria-label="Close">
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {retentionQuizResult?.submitError ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                  <p>{t('user.dashboard.retentionQuizLoadError')}</p>
                  <button type="button" className="user-course-btn primary" style={{ marginTop: '16px' }} onClick={closeRetentionQuizModal}>
                    {t('user.dashboard.retentionQuizClose') || 'Close'}
                  </button>
                </div>
              ) : retentionQuizResult ? (
                <div className="retention-quiz-result">
                  <p style={{ fontSize: '18px', marginBottom: '8px' }}>
                    {t('user.dashboard.retentionQuizScore') || 'Score'}: <strong>{retentionQuizResult.score}%</strong>
                    {retentionQuizResult.passed !== undefined && (
                      <span style={{ marginLeft: '12px', color: retentionQuizResult.passed ? 'var(--success)' : 'var(--error)' }}>
                        {retentionQuizResult.passed ? (t('user.dashboard.retentionQuizPassed') || 'Passed') : (t('user.dashboard.retentionQuizRepeat') || 'Try again next time')}
                      </span>
                    )}
                  </p>
                  {retentionQuizResult.memorizedPercentage != null && (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {t('user.dashboard.retentionMemorized') || 'Memorized'}: {retentionQuizResult.memorizedPercentage}%
                    </p>
                  )}
                  {retentionQuizResult.nextReviewDate && (
                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {t('user.dashboard.retentionNextReview') || 'Next review'}: {new Date(retentionQuizResult.nextReviewDate).toLocaleDateString()}
                    </p>
                  )}
                  <button type="button" className="user-course-btn primary" onClick={closeRetentionQuizModal} style={{ marginTop: '16px' }}>
                    {t('user.dashboard.retentionQuizClose') || 'Close'}
                  </button>
                </div>
              ) : retentionQuizLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
                  <i className="material-icons" style={{ fontSize: '48px' }}>hourglass_empty</i>
                  <p>{t('user.sidebar.loading') || 'Loading quiz...'}</p>
                </div>
              ) : retentionQuizData?.questions?.length > 0 ? (
                <>
                  {retentionQuizArticle.isExam && (
                    <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {t('user.dashboard.retentionQuizExamHint')}
                    </p>
                  )}
                  <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                    {t('user.dashboard.retentionQuizIntro') || 'Choose the correct meaning for each word.'}
                  </p>
                  {retentionQuizData.questions.map((q, qIdx) => (
                    <div key={qIdx} style={{ marginBottom: '20px' }}>
                      <p style={{ fontWeight: '600', marginBottom: '8px' }}>{q.word}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(q.options || []).map((opt, oIdx) => (
                          <button
                            key={oIdx}
                            type="button"
                            className={`user-course-btn retention-quiz-option${retentionQuizSelected[qIdx] === oIdx ? ' selected' : ''}`}
                            onClick={() => setRetentionQuizOption(qIdx, oIdx)}
                            disabled={retentionQuizSubmitting}
                          >
                            {opt.text}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button
                      type="button"
                      className="user-course-btn primary"
                      onClick={submitRetentionQuiz}
                      disabled={retentionQuizSubmitting || (retentionQuizSelected || []).some((v, i) => retentionQuizData.questions[i] && v == null)}
                    >
                      {retentionQuizSubmitting ? (t('user.dashboard.retentionQuizSubmitting') || 'Submitting...') : (t('user.dashboard.retentionQuizSubmit') || 'Submit')}
                    </button>
                    <button type="button" className="user-course-btn" onClick={closeRetentionQuizModal} disabled={retentionQuizSubmitting}>
                      {t('user.dashboard.retentionQuizSkip') || 'Skip'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                  <p>
                    {t(`user.dashboard.${retentionQuizMessageKey(retentionQuizData) || 'retentionQuizNoWords'}`)}
                  </p>
                  <button type="button" className="user-course-btn primary" style={{ marginTop: '16px' }} onClick={closeRetentionQuizModal}>
                    {t('user.dashboard.retentionQuizClose') || 'Close'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unlock Full Access modal (clean, premium) */}
      {showUnlockModal && (
        <div className="unlock-modal-overlay" onClick={() => setShowUnlockModal(false)}>
          <div className="unlock-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unlock-modal-header">
              <h2 className="unlock-modal-title">Unlock Full Access</h2>
              <button type="button" className="unlock-modal-close material-icons" onClick={() => setShowUnlockModal(false)} aria-label="Close">close</button>
            </div>
            <p className="unlock-modal-subtitle">Access 120+ articles and more:</p>
            <ul className="unlock-modal-benefits">
              <li>Educational Vocabulary</li>
              <li>Reading Practice</li>
              <li>Exam Vocabulary</li>
              <li>Dictation & more</li>
            </ul>
            <div className="unlock-modal-actions">
              <button type="button" className="user-course-btn primary" onClick={() => { setShowUnlockModal(false); setActiveSection('subscription'); }}>
                View Individual Plan
              </button>
              <button type="button" className="user-course-btn" onClick={() => { setShowUnlockModal(false); setActiveSection('subscription'); }}>
                View Family Plan
              </button>
              <button type="button" className="unlock-modal-later" onClick={() => setShowUnlockModal(false)}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Article Details Modal */}
      <ArticleDetailsModal
        article={detailsArticle}
        isOpen={showDetailsModal}
        onClose={closeDetailsModal}
        onStartReading={(article) => {
          closeDetailsModal();
          openArticleModal(article);
        }}
        isLocked={detailsArticleIsLocked}
        onUnlockClick={() => { closeDetailsModal(); setShowUnlockModal(true); }}
        userId={currentUser?.id}
      />


      <style>{`
        /* Educational Levels Vocabulary Styles */
        .user-content-header-top {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }



        .back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: var(--hover);
          border: none;
          border-radius: 8px;
          color: var(--text);
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .back-btn:hover {
          background: var(--border);
        }

        .educational-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          border-bottom: 2px solid var(--border);
        }

        .educational-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: transparent;
          border: none;
          border-bottom: 3px solid transparent;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .educational-tab:hover {
          color: var(--text);
          background: var(--hover);
        }

        .educational-tab.active {
          color: var(--accent);
          border-bottom-color: var(--accent);
        }

        .year-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .year-tab {
          padding: 8px 16px;
          background: var(--hover);
          border: 1px solid var(--border);
          border-radius: 20px;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .year-tab:hover {
          background: var(--border);
          color: var(--text);
        }

        .year-tab.active {
          background: var(--accent);
          color: white;
          border-color: var(--accent);
        }



        .educational-content-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .educational-content-card {
          background: var(--background);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .educational-content-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .educational-content-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 16px;
        }

        .educational-content-icon {
          width: 48px;
          height: 48px;
          background: var(--accent);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
        }

        .educational-content-info {
          flex: 1;
        }

        .educational-content-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text);
          margin: 0 0 8px 0;
        }

        .educational-content-description {
          color: var(--text-secondary);
          font-size: 14px;
          line-height: 1.5;
          margin: 0;
        }

        .educational-content-meta {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }

        .educational-content-difficulty,
        .educational-content-duration {
          padding: 4px 12px;
          background: var(--hover);
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .educational-content-difficulty {
          color: var(--success);
        }

        .educational-content-duration {
          color: var(--warning);
        }

        .educational-content-actions {
          display: flex;
          gap: 12px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .educational-tabs {
            flex-direction: column;
            gap: 4px;
          }

          .educational-tab {
            justify-content: center;
          }

          .year-tabs {
            justify-content: center;
          }

          .educational-content-grid {
            grid-template-columns: 1fr;
          }

          .educational-content-actions {
            flex-direction: column;
          }
        }

        /* Article Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--card-bg, white);
          border-radius: 12px;
          max-width: 800px;
          width: 90%;
          max-height: 80vh;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--border, #e1e5e9);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 24px;
          color: var(--text, #2c3e50);
        }

        .close-modal {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--text-secondary, #666);
          padding: 8px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .close-modal:hover {
          background: var(--hover, #f8f9fa);
          color: var(--text, #333);
        }

        .modal-body {
          padding: 24px;
          max-height: 60vh;
          overflow-y: auto;
          line-height: 1.6;
          font-size: 16px;
          color: var(--text, #212121);
        }

        /* Floating Box Styles */
        .floating-box {
          background: #ffffff;
          color: #202124;
          border: 1px solid #dadce0;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1100;
          min-width: 280px;
        }

        .floating-box p {
          margin: 8px 0;
          font-size: 14px;
          color: #202124;
        }

        .floating-box strong {
          color: #202124;
        }

        .floating-box button {
          margin: 4px;
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .floating-box .know-it-btn {
          background: #34a853;
          color: #fff;
        }

        .floating-box .save-word-btn {
          background: #1a73e8;
          color: #fff;
        }

        .floating-box .pronounce-btn {
          background: #fbbc04;
          color: #202124;
        }

        .floating-box button:hover {
          opacity: 1;
          filter: brightness(1.06);
          transform: translateY(-1px);
        }

        .article-card {
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .article-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .article-card-lock-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          z-index: 3;
          font-size: 18px;
          color: rgba(0, 0, 0, 0.55);
          background: rgba(255, 255, 255, 0.92);
          border-radius: 50%;
          padding: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
        }

        .unlock-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
        }
        .unlock-modal {
          background: var(--card-bg, #fff);
          border-radius: 12px;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
          width: 90%;
          max-width: 420px;
          padding: 24px;
        }
        .unlock-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }
        .unlock-modal-title {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }
        .unlock-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: var(--text-secondary);
          font-size: 22px;
        }
        .unlock-modal-close:hover {
          color: var(--text);
        }
        .unlock-modal-subtitle {
          margin: 0 0 12px 0;
          font-size: 14px;
          color: var(--text-secondary);
        }
        .unlock-modal-benefits {
          margin: 0 0 20px 0;
          padding-left: 20px;
          font-size: 14px;
          line-height: 1.6;
          color: var(--text);
        }
        .unlock-modal-actions {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .unlock-modal-actions .user-course-btn {
          width: 100%;
        }
        .unlock-modal-later {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 14px;
          color: var(--text-secondary);
          padding: 8px;
          margin-top: 4px;
        }
        .unlock-modal-later:hover {
          color: var(--text);
        }

        /* No Articles Found Styles */
        .no-articles-found {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          margin: 20px 0;
        }

        .no-articles-icon {
          margin-bottom: 24px;
        }

        .no-articles-icon .material-icons {
          font-size: 64px;
          color: #ccc;
        }

        .no-articles-title {
          font-size: 24px;
          font-weight: 600;
          color: #2c3e50;
          margin: 0 0 16px 0;
        }

        .no-articles-message {
          font-size: 16px;
          color: #666;
          margin: 0 0 12px 0;
          line-height: 1.5;
        }

        .no-articles-subtitle {
          font-size: 14px;
          color: #999;
          margin: 0 0 32px 0;
          line-height: 1.4;
        }

        .back-to-courses-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-to-courses-btn:hover {
          background: var(--primary-dark);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .back-to-courses-btn .material-icons {
          font-size: 20px;
        }

        /* International Exam Reading Styles */
        .exam-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .exam-tab {
          padding: 8px 16px;
          background: var(--hover);
          border: 1px solid var(--border);
          border-radius: 20px;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .exam-tab:hover {
          background: var(--border);
          color: var(--text);
        }

        .exam-tab.active {
          background: var(--accent);
          color: white;
          border-color: var(--accent);
        }

        /* Search Box Styles */
        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: var(--hover);
          border: 1px solid var(--border);
          border-radius: 20px;
          color: var(--text-secondary);
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .search-box:hover {
          background: var(--border);
          color: var(--text);
        }

        .search-box input {
          background: transparent;
          border: none;
          outline: none;
          width: 100%;
          color: var(--text);
        }

        .search-box .material-icons {
          font-size: 20px;
          color: var(--text-secondary);
        }

        /* Segmented Class */
        .segmented {
          border: 1px solid var(--border);
          border-radius: 20px;
          overflow: hidden;
        }

        .segmented .exam-tab {
          border: none;
          border-radius: 0;
          background: var(--hover);
          color: var(--text-secondary);
        }

        .segmented .exam-tab:hover {
          background: var(--border);
          color: var(--text);
        }

        .segmented .exam-tab.active {
          background: var(--accent);
          color: white;
        }

        /* Exam Toolbar Styles */
        .exam-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .selection-summary {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .selection-chip {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: var(--hover);
          border: 1px solid var(--border);
          border-radius: 20px;
          color: var(--text-secondary);
          font-size: 14px;
        }

        .selection-chip .material-icons {
          font-size: 20px;
          color: var(--text-secondary);
        }

        .selection-meta {
          color: var(--text-secondary);
          font-size: 14px;
        }


      `}</style>

              {/* Dictation Overlay */}
        <DictationOverlay
          article={currentDictationArticle}
          isOpen={dictationOverlayOpen}
          onClose={closeDictationOverlay}
          onSaveMistakes={saveMistakesToVocabulary}
        />

        {/* Dictation Details Overlay */}
        <DictationDetailsOverlay
          article={currentDictationDetailsArticle}
          isOpen={dictationDetailsOverlayOpen}
          onClose={closeDictationDetailsOverlay}
        />

        {/* Support Classes Modal */}
        {supportClassModal.show && (
          <div className="modal-overlay" onClick={closeSupportClassModal}>
            <div className="modal-content support-class-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <i className="material-icons">
                    {supportClassModal.type === 'recorded' ? 'play_circle' : 
                     supportClassModal.type === 'workshops' ? 'live_tv' : 'class'}
                  </i>
                  {supportClassModal.type === 'recorded' ? 'Recorded Courses' :
                   supportClassModal.type === 'workshops' ? 'Workshops' : 'Normal Classes'}
                </h2>
                <span className="close" onClick={closeSupportClassModal}>&times;</span>
              </div>
              
              <div className="modal-body">
                <div className="support-class-steps">
                  <div className="step-indicator">
                    <div className="step active">1</div>
                    <div className="step-line"></div>
                    <div className={`step ${selectedSchoolLevel ? 'active' : ''}`}>2</div>
                    <div className="step-line"></div>
                    <div className={`step ${selectedSubject && selectedYear ? 'active' : ''}`}>3</div>
                  </div>
                  
                  <div className="step-labels">
                    <span>School Level</span>
                    <span>Subject</span>
                    <span>Year</span>
                  </div>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleSupportClassSubmit(); }}>
                  {/* Step 1: School Level Selection */}
                  <div className="form-group">
                    <label htmlFor="schoolLevel" className="required">School Level *</label>
                    <select 
                      id="schoolLevel"
                      value={selectedSchoolLevel}
                      onChange={(e) => {
                        setSelectedSchoolLevel(e.target.value);
                        setSelectedSubject('');
                        setSelectedYear('');
                      }}
                      required
                    >
                      <option value="">Select School Level</option>
                      {getSchoolLevels().map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2: Subject Selection */}
                  {selectedSchoolLevel && (
                    <div className="form-group">
                      <label htmlFor="subject" className="required">Subject *</label>
                      <select 
                        id="subject"
                        value={selectedSubject}
                        onChange={(e) => {
                          setSelectedSubject(e.target.value);
                          setSelectedYear('');
                        }}
                        required
                      >
                        <option value="">Select Subject</option>
                        {getSubjectsByLevel(selectedSchoolLevel).map(subject => (
                          <option key={subject} value={subject}>{subject}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Step 3: Year Selection */}
                  {selectedSubject && (
                    <div className="form-group">
                      <label htmlFor="year" className="required">Year *</label>
                      <select 
                        id="year"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        required
                      >
                        <option value="">Select Year</option>
                        {getSchoolLevels().find(level => level.value === selectedSchoolLevel)?.years.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="form-btn secondary" onClick={closeSupportClassModal}>
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className={`form-btn primary ${supportClassModal.type}`}
                      disabled={!selectedSchoolLevel || !selectedSubject || !selectedYear}
                    >
                      <i className="material-icons">play_arrow</i>
                      Start Learning
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

    </div>
  );
};

export default UserDashboard; 